import React from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ChevronDown, MessageCircle, X } from 'lucide-react';
import {
    createUFOVExampleTrial,
    UFOVBoardSurface,
    UFOVChoiceGrid,
    UFOVDirectionPad,
} from './UFOVBoardPrimitives';
import { UFOVSymbol, UFOVSymbolVariantProvider, useUFOVSymbolVariant } from './UFOVSymbol';
import OnboardingProgress from './OnboardingProgress';
import BrainGameCardIcon, { getBrainGameCardToneClass } from './brain-games/BrainGameCardIcon';
import { getUFOVBrandConfig, getUFOVConfig } from '../lib/ufovRuntime';
import brainGameLevels from '../lib/brainGameLevels';
import brainGameHighScores from '../lib/brainGameHighScores';
import gameScoreHistory from '../lib/gameScoreHistory';
import ufovTrials from '../lib/ufovTrials';
import { ADDITIONAL_UFOV_COPY, UFOV_FINISHING_COPY } from '../i18n/additionalUfovCopy';
import { getProgressCopy } from '../i18n/progressLocales';
import CALCULATE_AGE_LOCALES from '../i18n/calculateAgeLocales.json';
import ONBOARDING_LOCALES from '../i18n/onboardingLocales.json';
import { UFOV_LANGUAGE_OPTIONS, normalizeUFOVLanguage } from '../i18n/ufovLocales';

const { getLevelDifficultyTier } = brainGameLevels;
const {
    BRAIN_GAME_HIGH_SCORE_METRICS,
    createBrainGameHighScores,
    formatBrainGameHighScoreValue,
} = brainGameHighScores;
const {
    buildOverallProgressSeries,
    createGameScoreHistory,
    getBestGameScorePoint,
    getGameScoreTrend,
    isLowerGameScoreBetter,
    seedGameScoreHistory,
} = gameScoreHistory;
const { getVideoBucketForLevel } = ufovTrials;

const MENU_LOGO_SRC = '/ufov_logo_mark.png';
const MENU_PREVIEW_ASSET_VERSION = '20260821-centered-crops-v3';
const MENU_DIFFICULTY_COPY_KEY_BY_TIER = Object.freeze({
    easy: 'difficultyEasy',
    medium: 'difficultyMedium',
    hard: 'difficultyHard',
    expert: 'difficultyExpert',
});
const SOPT_MENU_ITEM_IDS = new Set(['sopt-classic', 'sopt-advanced']);
const MENU_AGE_LABELS = {
    en: 'AGE', fr: 'ÂGE', es: 'EDAD', pt: 'IDADE', it: 'ETÀ', de: 'ALTER',
    ja: '年齢', ko: '나이', ar: 'العمر', sv: 'ÅLDER', no: 'ALDER', da: 'ALDER',
    nl: 'LEEFTIJD', pl: 'WIEK', tr: 'YAŞ', 'zh-hans': '年龄', 'zh-hant': '年齡',
    ru: 'ВОЗРАСТ', id: 'USIA', hi: 'उम्र',
};
const MENU_LEVEL_BASED_AGE_LABELS = {
    en: 'Level-based age estimate', fr: 'Estimation de l’âge selon le niveau', es: 'Edad estimada según el nivel',
    pt: 'Estimativa de idade pelo nível', it: 'Stima dell’età basata sul livello', de: 'Levelbasierte Altersschätzung',
    ja: 'レベルに基づく年齢推定', ko: '레벨 기반 추정 나이', ar: 'تقدير العمر حسب المستوى',
    sv: 'Nivåbaserad åldersuppskattning', no: 'Nivåbasert aldersestimat', da: 'Niveaubaseret aldersestimat',
    nl: 'Leeftijdsschatting op basis van niveau', pl: 'Szacowany wiek na podstawie poziomu', tr: 'Seviyeye dayalı yaş tahmini',
    'zh-hans': '基于等级的年龄估计', 'zh-hant': '依等級估算的年齡', ru: 'Оценка возраста по уровню',
    id: 'Perkiraan usia berdasarkan level', hi: 'लेवल पर आधारित उम्र का अनुमान',
};
const SOPT_MENU_PREVIEWS = {
    classic: {
        videoSrc: `/assets/ufov/brain-games/previews/sopt-classic.mp4?v=${MENU_PREVIEW_ASSET_VERSION}`,
        posterSrc: `/assets/ufov/brain-games/previews/sopt-classic-poster.webp?v=${MENU_PREVIEW_ASSET_VERSION}`,
        orientation: 'portrait',
    },
    advanced: {
        videoSrc: `/assets/ufov/brain-games/previews/sopt-advanced.mp4?v=${MENU_PREVIEW_ASSET_VERSION}`,
        posterSrc: `/assets/ufov/brain-games/previews/sopt-advanced-poster.webp?v=${MENU_PREVIEW_ASSET_VERSION}`,
        orientation: 'portrait',
    },
};
const CALIBRATION_ILLUSTRATION_SRC = '/assets/ufov/onboarding/sopt-processing-discovery.png';
const DEFAULT_ONBOARDING_STATIC_ASSETS = {
    studyPage: '/assets/ufov/onboarding/active-study-page1-960.jpg',
    researchPage: '/assets/ufov/onboarding/do-your-own-research.jpg',
    ratingIllustration: '/assets/ufov/onboarding/rating-experience-illustration-640.png',
    assessmentIllustration: '/assets/ufov/onboarding/assessment-faculties-illustration-640.png',
    teamImprovementIllustration: '/assets/ufov/onboarding/team-improvement-illustration.png',
    lifetimeAccessIllustration: '/assets/ufov/onboarding/lifetime-access-illustration.png',
    postPurchaseMedal: '/assets/ufov/postpurchase/congratulations-medal-transparent.png',
    postPurchaseUpdates: '/assets/ufov/postpurchase/future-updates-box-transparent.png',
    postPurchaseCalendar: '/assets/ufov/postpurchase/rest-days-calendar-transparent.png',
};

const configuredOnboardingAssets = getUFOVConfig().assets?.onboarding || {};
const configuredAsset = (key) => {
    const value = configuredOnboardingAssets[key];

    return typeof value === 'string' && value.trim() ? value.trim() : DEFAULT_ONBOARDING_STATIC_ASSETS[key];
};

export const UFOV_ONBOARDING_STATIC_ASSETS = {
    studyPage: configuredAsset('studyPage'),
    researchPage: configuredAsset('researchPage'),
    ratingIllustration: configuredAsset('ratingIllustration'),
    assessmentIllustration: configuredAsset('assessmentIllustration'),
    teamImprovementIllustration: configuredAsset('teamImprovementIllustration'),
    lifetimeAccessIllustration: configuredAsset('lifetimeAccessIllustration'),
    postPurchaseMedal: configuredAsset('postPurchaseMedal'),
    postPurchaseUpdates: configuredAsset('postPurchaseUpdates'),
    postPurchaseCalendar: configuredAsset('postPurchaseCalendar'),
};
export const UFOV_ONBOARDING_STATIC_ASSET_PATHS = Object.values(UFOV_ONBOARDING_STATIC_ASSETS);
const SVG_STROKE = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    strokeWidth: 2.25,
};
const DIALOG_FOCUSABLE_SELECTOR = [
    'button:not(:disabled)',
    '[href]',
    'input:not(:disabled)',
    'select:not(:disabled)',
    'textarea:not(:disabled)',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

function useDialogFocusTrap(open, onDismiss = null) {
    const dialogRef = React.useRef(null);
    const returnFocusRef = React.useRef(null);
    const onDismissRef = React.useRef(onDismiss);

    React.useEffect(() => {
        onDismissRef.current = onDismiss;
    }, [onDismiss]);

    React.useEffect(() => {
        if (!open || typeof document === 'undefined') {
            return undefined;
        }

        const dialog = dialogRef.current;

        if (!dialog) {
            return undefined;
        }

        returnFocusRef.current = document.activeElement;
        const focusFirstControl = () => {
            const preferredControl = dialog.querySelector('[data-dialog-autofocus="true"], [autofocus]');
            const firstControl = dialog.querySelector(DIALOG_FOCUSABLE_SELECTOR);

            (preferredControl || firstControl || dialog).focus?.();
        };
        const frameId = typeof window !== 'undefined'
            ? window.requestAnimationFrame(focusFirstControl)
            : null;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && onDismissRef.current) {
                event.preventDefault();
                onDismissRef.current();
                return;
            }

            if (event.key !== 'Tab') {
                return;
            }

            const controls = Array.from(dialog.querySelectorAll(DIALOG_FOCUSABLE_SELECTOR));

            if (controls.length === 0) {
                event.preventDefault();
                dialog.focus?.();
                return;
            }

            const firstControl = controls[0];
            const lastControl = controls[controls.length - 1];

            if (event.shiftKey && (document.activeElement === firstControl || document.activeElement === dialog)) {
                event.preventDefault();
                lastControl.focus();
            } else if (!event.shiftKey && document.activeElement === lastControl) {
                event.preventDefault();
                firstControl.focus();
            }
        };

        dialog.addEventListener('keydown', handleKeyDown);

        return () => {
            dialog.removeEventListener('keydown', handleKeyDown);
            if (frameId !== null && typeof window !== 'undefined') {
                window.cancelAnimationFrame(frameId);
            }
            returnFocusRef.current?.focus?.();
        };
    }, [open]);

    return dialogRef;
}

function StepPill({ children, tone = 'neutral' }) {
    return <div className={`UFOV-game__step-pill is-${tone}`}>{children}</div>;
}

function ScreenHeading({
    step,
    stepTone = 'neutral',
    title,
    description,
    compactBrand = false,
    className = '',
}) {
    return (
        <div className={`UFOV-game__heading ${className}`.trim()}>
            {step ? <StepPill tone={stepTone}>{step}</StepPill> : null}
            <h1 className={`UFOV-game__title ${compactBrand ? 'is-compact' : ''}`}>{title}</h1>
            {description ? <p className="UFOV-game__subtitle">{description}</p> : null}
        </div>
    );
}

function ScreenShell({
    ariaLabel,
    className = '',
    header,
    main,
    footer,
    mainClassName = '',
    mainRef = null,
    footerClassName = '',
}) {
    const screenRef = React.useRef(null);
    const isOnboardingScreen = className.split(/\s+/).includes('UFOV-game__screen--onboarding');

    React.useLayoutEffect(() => {
        const screen = screenRef.current;

        if (!isOnboardingScreen || !screen || typeof window === 'undefined') {
            return undefined;
        }

        let frameId = 0;
        const needsSmallerCopy = () => {
            const title = screen.querySelector('.UFOV-game__title, .UFOV-game__question-title, h1');

            if (!title) {
                return screen.scrollHeight > screen.clientHeight + 2;
            }

            const titleStyle = window.getComputedStyle(title);
            const lineHeight = Number.parseFloat(titleStyle.lineHeight)
                || Number.parseFloat(titleStyle.fontSize) * 1.1;
            const titleLines = Math.ceil(title.getBoundingClientRect().height / Math.max(1, lineHeight));

            return titleLines > 3 || screen.scrollHeight > screen.clientHeight + 2;
        };
        const fitCopy = () => {
            screen.classList.remove('is-copy-compact', 'is-copy-tight');

            if (needsSmallerCopy()) {
                screen.classList.add('is-copy-compact');
            }

            if (needsSmallerCopy()) {
                screen.classList.add('is-copy-tight');
            }
        };
        const scheduleFit = () => {
            window.cancelAnimationFrame(frameId);
            frameId = window.requestAnimationFrame(fitCopy);
        };

        scheduleFit();
        window.addEventListener('resize', scheduleFit);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener('resize', scheduleFit);
        };
    }, [ariaLabel, className, footer, header, isOnboardingScreen, main]);

    return (
        <section aria-label={ariaLabel} className={`UFOV-game__screen ${className}`.trim()} ref={screenRef}>
            {header ? <div className="UFOV-game__screen-top">{header}</div> : null}
            {main ? <div className={`UFOV-game__screen-main ${mainClassName}`.trim()} ref={mainRef}>{main}</div> : null}
            {footer ? <div className={`UFOV-game__screen-footer ${footerClassName}`.trim()}>{footer}</div> : null}
        </section>
    );
}

const ONBOARDING_LANGUAGE_OPTIONS = UFOV_LANGUAGE_OPTIONS;

const SHOW_VIBRATION_CONTROLS = false;

const ONBOARDING_COPY = {
    en: {
        settingsTitle: 'Before we start',
        settingsDescription: 'Choose a language',
        languageTitle: 'Language',
        showMoreLanguages: 'Show more languages',
        showFewerLanguages: 'Show fewer languages',
        vibrationTitle: 'Vibration',
        vibrationDescription: '3-2-1 taps',
        colorBlindModeTitle: 'Color-blind mode',
        colorBlindModeDescription: 'Uses a blue, orange, and purple palette.',
        onLabel: 'On',
        offLabel: 'Off',
        continueLabel: 'Continue',
        wrongAnswer: 'Wrong answer',
        panelTitle: 'Let’s find your starting level',
        panelDescription: "You'll complete 3 slower practice rounds.\nWe'll use your answers to set a comfortable starting level.",
        panelButton: 'Start calibration',
        ageOptInTitle: 'Would you like an age estimate?',
        ageOptInDescription: 'Complete the 5-round age test to compare your average performance with age-based score ranges.',
        ageOptInNote: 'This is a training estimate, not a medical assessment.',
        calculateAge: 'Calculate my age',
        skipAge: 'Skip for now',
        ageTitle: 'Estimated processing age',
        ageDescription: 'Based on your 5 age-test rounds.',
        ageLabel: 'Age estimate',
        yearsOld: 'years old',
        shareAgeTitle: 'Share your result',
        shareAgeDescription: 'Send your age estimate to someone.',
        shareAgeButton: 'Share on WhatsApp',
        reviewTitle: 'One quick rating',
        reviewLines: [
            'Your feedback helps keep the training useful.',
            'A quick store rating prompt appears next.',
        ],
        feedbackModalTitle: 'How would you rate it?',
        feedbackModalDescription: 'Your onboarding rating helps us improve the training.',
        feedbackRatingLabel: 'Your rating',
        feedbackStarLabel: '{count} out of 5 stars',
        feedbackLike: 'Yes',
        feedbackDislike: 'Not really',
        feedbackCommentIntro: 'Add a comment if you want.',
        feedbackCommentLabel: 'Comment',
        feedbackCommentPlaceholder: 'Optional comment',
        feedbackConfirm: 'Send',
        feedbackSaving: 'Saving...',
        feedbackSkip: 'Not now',
        feedbackError: 'Could not save feedback. Try again or continue later.',
        rows: [
            'You’ll see an image very briefly.',
            'Remember the icon in the center.',
            'Remember the direction of the odd icon outside.',
        ],
        introTitle: 'How it works',
        introDescription: 'Watch the center and the outside target after a fast flash.',
        exampleTitle: 'Example board',
        centerTitle: 'Center question',
        directionTitle: 'Direction question',
        studyTitle: 'Based on the ACTIVE study',
        studyDescription: 'SOPT Classic is inspired by the visual speed-of-processing exercises studied in ACTIVE. Findings from that research apply to the studied training protocol, not every game in this app.',
        researchTitle: 'SOPT Classic is based on a studied training method',
        researchDescription: '',
        researchLines: [
            'Review the studies behind SOPT Classic.',
            'The findings apply to the studied protocol.',
        ],
        researchAlt: 'Research illustration',
        seeExample: 'See example',
        next: 'Next',
        start: 'Start',
        appAriaLabel: 'Attention game',
        attentionRunMenu: 'Attention run menu',
        ranking: 'Ranking',
        classic: 'SOPT Classic',
        classicSubtitle: 'Attention',
        advanced: 'SOPT Advanced',
        advancedSubtitle: 'Attention',
        dailyRun: 'DAILY RUN',
        settings: 'Settings',
        help: 'Help',
        level: 'LEVEL',
        startRun: 'Start run',
        dailyTargetCleared: 'Daily target cleared. Keep training if you want.',
        runsToGoal: 'runs to reach today\'s goal.',
        recommendedRuns: 'Recommended: 20 runs per day.',
        freeRunsAvailableToday: 'You get {total} free runs today. {count} left.',
        freeRunsUsedToday: 'Free runs finished for today. Premium unlocks unlimited runs.',
        preparingPack: 'Preparing pack...',
        menuTourNext: 'Next',
        menuTour: [
            ['Classic mode', 'Two questions per run: center icon and direction. This is the fastest daily training mode.'],
            ['Advanced mode', 'Adds one more memory target, so the same board becomes a harder attention challenge.'],
            ['Daily run', 'Aim for a short, consistent practice routine that works for you.'],
            ['Ranking', 'Check your level, streak, and calibration estimate here.'],
        ],
        optionsTitle: 'Options',
        optionsDescription: 'Choose which visual themes can appear in training. Purchases are restored through the app store; no login is required.',
        chooseThemesTitle: 'Choose themes',
        chooseThemesDescription: 'Only selected themes can appear in asset-board training runs.',
        themesTitle: 'Themes',
        selectedThemes: 'selected for asset-board runs.',
        chooseThemesButton: 'Choose themes',
        selected: 'selected',
        selectAll: 'Select all',
        purchasesTitle: 'Purchases',
        purchasesDescription: 'Restore access purchased with this App Store or Play Store account.',
        settingsFeedbackTitle: 'Send feedback',
        settingsFeedbackDescription: 'Share an idea, problem, or suggestion with the SOPT team.',
        settingsFeedbackButton: 'Write feedback',
        contact: 'Contact',
        contactHint: 'or feedback',
        settingsFeedbackPageTitle: 'Send feedback',
        settingsFeedbackPageDescription: 'This message goes privately to the SOPT team. It is separate from your onboarding rating.',
        settingsFeedbackEmailLabel: 'Email (optional)',
        settingsFeedbackEmailPlaceholder: 'you@example.com',
        settingsFeedbackLabel: 'Your feedback',
        settingsFeedbackPlaceholder: 'What would you like us to know?',
        settingsFeedbackSubmit: 'Send feedback',
        settingsFeedbackSending: 'Sending...',
        settingsFeedbackSent: 'Thank you. Your feedback was sent.',
        settingsFeedbackError: 'Could not send your feedback. Please try again.',
        restorePurchase: 'Restore purchase',
        restorePurchases: 'Restore Purchases',
        checking: 'Checking...',
        anonymousRanking: 'Anonymous ranking',
        resetRankingDescription: 'Reset the anonymous ranking profile stored on this device.',
        resetRanking: 'Reset ranking',
        resetting: 'Resetting...',
        resetAppTitle: 'Reset app',
        resetAppDescription: 'Restart onboarding, explanations, progress, paywall state, and local purchase cache.',
        resetApp: 'Reset all',
        manageSubscription: 'Manage subscription',
        terms: 'Terms',
        privacy: 'Privacy',
        save: 'Save',
        back: 'Back',
        betterThan: 'Better than',
        top: 'Top',
        ofPlayers: 'of players',
        rank: 'Rank',
        bestStreak: 'Best streak',
        day: 'day',
        days: 'days',
        rankingNote: 'Keep training to climb higher.',
        getReady: 'Get ready',
        catchFlash: 'Catch the flash',
        lockItIn: 'Lock it in',
        liveTrial: 'Live trial',
        loading: 'Loading',
        secondCountdown: 'second countdown',
        takeLook: 'Take a look',
        realExerciseSpeedNote: 'In the real exercise, the image will appear very briefly.',
        centerQuestion: 'Center question',
        positionQuestion: 'Position question',
        peripheralQuestion: 'Peripheral question',
        whichCenter: 'Which was at the center?',
        whichDirectionPrefix: 'Which direction was the',
        whichDirectionSuffix: '?',
        whichOddIcon: 'Which was the odd icon?',
        scoreRun: 'Score run',
        startTraining: 'Start for free',
        startTrainingToday: 'Start for free',
        paywallMenuDescription: 'Choose a plan to play, track progress, and use every training mode.',
        paywallOnboardingDescription: 'Keep your calibration moving with unlimited runs, advanced mode, and saved progress.',
        trainingActive: 'Training active',
        openingCheckout: 'Opening checkout...',
        unlimitedDailyRuns: 'Unlimited daily runs',
        unlimitedDailyRunsDescription: 'Train as much as you want.',
        advancedMode: 'Advanced mode',
        advancedModeDescription: 'Access smarter challenges.',
        progressSaved: 'Progress saved',
        progressSavedDescription: 'Your level and stats stay safe.',
        closePaywall: 'Close paywall',
        runResult: 'Run result',
        wellDone: 'Well done!',
        gotItRight: 'You got it right',
        goodRead: 'Good read',
        caughtPart: 'You caught part of it',
        signalLost: 'Signal lost',
        keepTrainingEdge: 'Keep training the edge',
        speed: 'Speed',
        noise: 'Noise',
        movement: 'Movement',
        levelUp: 'Level up',
        levelDown: 'Level down',
        center: 'Center',
        direction: 'Direction',
        outside: 'Outside',
        nextRun: 'Next run',
        nextRound: 'Next round',
        seeResult: 'See result',
        changeMode: 'Change mode',
        weeklyPlan: 'Weekly',
        lifetimePlan: 'Lifetime',
        cancelAnytime: 'Cancel anytime',
        oneTimePurchase: 'One-time purchase',
        storeCheckout: 'Store checkout',
        bestValue: 'Forever',
        startWeeklyPlan: 'Start weekly plan',
        getLifetimeAccess: 'Get lifetime access',
        unlockForever: 'Unlock forever',
        premium: 'Premium',
        weekSuffix: '/ week',
        onceSuffix: 'once',
        checkingStoreAccount: 'Checking the store account...',
        planUnavailable: 'This plan is not available yet.',
        purchaseCancelled: 'Purchase cancelled.',
        checkoutUnavailable: 'The checkout could not be opened right now.',
        anonymousRankingReset: 'Anonymous ranking reset. Your next run starts a new anonymous ranking.',
        anonymousRankingResetFailed: 'Anonymous ranking could not be reset right now.',
        activeOnDevice: '{name} is active on this device.',
        activeInBrowser: '{name} is active in this browser.',
        secureCheckoutDevice: 'Secure checkout opens through the app store on this device.',
        revenueCatCheckout: 'RevenueCat will open secure checkout when you choose a plan.',
        pricingLoading: 'Plan pricing is still loading from RevenueCat.',
        noPremiumBrowser: 'No active premium access was found in this browser.',
        restoreFailed: 'Restore could not be completed right now.',
        premiumFlowInterrupted: 'Premium flow interrupted.',
        openingPlanCheckout: 'Opening {name} checkout...',
        waitingStoreTitle: 'Waiting for App Store',
        waitingStoreButton: 'Waiting for App Store...',
        waitingStoreConfirmation: 'Waiting for App Store confirmation. This can take a while.',
        waitingStoreConfirmationLong: 'Still waiting for App Store. Keep the app open; Apple may still be checking your payment method.',
    },
    fr: {
        settingsTitle: 'Avant de commencer',
        settingsDescription: 'Choisissez une langue',
        languageTitle: 'Langue',
        vibrationTitle: 'Vibration',
        vibrationDescription: 'Vibrations 3-2-1',
        colorBlindModeTitle: 'Mode daltonien',
        colorBlindModeDescription: 'Utilise une palette bleu, orange et violet.',
        onLabel: 'Oui',
        offLabel: 'Non',
        continueLabel: 'Continuer',
        wrongAnswer: 'Mauvaise réponse',
        panelTitle: 'Trouvons votre niveau de départ',
        panelDescription: "Vous effectuerez 3 manches d'entraînement plus lentes.\nVos réponses serviront à définir un niveau de départ confortable.",
        panelButton: 'Commencer la calibration',
        ageOptInTitle: "Souhaitez-vous obtenir une estimation d'âge ?",
        ageOptInDescription: "Effectuez le test d'âge en 5 manches pour comparer votre performance moyenne à des plages de scores par âge.",
        ageOptInNote: "Il s'agit d'une estimation d'entraînement, et non d'une évaluation médicale.",
        calculateAge: 'Calculer mon âge',
        skipAge: 'Passer pour le moment',
        ageTitle: 'Âge de traitement estimé',
        ageDescription: "Basé sur les 5 manches de votre test d'âge.",
        ageLabel: 'Âge estimé',
        yearsOld: 'ans',
        shareAgeTitle: 'Partagez votre résultat',
        shareAgeDescription: 'Envoyez votre estimation d’âge à quelqu’un.',
        shareAgeButton: 'Partager sur WhatsApp',
        reviewTitle: 'Une note rapide',
        reviewLines: [
            'Votre avis contribue à améliorer cet entraînement.',
            'Une invitation à noter l’application apparaîtra ensuite.',
        ],
        feedbackModalTitle: 'Comment l’évaluez-vous ?',
        feedbackModalDescription: 'Votre note initiale nous aide à améliorer l’entraînement.',
        feedbackRatingLabel: 'Votre note',
        feedbackStarLabel: '{count} étoiles sur 5',
        feedbackLike: 'Oui',
        feedbackDislike: 'Pas vraiment',
        feedbackCommentIntro: 'Ajoutez un commentaire si vous le souhaitez.',
        feedbackCommentLabel: 'Commentaire',
        feedbackCommentPlaceholder: 'Commentaire optionnel',
        feedbackConfirm: 'Envoyer',
        feedbackSaving: 'Enregistrement...',
        feedbackSkip: 'Pas maintenant',
        feedbackError: 'Impossible d’enregistrer l’avis. Réessayez ou continuez plus tard.',
        rows: [
            'Une image apparaîtra très brièvement.',
            'Mémorisez l’icône au centre.',
            'Mémorisez la direction de l’icône différente à l’extérieur.',
        ],
        introTitle: 'Comment ça marche',
        introDescription: 'Regardez le centre et la cible extérieure après un flash rapide.',
        exampleTitle: 'Exemple',
        centerTitle: 'Question centrale',
        directionTitle: 'Question de direction',
        studyTitle: 'Basé sur l’étude ACTIVE',
        studyDescription: 'SOPT Classic s’inspire des exercices visuels de vitesse de traitement étudiés dans ACTIVE. Les résultats concernent le protocole étudié, et non tous les jeux de l’application.',
        researchTitle: 'SOPT Classic repose sur une méthode d’entraînement étudiée',
        researchDescription: '',
        researchLines: [
            'Consultez les études sur lesquelles repose SOPT Classic.',
            'Les résultats s’appliquent au protocole étudié.',
        ],
        researchAlt: 'Illustration de recherche',
        seeExample: 'Voir l’exemple',
        next: 'Suivant',
        start: 'Commencer',
        appAriaLabel: 'Jeu d’attention',
        attentionRunMenu: 'Menu d’entraînement',
        ranking: 'Classement',
        classic: 'SOPT Classic',
        classicSubtitle: 'Attention',
        advanced: 'SOPT Advanced',
        advancedSubtitle: 'Attention',
        dailyRun: 'SÉANCE DU JOUR',
        settings: 'Réglages',
        help: 'Aide',
        level: 'NIVEAU',
        startRun: 'Commencer',
        dailyTargetCleared: 'Objectif du jour atteint. Continuez si vous voulez.',
        runsToGoal: 'manches pour atteindre l’objectif du jour.',
        recommendedRuns: 'Recommandé : 20 manches par jour.',
        freeRunsAvailableToday: 'Vous avez {total} manches gratuites aujourd’hui. Il vous en reste {count}.',
        freeRunsUsedToday: 'Vos manches gratuites sont terminées pour aujourd’hui. Premium débloque des manches illimitées.',
        preparingPack: 'Préparation du pack…',
        menuTourNext: 'Suivant',
        menuTour: [
            ['Mode classique', 'Deux questions par manche : l’icône centrale et la direction. C’est le mode quotidien le plus rapide.'],
            ['Mode avancé', 'Ajoute une cible à mémoriser, ce qui rend le même plateau plus difficile.'],
            ['Séance du jour', 'Visez 20 manches courtes par jour. C’est la routine recommandée.'],
            ['Classement', 'Consultez ici votre niveau, votre série et votre âge cérébral estimé.'],
        ],
        optionsTitle: 'Options',
        optionsDescription: 'Choisissez les thèmes visuels de l’entraînement. Les achats sont restaurés via l’App Store ou le Play Store, sans connexion.',
        chooseThemesTitle: 'Choisir les thèmes',
        chooseThemesDescription: 'Seuls les thèmes choisis peuvent apparaître dans les entraînements avec plateau.',
        themesTitle: 'Thèmes',
        selectedThemes: 'sélectionnés pour les manches.',
        chooseThemesButton: 'Choisir',
        selected: 'sélectionnés',
        selectAll: 'Tout choisir',
        purchasesTitle: 'Achats',
        purchasesDescription: 'Restaurez l’accès acheté avec ce compte App Store ou Play Store.',
        settingsFeedbackTitle: 'Envoyer un avis',
        settingsFeedbackDescription: 'Partagez une idée, un problème ou une suggestion avec l’équipe SOPT.',
        settingsFeedbackButton: 'Écrire',
        contact: 'Contact',
        contactHint: 'ou avis',
        settingsFeedbackPageTitle: 'Envoyer un avis',
        settingsFeedbackPageDescription: 'Ce message est envoyé en privé à l’équipe SOPT. Il est distinct de votre note initiale.',
        settingsFeedbackEmailLabel: 'E-mail (facultatif)',
        settingsFeedbackEmailPlaceholder: 'vous@exemple.com',
        settingsFeedbackLabel: 'Votre message',
        settingsFeedbackPlaceholder: 'Que souhaitez-vous nous dire ?',
        settingsFeedbackSubmit: 'Envoyer',
        settingsFeedbackSending: 'Envoi...',
        settingsFeedbackSent: 'Merci. Votre message a été envoyé.',
        settingsFeedbackError: 'Impossible d’envoyer votre message. Réessayez.',
        restorePurchase: 'Restaurer',
        restorePurchases: 'Restaurer les achats',
        checking: 'Vérification…',
        anonymousRanking: 'Classement anonyme',
        resetRankingDescription: 'Réinitialisez le profil de classement stocké sur cet appareil.',
        resetRanking: 'Réinitialiser',
        resetting: 'Réinitialisation…',
        resetAppTitle: 'Réinitialiser',
        resetAppDescription: 'Relancez l’onboarding, les explications, la progression, le paywall et le cache local des achats.',
        resetApp: 'Tout réinitialiser',
        manageSubscription: 'Gérer l’abonnement',
        terms: 'Conditions',
        privacy: 'Confidentialité',
        save: 'Enregistrer',
        back: 'Retour',
        betterThan: 'Mieux que',
        top: 'Top',
        ofPlayers: 'des joueurs',
        rank: 'Rang',
        bestStreak: 'Meilleure série',
        day: 'jour',
        days: 'jours',
        rankingNote: 'Continuez l’entraînement pour progresser.',
        getReady: 'Préparez-vous',
        catchFlash: 'Capturez le flash',
        lockItIn: 'Memorisez',
        liveTrial: 'Manche en direct',
        loading: 'Chargement',
        secondCountdown: 'compte à rebours',
        takeLook: 'Regardez',
        realExerciseSpeedNote: 'Dans l’exercice réel, l’image sera affichée TRÈS brièvement.',
        centerQuestion: 'Question centrale',
        positionQuestion: 'Question de position',
        peripheralQuestion: 'Question périphérique',
        whichCenter: 'Quel symbole était au centre ?',
        whichDirectionPrefix: 'Dans quelle direction était le',
        whichDirectionSuffix: ' ?',
        whichOddIcon: 'Quelle était l’icône différente ?',
        scoreRun: 'Calculer',
        startTraining: 'Commencer gratuitement',
        startTrainingToday: 'Commencer gratuitement',
        paywallMenuDescription: 'Choisissez une offre pour jouer, suivre vos progrès et utiliser tous les modes.',
        paywallOnboardingDescription: 'Continuez votre calibration avec des manches illimitées, le mode avancé et les progrès sauvegardés.',
        trainingActive: 'Entraînement actif',
        openingCheckout: 'Ouverture du paiement...',
        unlimitedDailyRuns: 'Manches illimitées',
        unlimitedDailyRunsDescription: 'Entraînez-vous autant que vous le souhaitez.',
        advancedMode: 'Mode avancé',
        advancedModeDescription: 'Accédez à des défis plus exigeants.',
        progressSaved: 'Progrès sauvegardés',
        progressSavedDescription: 'Votre niveau et vos statistiques restent en sécurité.',
        closePaywall: 'Fermer',
        runResult: 'Résultat',
        wellDone: 'Bravo !',
        gotItRight: 'Vous avez réussi',
        goodRead: 'Bien vu',
        caughtPart: 'Vous en avez vu une partie',
        signalLost: 'Signal perdu',
        keepTrainingEdge: 'Continuez à entraîner le champ visuel',
        speed: 'Vitesse',
        noise: 'Bruit',
        movement: 'Mouvement',
        levelUp: 'Niveau +',
        levelDown: 'Niveau -',
        center: 'Centre',
        direction: 'Direction',
        outside: 'Extérieur',
        nextRun: 'Manche suivante',
        nextRound: 'Manche suivante',
        seeResult: 'Voir le résultat',
        changeMode: 'Changer de mode',
        weeklyPlan: 'Hebdomadaire',
        lifetimePlan: 'À vie',
        cancelAnytime: 'Annulable à tout moment',
        oneTimePurchase: 'Achat unique',
        storeCheckout: 'Paiement via la boutique',
        bestValue: 'Pour toujours',
        startWeeklyPlan: 'Commencer l’offre hebdomadaire',
        getLifetimeAccess: 'Obtenir l’accès à vie',
        unlockForever: 'Débloquer pour toujours',
        premium: 'Premium',
        weekSuffix: '/ semaine',
        onceSuffix: 'une fois',
        checkingStoreAccount: 'Vérification du compte de la boutique…',
        planUnavailable: 'Cette offre n’est pas encore disponible.',
        purchaseCancelled: 'Achat annulé.',
        checkoutUnavailable: 'Le paiement ne peut pas être ouvert pour le moment.',
        anonymousRankingReset: 'Classement anonyme réinitialisé. La prochaine manche démarrera un nouveau classement.',
        anonymousRankingResetFailed: 'Le classement anonyme ne peut pas être réinitialisé pour le moment.',
        activeOnDevice: '{name} est actif sur cet appareil.',
        activeInBrowser: '{name} est actif dans ce navigateur.',
        secureCheckoutDevice: 'Le paiement sécurisé s’ouvre via l’App Store sur cet appareil.',
        revenueCatCheckout: 'RevenueCat ouvrira le paiement sécurisé lorsque vous choisirez une offre.',
        pricingLoading: 'Les prix se chargent depuis RevenueCat.',
        noPremiumBrowser: 'Aucun accès premium actif n’a été trouvé dans ce navigateur.',
        restoreFailed: 'La restauration a échoué pour le moment.',
        premiumFlowInterrupted: 'Parcours premium interrompu.',
        openingPlanCheckout: 'Ouverture du paiement {name}...',
        waitingStoreTitle: 'En attente de l’App Store',
        waitingStoreButton: 'En attente de l’App Store…',
        waitingStoreConfirmation: 'En attente de la confirmation de l’App Store. Cela peut prendre un moment.',
        waitingStoreConfirmationLong: 'Toujours en attente de l’App Store. Gardez l’application ouverte ; Apple vérifie peut-être encore votre moyen de paiement.',
    },
    es: {
        settingsTitle: 'Antes de empezar',
        settingsDescription: 'Elige un idioma',
        languageTitle: 'Idioma',
        vibrationTitle: 'Vibración',
        vibrationDescription: 'Toques 3-2-1',
        colorBlindModeTitle: 'Modo daltonismo',
        colorBlindModeDescription: 'Usa una paleta azul, naranja y morada.',
        onLabel: 'Sí',
        offLabel: 'No',
        continueLabel: 'Continuar',
        wrongAnswer: 'Respuesta incorrecta',
        panelTitle: 'Vamos a encontrar tu nivel inicial',
        panelDescription: 'Completarás 3 rondas de práctica más lentas.\nUsaremos tus respuestas para establecer un nivel inicial cómodo.',
        panelButton: 'Iniciar calibración',
        ageOptInTitle: '¿Quieres obtener una estimación de edad?',
        ageOptInDescription: 'Completa la prueba de edad de 5 rondas para comparar tu rendimiento medio con rangos de puntuación por edad.',
        ageOptInNote: 'Es una estimación de entrenamiento, no una evaluación médica.',
        calculateAge: 'Calcular mi edad',
        skipAge: 'Omitir por ahora',
        ageTitle: 'Edad de procesamiento estimada',
        ageDescription: 'Basada en las 5 rondas de tu prueba de edad.',
        ageLabel: 'Edad estimada',
        yearsOld: 'años',
        shareAgeTitle: 'Comparte tu resultado',
        shareAgeDescription: 'Envía tu edad estimada a alguien.',
        shareAgeButton: 'Compartir por WhatsApp',
        reviewTitle: 'Una valoración rápida',
        reviewLines: [
            'Tu opinión ayuda a que el entrenamiento siga siendo útil.',
            'A continuación aparecerá una solicitud de valoración.',
        ],
        feedbackModalTitle: '¿Cómo lo valorarías?',
        feedbackModalDescription: 'Tu valoración inicial nos ayuda a mejorar el entrenamiento.',
        feedbackRatingLabel: 'Tu valoración',
        feedbackStarLabel: '{count} de 5 estrellas',
        feedbackLike: 'Sí',
        feedbackDislike: 'No mucho',
        feedbackCommentIntro: 'Añade un comentario si quieres.',
        feedbackCommentLabel: 'Comentario',
        feedbackCommentPlaceholder: 'Comentario opcional',
        feedbackConfirm: 'Enviar',
        feedbackSaving: 'Guardando...',
        feedbackSkip: 'Ahora no',
        feedbackError: 'No se pudo guardar la opinión. Inténtalo de nuevo o continúa más tarde.',
        rows: [
            'Se mostrará una imagen muy brevemente.',
            'Recuerda el icono del centro.',
            'Recuerda la dirección del icono diferente del exterior.',
        ],
        introTitle: 'Cómo funciona',
        introDescription: 'Mira el centro y el objetivo exterior después de un destello rápido.',
        exampleTitle: 'Ejemplo',
        centerTitle: 'Pregunta central',
        directionTitle: 'Pregunta de dirección',
        studyTitle: 'Basado en el estudio ACTIVE',
        studyDescription: 'SOPT Classic se inspira en ejercicios visuales de velocidad de procesamiento estudiados en ACTIVE. Los resultados corresponden al protocolo estudiado, no a todos los juegos de la app.',
        researchTitle: 'SOPT Classic se basa en un método de entrenamiento estudiado',
        researchDescription: '',
        researchLines: [
            'Consulta los estudios en los que se basa SOPT Classic.',
            'Los resultados corresponden al protocolo estudiado.',
        ],
        researchAlt: 'Ilustración de investigación',
        seeExample: 'Ver ejemplo',
        next: 'Siguiente',
        start: 'Empezar',
        appAriaLabel: 'Juego de atención',
        attentionRunMenu: 'Menú de entrenamiento',
        ranking: 'Clasificación',
        classic: 'SOPT Classic',
        classicSubtitle: 'Atención',
        advanced: 'SOPT Advanced',
        advancedSubtitle: 'Atención',
        dailyRun: 'RONDA DIARIA',
        settings: 'Ajustes',
        help: 'Ayuda',
        level: 'NIVEL',
        startRun: 'Empezar',
        dailyTargetCleared: 'Objetivo diario completado. Sigue entrenando si quieres.',
        runsToGoal: 'rondas para alcanzar el objetivo de hoy.',
        recommendedRuns: 'Recomendado: 20 rondas al día.',
        freeRunsAvailableToday: 'Tienes {total} rondas gratis hoy. Te quedan {count}.',
        freeRunsUsedToday: 'Ya usaste las rondas gratis de hoy. Premium desbloquea rondas ilimitadas.',
        preparingPack: 'Preparando el paquete…',
        menuTourNext: 'Siguiente',
        menuTour: [
            ['Modo clásico', 'Dos preguntas por ronda: icono central y dirección. Es el modo diario más rápido.'],
            ['Modo avanzado', 'Añade un elemento más que recordar, por lo que el mismo tablero resulta más difícil.'],
            ['Ronda diaria', 'Intenta hacer 20 rondas cortas al día. Es la rutina recomendada.'],
            ['Clasificación', 'Consulta aquí tu nivel, tu racha y tu edad cerebral estimada.'],
        ],
        optionsTitle: 'Opciones',
        optionsDescription: 'Elige qué temas visuales pueden aparecer en el entrenamiento. Las compras se restauran desde la tienda sin iniciar sesión.',
        chooseThemesTitle: 'Elegir temas',
        chooseThemesDescription: 'Solo los temas seleccionados pueden aparecer en las rondas con tablero.',
        themesTitle: 'Temas',
        selectedThemes: 'seleccionados para las rondas.',
        chooseThemesButton: 'Elegir',
        selected: 'seleccionados',
        selectAll: 'Seleccionar todo',
        purchasesTitle: 'Compras',
        purchasesDescription: 'Restaura el acceso comprado con esta cuenta de App Store o Play Store.',
        settingsFeedbackTitle: 'Enviar comentarios',
        settingsFeedbackDescription: 'Comparte una idea, un problema o una sugerencia con el equipo de SOPT.',
        settingsFeedbackButton: 'Escribir',
        contact: 'Contacto',
        contactHint: 'o comentarios',
        settingsFeedbackPageTitle: 'Enviar comentarios',
        settingsFeedbackPageDescription: 'Este mensaje se envía de forma privada al equipo de SOPT y es independiente de tu valoración inicial.',
        settingsFeedbackEmailLabel: 'Email (opcional)',
        settingsFeedbackEmailPlaceholder: 'tu@ejemplo.com',
        settingsFeedbackLabel: 'Tu mensaje',
        settingsFeedbackPlaceholder: '¿Qué te gustaría contarnos?',
        settingsFeedbackSubmit: 'Enviar',
        settingsFeedbackSending: 'Enviando...',
        settingsFeedbackSent: 'Gracias. Tu mensaje se ha enviado.',
        settingsFeedbackError: 'No se pudo enviar. Inténtalo de nuevo.',
        restorePurchase: 'Restaurar',
        restorePurchases: 'Restaurar compras',
        checking: 'Comprobando...',
        anonymousRanking: 'Clasificación anónima',
        resetRankingDescription: 'Restablece el perfil de clasificación guardado en este dispositivo.',
        resetRanking: 'Restablecer',
        resetting: 'Restableciendo...',
        resetAppTitle: 'Restablecer app',
        resetAppDescription: 'Reinicia el onboarding, las explicaciones, el progreso, el paywall y la caché local de compras.',
        resetApp: 'Restablecer todo',
        manageSubscription: 'Gestionar suscripción',
        terms: 'Términos',
        privacy: 'Privacidad',
        save: 'Guardar',
        back: 'Volver',
        betterThan: 'Mejor que',
        top: 'Top',
        ofPlayers: 'de jugadores',
        rank: 'Puesto',
        bestStreak: 'Mejor racha',
        day: 'día',
        days: 'días',
        rankingNote: 'Sigue entrenando para subir.',
        getReady: 'Prepárate',
        catchFlash: 'Capta el destello',
        lockItIn: 'Memorízalo',
        liveTrial: 'Ronda en directo',
        loading: 'Cargando',
        secondCountdown: 'cuenta atrás',
        takeLook: 'Mira',
        realExerciseSpeedNote: 'En el ejercicio real se mostrará MUY brevemente.',
        centerQuestion: 'Pregunta central',
        positionQuestion: 'Pregunta de posición',
        peripheralQuestion: 'Pregunta periférica',
        whichCenter: '¿Cuál estaba en el centro?',
        whichDirectionPrefix: '¿En qué dirección estaba el',
        whichDirectionSuffix: '?',
        whichOddIcon: '¿Cuál era el icono diferente?',
        scoreRun: 'Puntuar',
        startTraining: 'Empezar gratis',
        startTrainingToday: 'Empezar gratis',
        paywallMenuDescription: 'Elige un plan para jugar, seguir tu progreso y usar todos los modos.',
        paywallOnboardingDescription: 'Sigue tu calibración con rondas ilimitadas, modo avanzado y progreso guardado.',
        trainingActive: 'Entrenamiento activo',
        openingCheckout: 'Abriendo pago...',
        unlimitedDailyRuns: 'Rondas ilimitadas',
        unlimitedDailyRunsDescription: 'Entrena todo lo que quieras.',
        advancedMode: 'Modo avanzado',
        advancedModeDescription: 'Accede a desafíos más exigentes.',
        progressSaved: 'Progreso guardado',
        progressSavedDescription: 'Tu nivel y tus estadísticas se mantienen seguros.',
        closePaywall: 'Cerrar',
        runResult: 'Resultado',
        wellDone: '¡Bien hecho!',
        gotItRight: 'Lo hiciste bien',
        goodRead: 'Buena lectura',
        caughtPart: 'Captaste una parte',
        signalLost: 'Señal perdida',
        keepTrainingEdge: 'Sigue entrenando el campo visual',
        speed: 'Velocidad',
        noise: 'Ruido',
        movement: 'Movimiento',
        levelUp: 'Sube nivel',
        levelDown: 'Baja nivel',
        center: 'Centro',
        direction: 'Dirección',
        outside: 'Exterior',
        nextRun: 'Siguiente ronda',
        nextRound: 'Siguiente ronda',
        seeResult: 'Ver resultado',
        changeMode: 'Cambiar modo',
        weeklyPlan: 'Semanal',
        lifetimePlan: 'De por vida',
        cancelAnytime: 'Cancela cuando quieras',
        oneTimePurchase: 'Compra única',
        storeCheckout: 'Pago en la tienda',
        bestValue: 'Para siempre',
        startWeeklyPlan: 'Empezar plan semanal',
        getLifetimeAccess: 'Obtener acceso de por vida',
        unlockForever: 'Desbloquear para siempre',
        premium: 'Premium',
        weekSuffix: '/ semana',
        onceSuffix: 'una vez',
        checkingStoreAccount: 'Comprobando la cuenta de la tienda...',
        planUnavailable: 'Este plan aún no está disponible.',
        purchaseCancelled: 'Compra cancelada.',
        checkoutUnavailable: 'No se pudo abrir el pago ahora.',
        anonymousRankingReset: 'Clasificación anónima restablecida. Tu próxima ronda iniciará una nueva clasificación anónima.',
        anonymousRankingResetFailed: 'No se pudo restablecer la clasificación anónima en este momento.',
        activeOnDevice: '{name} está activo en este dispositivo.',
        activeInBrowser: '{name} está activo en este navegador.',
        secureCheckoutDevice: 'El pago seguro se abre desde la tienda en este dispositivo.',
        revenueCatCheckout: 'RevenueCat abrirá el pago seguro cuando elijas un plan.',
        pricingLoading: 'Los precios se están cargando desde RevenueCat.',
        noPremiumBrowser: 'No se encontró acceso premium activo en este navegador.',
        restoreFailed: 'No se pudo completar la restauración en este momento.',
        premiumFlowInterrupted: 'Flujo premium interrumpido.',
        openingPlanCheckout: 'Abriendo pago de {name}...',
        waitingStoreTitle: 'Esperando la App Store',
        waitingStoreButton: 'Esperando App Store...',
        waitingStoreConfirmation: 'Esperando la confirmación de la App Store. Esto puede tardar.',
        waitingStoreConfirmationLong: 'Seguimos esperando a la App Store. Mantén la app abierta; es posible que Apple todavía esté comprobando tu método de pago.',
    },
};

ONBOARDING_COPY.pt = {
    ...ONBOARDING_COPY.en,
    settingsTitle: 'Antes de começar',
    settingsDescription: 'Escolha um idioma',
    languageTitle: 'Idioma',
    vibrationTitle: 'Vibração',
    vibrationDescription: 'Toques 3-2-1',
    colorBlindModeTitle: 'Modo daltonismo',
    colorBlindModeDescription: 'Usa uma paleta azul, laranja e roxa.',
    onLabel: 'Sim',
    offLabel: 'Não',
    continueLabel: 'Continuar',
    wrongAnswer: 'Resposta incorreta',
    panelTitle: 'Vamos encontrar seu nível inicial',
    panelDescription: 'Você fará 3 rodadas de prática mais lentas.\nUsaremos suas respostas para definir um nível inicial confortável.',
    panelButton: 'Iniciar calibração',
    ageOptInTitle: 'Quer receber uma estimativa de idade?',
    ageOptInDescription: 'Faça o teste de idade de 5 rodadas para comparar seu desempenho médio com faixas de pontuação por idade.',
    ageOptInNote: 'Esta é uma estimativa de treino, não uma avaliação médica.',
    calculateAge: 'Calcular minha idade',
    skipAge: 'Pular por enquanto',
    ageTitle: 'Idade de processamento estimada',
    ageDescription: 'Com base nas 5 rodadas do seu teste de idade.',
    ageLabel: 'Idade estimada',
    yearsOld: 'anos',
    shareAgeTitle: 'Compartilhe seu resultado',
    shareAgeDescription: 'Envie sua idade estimada para alguém.',
    shareAgeButton: 'Compartilhar no WhatsApp',
    reviewTitle: 'Uma avaliação rápida',
    reviewLines: [
        'Sua opinião ajuda a manter o treino útil.',
        'Em seguida, aparecerá o pedido de avaliação da loja.',
    ],
    feedbackModalTitle: 'Como você avaliaria?',
    feedbackModalDescription: 'Sua avaliação inicial nos ajuda a melhorar o treino.',
    feedbackRatingLabel: 'Sua avaliação',
    feedbackStarLabel: '{count} de 5 estrelas',
    feedbackLike: 'Sim',
    feedbackDislike: 'Nem tanto',
    feedbackCommentIntro: 'Adicione um comentário se quiser.',
    feedbackCommentLabel: 'Comentário',
    feedbackCommentPlaceholder: 'Comentário opcional',
    feedbackConfirm: 'Enviar',
    feedbackSaving: 'Salvando...',
    feedbackSkip: 'Agora não',
    feedbackError: 'Não foi possível salvar sua opinião. Tente novamente ou continue depois.',
    rows: [
        'Uma imagem será mostrada muito rapidamente.',
        'Lembre-se do ícone no centro.',
        'Lembre-se da direção do ícone diferente na parte externa.',
    ],
    introTitle: 'Como funciona',
    introDescription: 'Olhe para o centro e para o alvo externo depois de um flash rápido.',
    exampleTitle: 'Tabuleiro de exemplo',
    centerTitle: 'Pergunta central',
    directionTitle: 'Pergunta de direção',
    studyTitle: 'Com base no estudo ACTIVE',
    studyDescription: 'O SOPT Classic se inspira em exercícios visuais de velocidade de processamento estudados no ACTIVE. Os resultados valem para o protocolo estudado, não para todos os jogos do app.',
    researchTitle: 'O SOPT Classic se baseia em um método de treino estudado',
    researchTitleLines: ['As evidências se aplicam', 'ao SOPT Classic'],
    researchLines: [
        'Consulte os estudos por trás do SOPT Classic.',
        'Os resultados se aplicam ao protocolo estudado.',
    ],
    researchAlt: 'Ilustração de pesquisa',
    seeExample: 'Ver exemplo',
    next: 'Próximo',
    start: 'Começar',
    appAriaLabel: 'Jogo de atenção',
    attentionRunMenu: 'Menu de treino de atenção',
    ranking: 'Classificação',
    classic: 'SOPT Classic',
    classicSubtitle: 'Atenção',
    advanced: 'SOPT Advanced',
    advancedSubtitle: 'Atenção',
    dailyRun: 'RODADA DIÁRIA',
    settings: 'Ajustes',
    help: 'Ajuda',
    level: 'NÍVEL',
    startRun: 'Começar a rodada',
    dailyTargetCleared: 'Meta diária concluída. Continue treinando se quiser.',
    runsToGoal: 'rodadas para a meta de hoje.',
    recommendedRuns: 'Recomendado: 20 rodadas por dia.',
    freeRunsAvailableToday: 'Você tem {total} rodadas grátis hoje. Restam {count}.',
    freeRunsUsedToday: 'As rodadas grátis de hoje acabaram. Premium libera rodadas ilimitadas.',
    preparingPack: 'Preparando pacote...',
    menuTourNext: 'Próximo',
    menuTour: [
        ['Modo clássico', 'Duas perguntas por rodada: ícone central e direção. É o modo diário mais rápido.'],
        ['Modo avançado', 'Adiciona mais um alvo de memória, então o mesmo tabuleiro fica mais difícil.'],
        ['Rodada diária', 'Tente fazer 20 rodadas curtas por dia. Essa é a rotina recomendada.'],
        ['Classificação', 'Veja aqui seu nível, sua sequência e o progresso da idade cerebral estimada.'],
    ],
    optionsTitle: 'Opções',
    optionsDescription: 'Escolha quais temas visuais podem aparecer no treino. As compras são restauradas pela loja, sem login.',
    chooseThemesTitle: 'Escolher temas',
    chooseThemesDescription: 'Somente os temas selecionados podem aparecer nas rodadas com tabuleiro.',
    themesTitle: 'Temas',
    selectedThemes: 'selecionados para rodadas com tabuleiro.',
    chooseThemesButton: 'Escolher',
    selected: 'selecionados',
    selectAll: 'Selecionar tudo',
    purchasesTitle: 'Compras',
    purchasesDescription: 'Restaure o acesso comprado com esta conta da App Store ou Play Store.',
    settingsFeedbackTitle: 'Enviar feedback',
    settingsFeedbackDescription: 'Compartilhe uma ideia, um problema ou uma sugestão com a equipe SOPT.',
    settingsFeedbackButton: 'Escrever',
    contact: 'Contato',
    contactHint: 'ou feedback',
    settingsFeedbackPageTitle: 'Enviar feedback',
    settingsFeedbackPageDescription: 'Esta mensagem é enviada em particular à equipe SOPT e fica separada da sua avaliação inicial.',
    settingsFeedbackEmailLabel: 'Email (opcional)',
    settingsFeedbackEmailPlaceholder: 'voce@exemplo.com',
    settingsFeedbackLabel: 'Sua mensagem',
    settingsFeedbackPlaceholder: 'O que você gostaria de nos contar?',
    settingsFeedbackSubmit: 'Enviar',
    settingsFeedbackSending: 'Enviando...',
    settingsFeedbackSent: 'Agradecemos. Sua mensagem foi enviada.',
    settingsFeedbackError: 'Não foi possível enviar. Tente novamente.',
    restorePurchase: 'Restaurar',
    restorePurchases: 'Restaurar compras',
    checking: 'Verificando...',
    anonymousRanking: 'Classificação anônima',
    resetRankingDescription: 'Redefina o perfil de classificação anônima salvo neste dispositivo.',
    resetRanking: 'Redefinir',
    resetting: 'Redefinindo...',
    resetAppTitle: 'Redefinir app',
    resetAppDescription: 'Reinicie o onboarding, as explicações, o progresso, o paywall e o cache local de compras.',
    resetApp: 'Redefinir tudo',
    manageSubscription: 'Gerenciar assinatura',
    terms: 'Termos',
    privacy: 'Privacidade',
    save: 'Salvar',
    back: 'Voltar',
    betterThan: 'Melhor que',
    top: 'Top',
    ofPlayers: 'dos jogadores',
    rank: 'Posição',
    bestStreak: 'Melhor sequência',
    day: 'dia',
    days: 'dias',
    rankingNote: 'Continue treinando para subir.',
    getReady: 'Prepare-se',
    catchFlash: 'Capture o flash',
    lockItIn: 'Memorize',
    liveTrial: 'Rodada ao vivo',
    loading: 'Carregando',
    secondCountdown: 'contagem regressiva',
    takeLook: 'Olhe',
    realExerciseSpeedNote: 'No exercício real, a imagem será mostrada MUITO rapidamente.',
    centerQuestion: 'Pergunta central',
    positionQuestion: 'Pergunta de posição',
    peripheralQuestion: 'Pergunta periférica',
    whichCenter: 'Qual estava no centro?',
    whichDirectionPrefix: 'Em que direção estava o',
    whichDirectionSuffix: '?',
    whichOddIcon: 'Qual era o ícone diferente?',
    scoreRun: 'Pontuar',
    startTraining: 'Começar grátis',
    startTrainingToday: 'Começar grátis',
    paywallMenuDescription: 'Escolha um plano para jogar, acompanhar o progresso e usar todos os modos.',
    paywallOnboardingDescription: 'Continue sua calibração com rodadas ilimitadas, modo avançado e progresso salvo.',
    trainingActive: 'Treino ativo',
    openingCheckout: 'Abrindo pagamento...',
    unlimitedDailyRuns: 'Rodadas diárias ilimitadas',
    unlimitedDailyRunsDescription: 'Treine quanto quiser.',
    advancedMode: 'Modo avançado',
    advancedModeDescription: 'Acesse desafios mais inteligentes.',
    progressSaved: 'Progresso salvo',
    progressSavedDescription: 'Seu nível e suas estatísticas ficam seguros.',
    closePaywall: 'Fechar paywall',
    runResult: 'Resultado',
    wellDone: 'Muito bem!',
    gotItRight: 'Você acertou',
    goodRead: 'Boa leitura',
    caughtPart: 'Você captou uma parte',
    signalLost: 'Sinal perdido',
    keepTrainingEdge: 'Continue treinando o limite',
    speed: 'Velocidade',
    noise: 'Ruído',
    movement: 'Movimento',
    levelUp: 'Nível acima',
    levelDown: 'Nível abaixo',
    center: 'Centro',
    direction: 'Direção',
    outside: 'Fora',
    nextRun: 'Próxima rodada',
    nextRound: 'Próxima rodada',
    seeResult: 'Ver resultado',
    changeMode: 'Mudar modo',
    weeklyPlan: 'Semanal',
    lifetimePlan: 'Vitalício',
    cancelAnytime: 'Cancele quando quiser',
    oneTimePurchase: 'Compra única',
    storeCheckout: 'Pagamento na loja',
    bestValue: 'Para sempre',
    startWeeklyPlan: 'Começar plano semanal',
    getLifetimeAccess: 'Obter acesso vitalício',
    unlockForever: 'Desbloquear para sempre',
    premium: 'Premium',
    weekSuffix: '/ semana',
    onceSuffix: 'uma vez',
    checkingStoreAccount: 'Verificando a conta da loja...',
    planUnavailable: 'Este plano ainda não está disponível.',
    purchaseCancelled: 'Compra cancelada.',
    checkoutUnavailable: 'Não foi possível abrir o pagamento agora.',
    anonymousRankingReset: 'Classificação anônima redefinida. Sua próxima rodada inicia uma nova classificação.',
    anonymousRankingResetFailed: 'Não foi possível redefinir a classificação anônima agora.',
    activeOnDevice: '{name} está ativo neste dispositivo.',
    activeInBrowser: '{name} está ativo neste navegador.',
    secureCheckoutDevice: 'O pagamento seguro abre pela loja neste dispositivo.',
    revenueCatCheckout: 'RevenueCat abrirá o pagamento seguro quando você escolher um plano.',
    pricingLoading: 'Os preços ainda estão sendo carregados do RevenueCat.',
    noPremiumBrowser: 'Nenhum acesso premium ativo foi encontrado neste navegador.',
    restoreFailed: 'A restauração não pode ser concluída agora.',
    premiumFlowInterrupted: 'Fluxo premium interrompido.',
    openingPlanCheckout: 'Abrindo pagamento de {name}...',
    waitingStoreTitle: 'Aguardando App Store',
    waitingStoreButton: 'Aguardando App Store...',
    waitingStoreConfirmation: 'Aguardando a confirmação da App Store. Isso pode demorar.',
    waitingStoreConfirmationLong: 'Ainda aguardando a App Store. Mantenha o app aberto; a Apple ainda pode estar verificando seu método de pagamento.',
};

ONBOARDING_COPY.it = {
    ...ONBOARDING_COPY.en,
    settingsTitle: 'Prima di iniziare',
    settingsDescription: 'Scegli la lingua',
    languageTitle: 'Lingua',
    vibrationTitle: 'Vibrazione',
    vibrationDescription: 'Tocchi 3-2-1',
    colorBlindModeTitle: 'Modalità daltonismo',
    colorBlindModeDescription: 'Usa una palette blu, arancione e viola.',
    onLabel: 'Sì',
    offLabel: 'No',
    continueLabel: 'Continua',
    wrongAnswer: 'Risposta errata',
    panelTitle: 'Troviamo il tuo livello iniziale',
    panelDescription: 'Completerai 3 round di pratica più lenti.\nUseremo le tue risposte per impostare un livello iniziale confortevole.',
    panelButton: 'Avvia la calibrazione',
    ageOptInTitle: "Vuoi ricevere una stima dell'età?",
    ageOptInDescription: "Completa il test dell'età di 5 round per confrontare la tua prestazione media con intervalli di punteggio per età.",
    ageOptInNote: 'È una stima di allenamento, non una valutazione medica.',
    calculateAge: 'Calcola la mia età',
    skipAge: 'Salta per ora',
    ageTitle: 'Età di elaborazione stimata',
    ageDescription: "Basata sui 5 round del test dell'età.",
    ageLabel: 'Età stimata',
    yearsOld: 'anni',
    shareAgeTitle: 'Condividi il risultato',
    shareAgeDescription: 'Invia a qualcuno la tua età stimata.',
    shareAgeButton: 'Condividi su WhatsApp',
    reviewTitle: 'Una rapida valutazione',
    reviewLines: [
        'Il tuo feedback aiuta a mantenere utile l’allenamento.',
        'Subito dopo apparirà la richiesta di valutazione dello store.',
    ],
    feedbackModalTitle: 'Come lo valuteresti?',
    feedbackModalDescription: 'La tua valutazione iniziale ci aiuta a migliorare l’allenamento.',
    feedbackRatingLabel: 'La tua valutazione',
    feedbackStarLabel: '{count} stelle su 5',
    feedbackLike: 'Sì',
    feedbackDislike: 'Non molto',
    feedbackCommentIntro: 'Aggiungi un commento se vuoi.',
    feedbackCommentLabel: 'Commento',
    feedbackCommentPlaceholder: 'Commento opzionale',
    feedbackConfirm: 'Invia',
    feedbackSaving: 'Salvataggio...',
    feedbackSkip: 'Non ora',
    feedbackError: 'Non è stato possibile salvare il feedback. Riprova o continua più tardi.',
    rows: [
        'Un’immagine viene mostrata molto rapidamente.',
        'Ricorda l’icona al centro.',
        'Ricorda la direzione dell’icona diversa all’esterno.',
    ],
    introTitle: 'Come funziona',
    introDescription: 'Guarda il centro e il bersaglio esterno dopo un flash rapido.',
    exampleTitle: 'Tabellone di esempio',
    centerTitle: 'Domanda centrale',
    directionTitle: 'Domanda sulla direzione',
    studyTitle: 'Basato sullo studio ACTIVE',
    studyDescription: 'SOPT Classic si ispira agli esercizi visivi di velocità di elaborazione studiati in ACTIVE. I risultati riguardano il protocollo studiato, non tutti i giochi dell’app.',
    researchTitle: 'SOPT Classic si basa su un metodo di allenamento studiato',
    researchTitleLines: ['Le evidenze riguardano', 'SOPT Classic'],
    researchLines: [
        'Consulta gli studi alla base di SOPT Classic.',
        'I risultati riguardano il protocollo studiato.',
    ],
    researchAlt: 'Illustrazione di ricerca',
    seeExample: 'Vedi esempio',
    next: 'Avanti',
    start: 'Inizia',
    appAriaLabel: 'Gioco di attenzione',
    attentionRunMenu: 'Menu di allenamento dell’attenzione',
    ranking: 'Classifica',
    classic: 'SOPT Classic',
    classicSubtitle: 'Attenzione',
    advanced: 'SOPT Advanced',
    advancedSubtitle: 'Attenzione',
    dailyRun: 'ROUND GIORNALIERO',
    settings: 'Impostazioni',
    help: 'Aiuto',
    level: 'LIVELLO',
    startRun: 'Inizia il round',
    dailyTargetCleared: 'Obiettivo giornaliero completato. Continua ad allenarti se vuoi.',
    runsToGoal: 'round per raggiungere l’obiettivo di oggi.',
    recommendedRuns: 'Consigliato: 20 round al giorno.',
    freeRunsAvailableToday: 'Hai {total} round gratis oggi. Ne restano {count}.',
    freeRunsUsedToday: 'I round gratis di oggi sono finiti. Premium sblocca round illimitati.',
    preparingPack: 'Preparazione del pacchetto…',
    menuTourNext: 'Avanti',
    menuTour: [
        ['Modalità classica', 'Due domande per round: icona centrale e direzione. È il modo giornaliero più rapido.'],
        ['Modalità avanzata', 'Aggiunge un altro bersaglio da ricordare, quindi lo stesso tabellone diventa più difficile.'],
        ['Round giornaliero', 'Prova a fare 20 round brevi al giorno. Questa è la routine consigliata.'],
        ['Classifica', 'Vedi qui il tuo livello, la serie e il progresso dell’età cerebrale stimata.'],
    ],
    optionsTitle: 'Opzioni',
    optionsDescription: 'Scegli quali temi visivi possono apparire nell’allenamento. Gli acquisti si ripristinano dallo store, senza effettuare l’accesso.',
    chooseThemesTitle: 'Scegli temi',
    chooseThemesDescription: 'Solo i temi selezionati possono apparire nei round con tabellone.',
    themesTitle: 'Temi',
    selectedThemes: 'selezionati per round con tabellone.',
    chooseThemesButton: 'Scegli',
    selected: 'selezionati',
    selectAll: 'Seleziona tutto',
    purchasesTitle: 'Acquisti',
    purchasesDescription: 'Ripristina l’accesso acquistato con questo account App Store o Play Store.',
    settingsFeedbackTitle: 'Invia feedback',
    settingsFeedbackDescription: 'Condividi un’idea, un problema o un suggerimento con il team SOPT.',
    settingsFeedbackButton: 'Scrivi',
    contact: 'Contatto',
    contactHint: 'o feedback',
    settingsFeedbackPageTitle: 'Invia feedback',
    settingsFeedbackPageDescription: 'Questo messaggio viene inviato privatamente al team SOPT ed è separato dalla valutazione iniziale.',
    settingsFeedbackEmailLabel: 'Email (facoltativa)',
    settingsFeedbackEmailPlaceholder: 'tu@esempio.com',
    settingsFeedbackLabel: 'Il tuo messaggio',
    settingsFeedbackPlaceholder: 'Cosa vorresti comunicarci?',
    settingsFeedbackSubmit: 'Invia',
    settingsFeedbackSending: 'Invio...',
    settingsFeedbackSent: 'Grazie. Il tuo messaggio è stato inviato.',
    settingsFeedbackError: 'Impossibile inviare il messaggio. Riprova.',
    restorePurchase: 'Ripristina',
    restorePurchases: 'Ripristina acquisti',
    checking: 'Verifica...',
    anonymousRanking: 'Classifica anonima',
    resetRankingDescription: 'Reimposta il profilo di classifica anonima salvato su questo dispositivo.',
    resetRanking: 'Reimposta',
    resetting: 'Reimpostazione...',
    resetAppTitle: 'Reimposta app',
    resetAppDescription: 'Riavvia l’onboarding, le spiegazioni, il progresso, il paywall e la cache locale degli acquisti.',
    resetApp: 'Reimposta tutto',
    manageSubscription: 'Gestisci l’abbonamento',
    terms: 'Termini',
    privacy: 'Privacy',
    save: 'Salva',
    back: 'Indietro',
    betterThan: 'Meglio del',
    top: 'Top',
    ofPlayers: 'dei giocatori',
    rank: 'Posizione',
    bestStreak: 'Migliore serie',
    day: 'giorno',
    days: 'giorni',
    rankingNote: 'Continua ad allenarti per salire.',
    getReady: 'Preparati',
    catchFlash: 'Cattura il flash',
    lockItIn: 'Memorizza',
    liveTrial: 'Round dal vivo',
    loading: 'Caricamento',
    secondCountdown: 'conto alla rovescia',
    takeLook: 'Guarda',
    realExerciseSpeedNote: 'Nell’esercizio reale, l’immagine apparirà MOLTO brevemente.',
    centerQuestion: 'Domanda centrale',
    positionQuestion: 'Domanda sulla posizione',
    peripheralQuestion: 'Domanda periferica',
    whichCenter: 'Quale icona era al centro?',
    whichDirectionPrefix: 'In che direzione era il',
    whichDirectionSuffix: '?',
    whichOddIcon: 'Qual era l’icona diversa?',
    scoreRun: 'Punteggia',
    startTraining: 'Inizia gratis',
    startTrainingToday: 'Inizia gratis',
    paywallMenuDescription: 'Scegli un piano per giocare, seguire i progressi e usare tutte le modalità.',
    paywallOnboardingDescription: 'Continua la calibrazione con round illimitati, modalità avanzata e progressi salvati.',
    trainingActive: 'Allenamento attivo',
    openingCheckout: 'Apertura pagamento...',
    unlimitedDailyRuns: 'Round giornalieri illimitati',
    unlimitedDailyRunsDescription: 'Allenati quanto vuoi.',
    advancedMode: 'Modalità avanzata',
    advancedModeDescription: 'Accedi a sfide più impegnative.',
    progressSaved: 'Progresso salvato',
    progressSavedDescription: 'Il tuo livello e le tue statistiche restano al sicuro.',
    closePaywall: 'Chiudi paywall',
    runResult: 'Risultato',
    wellDone: 'Molto bene!',
    gotItRight: 'Hai risposto correttamente',
    goodRead: 'Buona lettura',
    caughtPart: 'Hai colto una parte',
    signalLost: 'Segnale perso',
    keepTrainingEdge: 'Continua ad allenare il limite',
    speed: 'Velocità',
    noise: 'Rumore',
    movement: 'Movimento',
    levelUp: 'Livello su',
    levelDown: 'Livello giù',
    center: 'Centro',
    direction: 'Direzione',
    outside: 'Fuori',
    nextRun: 'Prossimo round',
    nextRound: 'Prossimo round',
    seeResult: 'Vedi risultato',
    changeMode: 'Cambia modalità',
    weeklyPlan: 'Settimanale',
    lifetimePlan: 'A vita',
    cancelAnytime: 'Annulla quando vuoi',
    oneTimePurchase: 'Acquisto unico',
    storeCheckout: 'Pagamento nello store',
    bestValue: 'Per sempre',
    startWeeklyPlan: 'Inizia piano settimanale',
    getLifetimeAccess: 'Ottieni accesso a vita',
    unlockForever: 'Sblocca per sempre',
    premium: 'Premium',
    weekSuffix: '/ settimana',
    onceSuffix: 'una volta',
    checkingStoreAccount: 'Verifica account store...',
    planUnavailable: 'Questo piano non è ancora disponibile.',
    purchaseCancelled: 'Acquisto annullato.',
    checkoutUnavailable: 'Non è stato possibile aprire il pagamento ora.',
    anonymousRankingReset: 'Classifica anonima reimpostata. Il tuo prossimo round avvierà una nuova classifica.',
    anonymousRankingResetFailed: 'Non è stato possibile reimpostare la classifica anonima ora.',
    activeOnDevice: '{name} è attivo su questo dispositivo.',
    activeInBrowser: '{name} è attivo in questo browser.',
    secureCheckoutDevice: 'Il pagamento sicuro si apre dallo store su questo dispositivo.',
    revenueCatCheckout: 'RevenueCat aprirà il pagamento sicuro quando scegli un piano.',
    pricingLoading: 'I prezzi sono ancora in caricamento da RevenueCat.',
    noPremiumBrowser: 'Nessun accesso premium attivo trovato in questo browser.',
    restoreFailed: 'Il ripristino non può essere completato ora.',
    premiumFlowInterrupted: 'Flusso premium interrotto.',
    openingPlanCheckout: 'Apertura pagamento di {name}...',
    waitingStoreTitle: 'In attesa di App Store',
    waitingStoreButton: 'In attesa di App Store...',
    waitingStoreConfirmation: 'In attesa della conferma App Store. Potrebbe richiedere tempo.',
    waitingStoreConfirmationLong: 'Ancora in attesa dell’App Store. Tieni l’app aperta; Apple potrebbe ancora verificare il metodo di pagamento.',
};

Object.entries(ADDITIONAL_UFOV_COPY).forEach(([language, localizedCopy]) => {
    ONBOARDING_COPY[language] = {
        ...ONBOARDING_COPY.en,
        ...localizedCopy,
    };
});

Object.entries(UFOV_FINISHING_COPY).forEach(([language, localizedCopy]) => {
    ONBOARDING_COPY[language] = {
        ...ONBOARDING_COPY.en,
        ...(ONBOARDING_COPY[language] || {}),
        ...localizedCopy,
    };
});

Object.entries(ONBOARDING_LOCALES).forEach(([language, localizedCopy]) => {
    ONBOARDING_COPY[language] = {
        ...ONBOARDING_COPY.en,
        ...(ONBOARDING_COPY[language] || {}),
        ...(localizedCopy.shared || {}),
    };
});

const normalizeOnboardingLanguage = (language) => (
    Object.prototype.hasOwnProperty.call(ONBOARDING_COPY, normalizeUFOVLanguage(language))
        ? normalizeUFOVLanguage(language)
        : 'en'
);

const getOnboardingCopy = (language = 'en', brand = getUFOVBrandConfig()) => {
    const normalizedLanguage = normalizeOnboardingLanguage(language);
    const copy = ONBOARDING_COPY[normalizedLanguage] || ONBOARDING_COPY.en;

    if (normalizedLanguage !== 'en') {
        return copy;
    }

    return {
        ...copy,
        introTitle: brand.copy.onboardingTitle || copy.introTitle,
        introDescription: brand.copy.onboardingDescription || copy.introDescription,
        studyDescription: brand.copy.studyDescription || copy.studyDescription,
    };
};

export const getUFOVGameCopy = getOnboardingCopy;

const getOnboardingSteps = (brand, language = 'en') => {
    const copy = getOnboardingCopy(language, brand);

    return [
        {
            id: 'intro',
            title: copy.introTitle,
            description: copy.introDescription,
            buttonLabel: copy.seeExample,
            rows: [
                { icon: 'timer', tone: 'mint', text: copy.rows[0] },
                { icon: 'focus', tone: 'sand', text: copy.rows[1] },
                { icon: 'direction', tone: 'peach', text: copy.rows[2] },
            ],
        },
        {
            id: 'example-board',
            title: copy.exampleTitle,
            description: '',
            buttonLabel: copy.next,
            example: 'board',
        },
        {
            id: 'center-question',
            title: copy.centerTitle,
            description: '',
            buttonLabel: copy.next,
            example: 'center',
        },
        {
            id: 'direction-question',
            title: copy.directionTitle,
            description: '',
            buttonLabel: copy.next,
            example: 'direction',
        },
        {
            id: 'study',
            title: copy.studyTitle,
            description: copy.studyDescription,
            buttonLabel: copy.start,
            studyPreview: true,
        },
        {
            id: 'research',
            title: copy.researchTitle,
            description: copy.researchDescription,
            buttonLabel: copy.next,
            researchPreview: true,
        },
        {
            id: 'assessment-ready',
            title: copy.panelTitle,
            description: copy.panelDescription,
            buttonLabel: copy.panelButton,
        },
        {
            id: 'assessment-age',
            title: copy.ageTitle,
            description: '',
            buttonLabel: copy.continueLabel,
        },
    ];
};

function OnboardingIcon({ type }) {
    const paths = {
        timer: (
            <>
                <path {...SVG_STROKE} d="M32 16v8" />
                <path {...SVG_STROKE} d="M25 10h14" />
                <path {...SVG_STROKE} d="M44 14l4-4" />
                <circle {...SVG_STROKE} cx="32" cy="34" r="18" />
                <path {...SVG_STROKE} d="M32 34l8-10" />
                <path {...SVG_STROKE} d="M10 28h8" />
                <path {...SVG_STROKE} d="M6 34h10" />
            </>
        ),
        focus: (
            <>
                <path {...SVG_STROKE} d="M20 18h-6a4 4 0 0 0-4 4v6" />
                <path {...SVG_STROKE} d="M44 18h6a4 4 0 0 1 4 4v6" />
                <path {...SVG_STROKE} d="M20 46h-6a4 4 0 0 1-4-4v-6" />
                <path {...SVG_STROKE} d="M44 46h6a4 4 0 0 0 4-4v-6" />
                <path d="M32 21l10 18H22Z" fill="#4cc7b6" />
            </>
        ),
        direction: (
            <>
                <path {...SVG_STROKE} d="M32 10v14" />
                <path {...SVG_STROKE} d="M32 54V40" />
                <path {...SVG_STROKE} d="M10 32h14" />
                <path {...SVG_STROKE} d="M54 32H40" />
                <path {...SVG_STROKE} d="M26 16l6-6 6 6" />
                <path {...SVG_STROKE} d="M26 48l6 6 6-6" />
                <path {...SVG_STROKE} d="M16 26l-6 6 6 6" />
                <path {...SVG_STROKE} d="M48 26l6 6-6 6" />
                <rect x="48" y="7" width="12" height="12" rx="3" fill="#d50101" />
            </>
        ),
        document: (
            <>
                <path {...SVG_STROKE} d="M20 8h18l12 12v30a6 6 0 0 1-6 6H20a6 6 0 0 1-6-6V14a6 6 0 0 1 6-6Z" />
                <path {...SVG_STROKE} d="M38 8v14h12" />
                <circle {...SVG_STROKE} cx="30" cy="29" r="7" />
                <path {...SVG_STROKE} d="M20 48h20" />
                <path {...SVG_STROKE} d="M22 39h16" />
            </>
        ),
        brain: (
            <>
                <path {...SVG_STROKE} d="M18 20h-8" />
                <path {...SVG_STROKE} d="M20 28H8" />
                <path {...SVG_STROKE} d="M32 16v32" />
                <path {...SVG_STROKE} d="M24 14a10 10 0 0 1 8 4 10 10 0 0 1 8-4c6 0 11 5 11 11a10 10 0 0 1-4 8 10 10 0 0 1-8 15 10 10 0 0 1-7-3 10 10 0 0 1-7 3 10 10 0 0 1-8-15 10 10 0 0 1-4-8c0-6 5-11 11-11Z" />
                <path {...SVG_STROKE} d="M24 22c3 0 5 2 5 5" />
                <path {...SVG_STROKE} d="M40 22c-3 0-5 2-5 5" />
                <path {...SVG_STROKE} d="M24 38c3 0 5 2 5 5" />
                <path {...SVG_STROKE} d="M40 38c-3 0-5 2-5 5" />
            </>
        ),
        shield: (
            <>
                <path {...SVG_STROKE} d="M32 8 50 14v16c0 13-9 23-18 26-9-3-18-13-18-26V14Z" />
                <path {...SVG_STROKE} d="m24 33 6 6 12-14" />
            </>
        ),
    };

    return (
        <svg aria-hidden="true" className="UFOV-game__illustration-svg" viewBox="0 0 64 64">
            {paths[type]}
        </svg>
    );
}

function OnboardingRows({ rows }) {
    return (
        <div className="UFOV-game__onboarding-list">
            {rows.map((row, index) => (
                <div className="UFOV-game__onboarding-row" key={`${row.icon}-${row.text}`}>
                    <div className={`UFOV-game__onboarding-index is-${row.tone}`}>{index + 1}</div>
                    <div className={`UFOV-game__onboarding-icon is-${row.tone}`}><OnboardingIcon type={row.icon} /></div>
                    <strong>{row.text}</strong>
                </div>
            ))}
        </div>
    );
}

function OnboardingStudyPreview() {
    const brand = getUFOVBrandConfig();

    return (
        <div className="UFOV-game__study-preview">
            <img
                alt={brand.copy.studyAlt}
                className="UFOV-game__study-page"
                decoding="async"
                src={UFOV_ONBOARDING_STATIC_ASSETS.studyPage}
            />
        </div>
    );
}

function OnboardingResearchPreview({ language = 'en' }) {
    const copy = getOnboardingCopy(language);

    return (
        <div className="UFOV-game__study-preview is-research">
            <img
                alt={copy.researchAlt}
                className="UFOV-game__study-page is-research"
                decoding="async"
                draggable="false"
                fetchpriority="high"
                loading="eager"
                src={UFOV_ONBOARDING_STATIC_ASSETS.researchPage}
            />
            <div className="UFOV-game__assessment-copy is-research">
                {(copy.researchLines || []).map((line) => <p key={line}>{line}</p>)}
            </div>
        </div>
    );
}

function OnboardingExample({
    demoReady = true,
    demoTrial = null,
    language = 'en',
    type,
    positions,
    selectedCenter,
    selectedPosition,
    onSelectCenter,
    onSelectPosition,
}) {
    const copy = getOnboardingCopy(language);
    const symbolVariant = useUFOVSymbolVariant();
    const exampleTrial = demoTrial || createUFOVExampleTrial(symbolVariant);

    if (type === 'board') {
        return (
            <div className="UFOV-game__onboarding-example is-board-only">
                <h2 className="UFOV-game__question-title">{copy.takeLook}</h2>
            {demoReady ? (
                <UFOVBoardSurface trial={exampleTrial} demo />
            ) : (
                <div aria-label={copy.loading} className="UFOV-game__board-preload" role="status" />
            )}
            <p className="UFOV-game__onboarding-speed-note">{copy.realExerciseSpeedNote}</p>
        </div>
        );
    }

    if (!demoReady) {
        return (
            <div className="UFOV-game__onboarding-example">
                <div aria-label={copy.loading} className="UFOV-game__board-preload is-compact" role="status" />
            </div>
        );
    }

    return (
        <div className="UFOV-game__onboarding-example">
            {type === 'center' ? (
                <div className="UFOV-game__prompt-block">
                    <h2 className="UFOV-game__question-title">{copy.whichCenter}</h2>
                    <UFOVChoiceGrid
                        options={exampleTrial.centerOptions || symbolVariant.onboardingExample?.centerOptions || []}
                        selected={selectedCenter}
                        onSelect={onSelectCenter}
                    />
                </div>
            ) : (
                <div className="UFOV-game__prompt-block">
                    <h2 className="UFOV-game__question-title">
                        {copy.whichDirectionPrefix} <UFOVSymbol className="UFOV-game__inline-symbol" symbol={exampleTrial.peripheralSymbol} usage="inline" />{copy.whichDirectionSuffix}
                    </h2>
                    <UFOVDirectionPad positions={positions} selected={selectedPosition} onSelect={onSelectPosition} />
                </div>
            )}
        </div>
    );
}

function OnboardingDemoLayers({
    demoTrial = null,
    language = 'en',
    onReady = null,
    onSelectCenter,
    onSelectPosition,
    positions,
    selectedCenter,
    selectedPosition,
    visibleType = '',
    visible = false,
}) {
    const layerRef = React.useRef(null);

    React.useEffect(() => {
        if (!demoTrial || typeof onReady !== 'function') {
            return undefined;
        }

        let cancelled = false;
        const completePrewarm = () => {
            if (cancelled) {
                return;
            }

            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    if (!cancelled) {
                        onReady();
                    }
                });
            });
        };
        const waitForImages = () => {
            const images = Array.from(layerRef.current?.querySelectorAll('img') || []);
            const pendingImages = images.filter((image) => !(image.complete && image.naturalWidth > 0));

            if (!pendingImages.length) {
                completePrewarm();
                return;
            }

            Promise.all(pendingImages.map((image) => new Promise((resolve) => {
                image.addEventListener('load', resolve, { once: true });
                image.addEventListener('error', resolve, { once: true });
            }))).then(completePrewarm);
        };

        waitForImages();

        return () => {
            cancelled = true;
        };
    }, [demoTrial?.id, onReady]);

    if (!demoTrial) {
        return null;
    }

    return (
        <div
            aria-hidden={visible ? undefined : 'true'}
            className={`UFOV-game__onboarding-demo-layers ${visible ? 'has-visible-layer' : 'is-hidden-only'}`.trim()}
            ref={layerRef}
        >
            {['board', 'center', 'direction'].map((type) => (
                <div
                    aria-hidden={visibleType === type ? undefined : 'true'}
                    className={`UFOV-game__onboarding-demo-layer ${visibleType === type ? 'is-visible' : 'is-prewarming'}`.trim()}
                    inert={visibleType === type ? undefined : ''}
                    key={type}
                >
                    <OnboardingExample
                        demoReady
                        demoTrial={demoTrial}
                        language={language}
                        onSelectCenter={onSelectCenter}
                        onSelectPosition={onSelectPosition}
                        positions={positions}
                        selectedCenter={selectedCenter}
                        selectedPosition={selectedPosition}
                        type={type}
                    />
                </div>
            ))}
        </div>
    );
}

function ButtonSpinner({ label = 'Loading' }) {
    return <span aria-label={label} className="UFOV-game__button-spinner" role="status" />;
}

function SettingsGlobeIcon() {
    return (
        <svg aria-hidden="true" className="UFOV-game__settings-section-icon" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="12.5" {...SVG_STROKE} />
            <path d="M3.5 16h25" {...SVG_STROKE} />
            <path d="M16 3.5c3.6 3.4 5.4 7.6 5.4 12.5S19.6 25.1 16 28.5" {...SVG_STROKE} />
            <path d="M16 3.5c-3.6 3.4-5.4 7.6-5.4 12.5s1.8 9.1 5.4 12.5" {...SVG_STROKE} />
        </svg>
    );
}

function SettingsVibrationIcon() {
    return (
        <svg aria-hidden="true" className="UFOV-game__settings-section-icon is-vibration" viewBox="0 0 36 36">
            <rect x="11" y="5" width="14" height="26" rx="3.5" {...SVG_STROKE} />
            <path d="M6.4 12.2c-1.4 2.3-1.4 5.3 0 7.6" {...SVG_STROKE} />
            <path d="M29.6 12.2c1.4 2.3 1.4 5.3 0 7.6" {...SVG_STROKE} />
            <path d="M4 9.2c-2.3 4.1-2.3 9.5 0 13.6" {...SVG_STROKE} />
            <path d="M32 9.2c2.3 4.1 2.3 9.5 0 13.6" {...SVG_STROKE} />
        </svg>
    );
}

const PRIMARY_ONBOARDING_LANGUAGE_COUNT = 5;

function LanguageFlag({ language }) {
    const normalizedLanguage = normalizeUFOVLanguage(language);

    return (
        <img
            alt=""
            aria-hidden="true"
            className="UFOV-game__language-flag"
            src={`/assets/ufov/languages/flags/${normalizedLanguage}.svg`}
        />
    );
}

function ReviewerAccessModal({ onClose, onSubmit, open = false }) {
    const [code, setCode] = React.useState('');
    const [status, setStatus] = React.useState('idle');
    const dialogRef = useDialogFocusTrap(open, onClose);

    React.useEffect(() => {
        if (!open) {
            return;
        }

        setCode('');
        setStatus('idle');
    }, [open]);

    if (!open) {
        return null;
    }

    const working = status === 'working';
    const accepted = status === 'accepted';
    const submit = async (event) => {
        event.preventDefault();

        if (accepted) {
            onClose?.();
            return;
        }

        if (working || !code.trim()) {
            return;
        }

        setStatus('working');

        try {
            const result = await onSubmit?.(code);
            setStatus(result?.valid === true ? 'accepted' : 'invalid');
        } catch (error) {
            setStatus('error');
        }
    };

    const statusLabel = status === 'accepted'
        ? 'Code accepted.'
        : status === 'invalid'
            ? 'Incorrect code.'
            : status === 'error'
                ? 'Could not check code.'
                : '';

    const modal = (
        <div className="UFOV-game__reviewer-access-backdrop">
            <form
                aria-labelledby="UFOV-reviewer-access-title"
                aria-modal="true"
                className="UFOV-game__reviewer-access-modal"
                onSubmit={submit}
                ref={dialogRef}
                role="dialog"
                tabIndex={-1}
            >
                <div className="UFOV-game__reviewer-access-head">
                    <h2 id="UFOV-reviewer-access-title">Enter reviewer code</h2>
                    <button
                        aria-label="Close"
                        className="UFOV-game__reviewer-access-close"
                        disabled={working}
                        onClick={onClose}
                        type="button"
                    >
                        <CloseIcon />
                    </button>
                </div>
                <input
                    aria-label="Reviewer code"
                    autoCapitalize="characters"
                    autoComplete="off"
                    autoCorrect="off"
                    autoFocus
                    disabled={working || accepted}
                    maxLength="80"
                    placeholder="Code"
                    spellCheck="false"
                    value={code}
                    onChange={(event) => {
                        setCode(event.target.value);
                        if (status !== 'idle') {
                            setStatus('idle');
                        }
                    }}
                />
                <p
                    aria-live="polite"
                    className={`UFOV-game__reviewer-access-status is-${status}`.trim()}
                >
                    {statusLabel}
                </p>
                <button
                    className="UFOV-game__reviewer-access-submit"
                    disabled={working || (!accepted && !code.trim())}
                    type="submit"
                >
                    {accepted ? 'Close' : working ? 'Checking...' : 'Continue'}
                </button>
            </form>
        </div>
    );

    return typeof document === 'undefined'
        ? modal
        : createPortal(modal, document.body);
}

export function UFOVOnboardingSettingsScreen({
    countdownHapticsEnabled = true,
    language = 'en',
    onCountdownHapticsChange,
    onLanguageChange,
    onNext,
    onReviewerAccessSubmit,
    totalSteps = null,
}) {
    const copy = getOnboardingCopy(language);
    const selectedLanguage = normalizeOnboardingLanguage(language);
    const primaryLanguageOptions = ONBOARDING_LANGUAGE_OPTIONS.slice(0, PRIMARY_ONBOARDING_LANGUAGE_COUNT);
    const selectedLanguageOption = ONBOARDING_LANGUAGE_OPTIONS.find((option) => option.id === selectedLanguage);
    const selectedLanguageIsPrimary = primaryLanguageOptions.some((option) => option.id === selectedLanguage);
    const collapsedLanguageOptions = selectedLanguageIsPrimary
        ? primaryLanguageOptions
        : [...primaryLanguageOptions.slice(0, PRIMARY_ONBOARDING_LANGUAGE_COUNT - 1), selectedLanguageOption].filter(Boolean);
    const [showAllLanguages, setShowAllLanguages] = React.useState(false);
    const [reviewerAccessOpen, setReviewerAccessOpen] = React.useState(false);
    const visibleLanguageOptions = showAllLanguages
        ? ONBOARDING_LANGUAGE_OPTIONS
        : collapsedLanguageOptions;

    return (
        <ScreenShell
            ariaLabel={copy.settingsTitle}
            className="UFOV-game__screen--onboarding UFOV-game__screen--onboarding-settings"
            header={(
                <div className="UFOV-game__heading">
                    <OnboardingProgress language={language} step={`1 / ${totalSteps || 1}`} />
                    <h1 className="UFOV-game__title is-compact">{copy.settingsTitle}</h1>
                    <p className="UFOV-game__subtitle">{copy.settingsDescription}</p>
                </div>
            )}
            main={(
                <>
                    <div className="UFOV-game__onboarding-settings-panel">
                        <div className="UFOV-game__settings-section-title">
                            <button
                                aria-label="Reviewer access"
                                className="UFOV-game__reviewer-access-trigger"
                                onClick={() => setReviewerAccessOpen(true)}
                                type="button"
                            >
                                <SettingsGlobeIcon />
                            </button>
                            <strong>{copy.languageTitle}</strong>
                        </div>

                        <div
                            className={`UFOV-game__language-options ${showAllLanguages ? 'is-expanded' : ''}`.trim()}
                            id="UFOV-onboarding-language-options"
                        >
                            {visibleLanguageOptions.map((option) => (
                                <button
                                    aria-pressed={selectedLanguage === option.id}
                                    className={`UFOV-game__language-option ${selectedLanguage === option.id ? 'is-selected' : ''}`.trim()}
                                    key={option.id}
                                    type="button"
                                    onClick={() => onLanguageChange?.(option.id)}
                                >
                                    <LanguageFlag language={option.id} />
                                    <span>{option.label}</span>
                                </button>
                            ))}
                        </div>
                        <button
                            aria-controls="UFOV-onboarding-language-options"
                            aria-expanded={showAllLanguages}
                            className={`UFOV-game__language-more-button ${showAllLanguages ? 'is-expanded' : ''}`.trim()}
                            type="button"
                            onClick={() => setShowAllLanguages((currentValue) => !currentValue)}
                        >
                            {showAllLanguages ? copy.showFewerLanguages : copy.showMoreLanguages}
                        </button>

                        {SHOW_VIBRATION_CONTROLS ? (
                            <>
                                <div aria-hidden="true" className="UFOV-game__settings-divider" />

                                <div className="UFOV-game__haptic-row">
                                    <SettingsVibrationIcon />
                                    <div className="UFOV-game__haptic-copy">
                                        <strong>{copy.vibrationTitle}</strong>
                                        <span>{copy.vibrationDescription}</span>
                                    </div>
                                    <label className="UFOV-game__toggle-control">
                                        <input
                                            checked={countdownHapticsEnabled}
                                            className="UFOV-game__toggle-input"
                                            type="checkbox"
                                            onChange={(event) => onCountdownHapticsChange?.(event.target.checked)}
                                        />
                                        <span aria-hidden="true" className="UFOV-game__toggle-track">
                                            <span className="UFOV-game__toggle-thumb" />
                                        </span>
                                        <span className="UFOV-game__toggle-label">{countdownHapticsEnabled ? copy.onLabel : copy.offLabel}</span>
                                    </label>
                                </div>
                            </>
                        ) : null}
                    </div>
                    <ReviewerAccessModal
                        onClose={() => setReviewerAccessOpen(false)}
                        onSubmit={onReviewerAccessSubmit}
                        open={reviewerAccessOpen}
                    />
                </>
            )}
            footer={(
                <button className="UFOV-game__primary-button" type="button" onClick={onNext}>
                    {copy.continueLabel}
                </button>
            )}
        />
    );
}

export function UFOVOnboardingScreen({
    buttonBusy = false,
    demoReady = true,
    demoVariantId = '',
    demoTrial = null,
    onDemoPrewarmReady = null,
    stepIndex,
    positions,
    selectedCenter,
    selectedPosition,
    onSelectCenter,
    onSelectPosition,
    onNext,
    canContinue = true,
    totalSteps = null,
    progressStepIndex = stepIndex,
    language = 'en',
    wrongNoticeKey = 0,
}) {
    const brand = getUFOVBrandConfig();
    const copy = getOnboardingCopy(language, brand);
    const onboardingSteps = getOnboardingSteps(brand, language);
    const step = onboardingSteps[Math.max(0, Math.min(onboardingSteps.length - 1, stepIndex))];
    const stepLabel = `${progressStepIndex + 1} / ${totalSteps || onboardingSteps.length}`;
    const isExampleStep = Boolean(step.example);
    const shouldMountDemoBoard = Boolean(
        demoVariantId
        && demoTrial
        && ['intro', 'example-board', 'center-question', 'direction-question'].includes(step.id)
    );
    const visibleDemoType = shouldMountDemoBoard && step.example ? step.example : '';
    const isImagePreviewStep = step.studyPreview || step.researchPreview;
    const titleClassName = `UFOV-game__title is-compact ${isImagePreviewStep ? 'is-study' : ''} ${step.researchPreview ? 'is-research' : ''}`.trim();
    const researchTitleLines = Array.isArray(copy.researchTitleLines)
        ? copy.researchTitleLines
        : [copy.researchTitle];
    const titleContent = step.researchPreview
        ? researchTitleLines.map((line) => <span key={line}>{line}</span>)
        : step.title;

    return (
        <ScreenShell
            ariaLabel={step.title}
            className="UFOV-game__screen--onboarding"
            header={(
                <div className="UFOV-game__heading">
                    <OnboardingProgress language={language} step={stepLabel} />
                    {!isExampleStep ? (
                        <>
                            <h1 className={titleClassName}>{titleContent}</h1>
                            {step.description ? (
                                <p className={`UFOV-game__subtitle ${isImagePreviewStep ? 'is-study' : ''} ${step.id === 'intro' ? 'is-intro' : ''}`.trim()}>{step.description}</p>
                            ) : null}
                        </>
                    ) : null}
                </div>
            )}
            main={(
                <div className={`UFOV-game__onboarding-stage ${visibleDemoType ? 'is-demo-layer-step' : ''}`.trim()}>
                    {step.studyPreview ? (
                        <OnboardingStudyPreview />
                    ) : step.researchPreview ? (
                        <OnboardingResearchPreview language={language} />
                    ) : step.rows ? (
                        <div className="UFOV-game__onboarding-step-content" key={step.id}>
                            <OnboardingRows rows={step.rows} />
                        </div>
                    ) : visibleDemoType ? (
                        !demoReady ? (
                            <div className="UFOV-game__onboarding-step-content" key={step.id}>
                                <div aria-label={copy.loading} className="UFOV-game__board-preload" role="status" />
                            </div>
                        ) : null
                    ) : demoVariantId ? (
                        <div className="UFOV-game__onboarding-step-content" key={step.id}>
                            <UFOVSymbolVariantProvider variantId={demoVariantId}>
                                <OnboardingExample
                                    demoReady={demoReady}
                                    demoTrial={demoTrial}
                                    language={language}
                                    onSelectCenter={onSelectCenter}
                                    onSelectPosition={onSelectPosition}
                                    positions={positions}
                                    selectedCenter={selectedCenter}
                                    selectedPosition={selectedPosition}
                                    type={step.example}
                                />
                            </UFOVSymbolVariantProvider>
                        </div>
                    ) : (
                        <div className="UFOV-game__onboarding-step-content" key={step.id}>
                            <OnboardingExample
                                demoReady={demoReady}
                                demoTrial={demoTrial}
                                language={language}
                                onSelectCenter={onSelectCenter}
                                onSelectPosition={onSelectPosition}
                                positions={positions}
                                selectedCenter={selectedCenter}
                                selectedPosition={selectedPosition}
                                type={step.example}
                            />
                        </div>
                    )}
                    {shouldMountDemoBoard ? (
                        <UFOVSymbolVariantProvider variantId={demoVariantId}>
                            <OnboardingDemoLayers
                                demoTrial={demoTrial}
                                language={language}
                                onReady={onDemoPrewarmReady}
                                onSelectCenter={onSelectCenter}
                                onSelectPosition={onSelectPosition}
                                positions={positions}
                                selectedCenter={selectedCenter}
                                selectedPosition={selectedPosition}
                                visible={Boolean(visibleDemoType) && demoReady}
                                visibleType={demoReady ? visibleDemoType : ''}
                            />
                        </UFOVSymbolVariantProvider>
                    ) : null}
                    {wrongNoticeKey > 0 ? (
                        <div
                            aria-live="polite"
                            className="UFOV-game__answer-toast"
                            key={wrongNoticeKey}
                            role="status"
                        >
                            <span aria-hidden="true">!</span>
                            {copy.wrongAnswer}
                        </div>
                    ) : null}
                </div>
            )}
            footer={(
                <button
                    className="UFOV-game__primary-button"
                    disabled={!canContinue}
                    type="button"
                    onClick={onNext}
                >
                    {buttonBusy ? <ButtonSpinner label={copy.loading} /> : step.buttonLabel}
                </button>
            )}
        />
    );
}

export function UFOVOnboardingReviewScreen({
    feedbackComment = '',
    feedbackError = '',
    feedbackRating = null,
    feedbackOpen = false,
    feedbackSubmitting = false,
    language = 'en',
    onFeedbackCancel,
    onFeedbackCommentChange,
    onFeedbackRatingChange,
    onFeedbackSubmit,
    progressLabel,
    onNext,
}) {
    const copy = getOnboardingCopy(language);

    return (
        <>
            <ScreenShell
                ariaLabel={copy.reviewTitle}
                className="UFOV-game__screen--onboarding UFOV-game__screen--assessment-ready"
                header={(
                    <div className="UFOV-game__heading">
                        <OnboardingProgress language={language} step={progressLabel} />
                        <h1 className="UFOV-game__title is-compact">{copy.reviewTitle}</h1>
                    </div>
                )}
                main={(
                    <div className="UFOV-game__onboarding-stage is-assessment-ready">
                        <RatingExperienceIllustration />
                        <div className="UFOV-game__assessment-copy">
                            {copy.reviewLines.map((line) => <p key={line}>{line}</p>)}
                        </div>
                    </div>
                )}
                footer={(
                    <button
                        className="UFOV-game__primary-button"
                        type="button"
                        onClick={onNext}
                    >
                        {copy.continueLabel}
                    </button>
                )}
            />
            <UFOVReviewFeedbackModal
                comment={feedbackComment}
                error={feedbackError}
                language={language}
                rating={feedbackRating}
                open={feedbackOpen}
                submitting={feedbackSubmitting}
                onCancel={onFeedbackCancel}
                onCommentChange={onFeedbackCommentChange}
                onRatingChange={onFeedbackRatingChange}
                onSubmit={onFeedbackSubmit}
            />
        </>
    );
}

function UFOVReviewFeedbackModal({
    comment = '',
    error = '',
    language = 'en',
    rating = null,
    open = false,
    submitting = false,
    onCancel,
    onCommentChange,
    onRatingChange,
    onSubmit,
}) {
    const copy = getOnboardingCopy(language);
    const dialogRef = useDialogFocusTrap(open, submitting ? null : onCancel);

    if (!open) {
        return null;
    }

    const modal = (
        <div className="UFOV-game__review-modal-backdrop">
            <div
                aria-labelledby="UFOV-review-feedback-title"
                aria-modal="true"
                className="UFOV-game__review-modal"
                ref={dialogRef}
                role="dialog"
                tabIndex={-1}
            >
                <div className="UFOV-game__review-modal-head">
                    <h2 id="UFOV-review-feedback-title">{copy.feedbackModalTitle}</h2>
                    <p>{copy.feedbackModalDescription}</p>
                </div>

                <fieldset className="UFOV-game__review-rating-field">
                    <legend>{copy.feedbackRatingLabel}</legend>
                    <div className="UFOV-game__review-stars" role="radiogroup" aria-label={copy.feedbackRatingLabel}>
                        {[1, 2, 3, 4, 5].map((value) => (
                            <button
                                aria-checked={rating === value}
                                aria-label={(copy.feedbackStarLabel || '{count} out of 5 stars').replace('{count}', value)}
                                className={rating >= value ? 'is-selected' : ''}
                                disabled={submitting}
                                key={value}
                                role="radio"
                                type="button"
                                onClick={() => onRatingChange?.(value)}
                            >
                                <span aria-hidden="true">★</span>
                            </button>
                        ))}
                    </div>
                </fieldset>

                <p className="UFOV-game__review-comment-intro">{copy.feedbackCommentIntro}</p>
                <label className="UFOV-game__review-comment-field">
                    <span>{copy.feedbackCommentLabel}</span>
                    <textarea
                        disabled={submitting}
                        maxLength={2000}
                        placeholder={copy.feedbackCommentPlaceholder}
                        rows={4}
                        value={comment}
                        onChange={(event) => onCommentChange?.(event.target.value)}
                    />
                </label>

                {error ? (
                    <p className="UFOV-game__review-modal-error" role="alert">{error || copy.feedbackError}</p>
                ) : null}

                <div className="UFOV-game__review-modal-actions">
                    <button
                        className="UFOV-game__review-modal-secondary"
                        disabled={submitting}
                        type="button"
                        onClick={onCancel}
                    >
                        {copy.feedbackSkip}
                    </button>
                    <button
                        className="UFOV-game__review-modal-primary"
                        disabled={submitting || !rating}
                        type="button"
                        onClick={onSubmit}
                    >
                        {submitting ? copy.feedbackSaving : copy.feedbackConfirm}
                    </button>
                </div>
            </div>
        </div>
    );

    return typeof document === 'undefined'
        ? modal
        : createPortal(modal, document.body);
}

function RatingExperienceIllustration() {
    return (
        <div className="UFOV-game__assessment-art" aria-hidden="true">
            <img
                alt=""
                className="UFOV-game__assessment-image"
                decoding="async"
                draggable="false"
                fetchpriority="high"
                loading="eager"
                src={UFOV_ONBOARDING_STATIC_ASSETS.ratingIllustration}
            />
        </div>
    );
}

function AssessmentReadyIllustration({ src = UFOV_ONBOARDING_STATIC_ASSETS.assessmentIllustration }) {
    return (
        <div className="UFOV-game__assessment-art" aria-hidden="true">
            <img
                alt=""
                className="UFOV-game__assessment-image"
                decoding="async"
                draggable="false"
                fetchpriority="high"
                loading="eager"
                src={src}
            />
        </div>
    );
}

export function UFOVOnboardingPanelScreen({
    title,
    description,
    buttonLabel,
    language = 'en',
    progressLabel,
    onNext,
}) {
    const copy = getOnboardingCopy(language);
    const resolvedTitle = title || copy.panelTitle;
    const resolvedDescription = description || copy.panelDescription;
    const resolvedButtonLabel = buttonLabel || copy.panelButton;

    return (
        <ScreenShell
            ariaLabel={resolvedTitle}
            className="UFOV-game__screen--onboarding UFOV-game__screen--assessment-ready"
            header={(
                <div className="UFOV-game__heading">
                    <OnboardingProgress language={language} step={progressLabel} />
                    <h1 className="UFOV-game__title is-compact">{resolvedTitle}</h1>
                </div>
            )}
            main={(
                <div className="UFOV-game__onboarding-stage is-assessment-ready">
                    <AssessmentReadyIllustration src={CALIBRATION_ILLUSTRATION_SRC} />
                    <div className="UFOV-game__assessment-copy">
                        {`${resolvedDescription || ''}`.split('\n').filter(Boolean).map((line) => (
                            <p key={line}>{line}</p>
                        ))}
                    </div>
                </div>
            )}
            footer={(
                <button className="UFOV-game__primary-button" type="button" onClick={onNext}>
                    {resolvedButtonLabel}
                </button>
            )}
        />
    );
}

export function UFOVOnboardingAgeOptInScreen({
    language = 'en',
    progressLabel,
    onCalculate,
    onSkip,
}) {
    const copy = getOnboardingCopy(language);

    return (
        <ScreenShell
            ariaLabel={copy.ageOptInTitle}
            className="UFOV-game__screen--onboarding UFOV-game__screen--assessment-ready"
            header={(
                <div className="UFOV-game__heading">
                    <OnboardingProgress language={language} step={progressLabel} />
                    <h1 className="UFOV-game__title is-compact">{copy.ageOptInTitle}</h1>
                    <p className="UFOV-game__subtitle">{copy.ageOptInDescription}</p>
                </div>
            )}
            main={(
                <div className="UFOV-game__onboarding-stage is-assessment-ready">
                    <AssessmentReadyIllustration />
                    <div className="UFOV-game__assessment-copy">
                        <p>{copy.ageOptInNote}</p>
                    </div>
                </div>
            )}
            footer={(
                <>
                    <button className="UFOV-game__primary-button" type="button" onClick={onCalculate}>
                        {copy.calculateAge}
                    </button>
                    <button className="UFOV-game__secondary-button" type="button" onClick={onSkip}>
                        {copy.skipAge}
                    </button>
                </>
            )}
        />
    );
}

export function UFOVOnboardingAgeScreen({
    estimatedAge,
    language = 'en',
    progressLabel,
    onNext,
}) {
    const copy = getOnboardingCopy(language);

    return (
        <ScreenShell
            ariaLabel={copy.ageTitle}
            className="UFOV-game__screen--onboarding"
            header={(
                <div className="UFOV-game__heading">
                    <OnboardingProgress language={language} step={progressLabel} />
                    <h1 className="UFOV-game__title is-compact">{copy.ageTitle}</h1>
                    <p className="UFOV-game__subtitle">{copy.ageDescription}</p>
                </div>
            )}
            main={(
                <div className="UFOV-game__onboarding-stage">
                    <div className="UFOV-game__onboarding-age-card">
                        <span>{copy.ageLabel}</span>
                        <strong>{estimatedAge} {copy.yearsOld}</strong>
                    </div>
                </div>
            )}
            footer={(
                <button className="UFOV-game__primary-button" type="button" onClick={onNext}>
                    {copy.continueLabel}
                </button>
            )}
        />
    );
}

export function UFOVOnboardingShareScreen({
    estimatedAge,
    language = 'en',
    progressLabel,
    onContinue,
}) {
    const copy = getOnboardingCopy(language);
    const calculateAgeCopy = CALCULATE_AGE_LOCALES[normalizeUFOVLanguage(language)] || CALCULATE_AGE_LOCALES.en;
    const shareMessage = calculateAgeCopy.shareMessage
        .replace(/\{age\}/g, `${estimatedAge}`)
        .replace(/\{url\}/g, 'https://sopt.es/calculate-age');
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    const openWhatsApp = () => {
        if (typeof window === 'undefined') {
            return;
        }

        if (window.ReactNativeWebView) {
            window.location.assign(whatsappUrl);
            return;
        }

        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <ScreenShell
            ariaLabel={copy.shareAgeTitle}
            className="UFOV-game__screen--onboarding UFOV-game__screen--onboarding-share"
            header={(
                <div className="UFOV-game__heading">
                    <OnboardingProgress language={language} step={progressLabel} />
                    <h1 className="UFOV-game__title is-compact">{copy.shareAgeTitle}</h1>
                    <p className="UFOV-game__subtitle">{copy.shareAgeDescription}</p>
                </div>
            )}
            main={(
                <div className="UFOV-game__onboarding-stage">
                    <div className="UFOV-game__onboarding-share-result">
                        <span>{copy.ageLabel}</span>
                        <strong>{estimatedAge}</strong>
                        <small>{copy.yearsOld}</small>
                    </div>
                </div>
            )}
            footer={(
                <>
                    <button className="UFOV-game__whatsapp-button" type="button" onClick={openWhatsApp}>
                        {copy.shareAgeButton}
                    </button>
                    <button className="UFOV-game__primary-button" type="button" onClick={onContinue}>
                        {copy.continueLabel}
                    </button>
                </>
            )}
            footerClassName="UFOV-game__onboarding-share-footer"
        />
    );
}

export function UFOVOnboardingDemographicsScreen({
    language = 'en',
    progressLabel,
    onSkip,
    onSubmit,
}) {
    const copy = getOnboardingCopy(language);
    const [age, setAge] = React.useState('');
    const [gender, setGender] = React.useState('');
    const formId = 'UFOV-onboarding-demographics-form';
    const descriptionId = 'UFOV-onboarding-demographics-description';
    const genderOptions = [
        ['woman', copy.demographicsGenderWoman],
        ['man', copy.demographicsGenderMan],
        ['non_binary', copy.demographicsGenderNonBinary],
        ['another_identity', copy.demographicsGenderAnother],
        ['prefer_not_to_say', copy.demographicsGenderPreferNot],
    ];
    const submit = (event) => {
        event.preventDefault();
        const parsedAge = age === '' ? null : Number.parseInt(age, 10);

        onSubmit?.({
            age: Number.isInteger(parsedAge) ? parsedAge : null,
            gender: gender === 'prefer_not_to_say' ? null : (gender || null),
        });
    };

    return (
        <ScreenShell
            ariaLabel={copy.demographicsTitle}
            className="UFOV-game__screen--onboarding UFOV-game__screen--onboarding-demographics"
            header={(
                <div className="UFOV-game__heading">
                    <OnboardingProgress language={language} step={progressLabel} />
                    <h1 className="UFOV-game__title is-compact">{copy.demographicsTitle}</h1>
                    <p className="UFOV-game__subtitle" id={descriptionId}>{copy.demographicsDescription}</p>
                </div>
            )}
            main={(
                <form
                    aria-describedby={descriptionId}
                    className="UFOV-game__demographics-form"
                    id={formId}
                    onSubmit={submit}
                >
                    <label className="UFOV-game__demographics-age-field">
                        <span>{copy.demographicsAgeLabel}</span>
                        <input
                            autoComplete="off"
                            inputMode="numeric"
                            max="120"
                            min="18"
                            placeholder={copy.demographicsAgePlaceholder}
                            step="1"
                            type="number"
                            value={age}
                            onChange={(event) => setAge(event.target.value)}
                        />
                    </label>

                    <fieldset className="UFOV-game__demographics-gender-field">
                        <legend>{copy.demographicsGenderLabel}</legend>
                        <div className="UFOV-game__demographics-gender-options">
                            {genderOptions.map(([value, label]) => (
                                <label className="UFOV-game__demographics-gender-option" key={value}>
                                    <input
                                        checked={gender === value}
                                        name="UFOV-onboarding-gender"
                                        type="radio"
                                        value={value}
                                        onChange={() => setGender(value)}
                                    />
                                    <span>{label}</span>
                                </label>
                            ))}
                        </div>
                    </fieldset>
                </form>
            )}
            mainClassName="UFOV-game__onboarding-demographics-main"
            footer={(
                <>
                    <button className="UFOV-game__primary-button" form={formId} type="submit">
                        {copy.continueLabel}
                    </button>
                    <button className="UFOV-game__secondary-button" type="button" onClick={onSkip}>
                        {copy.demographicsSkip}
                    </button>
                </>
            )}
        />
    );
}

function TargetIcon() {
    return (
        <svg aria-hidden="true" className="UFOV-game__menu-icon" viewBox="0 0 40 40">
            <circle {...SVG_STROKE} cx="20" cy="20" r="9" />
            <circle {...SVG_STROKE} cx="20" cy="20" r="2.5" />
            <path {...SVG_STROKE} d="M20 4v7" />
            <path {...SVG_STROKE} d="M20 29v7" />
            <path {...SVG_STROKE} d="M4 20h7" />
            <path {...SVG_STROKE} d="M29 20h7" />
        </svg>
    );
}

function AdvancedModeIcon() {
    return (
        <svg aria-hidden="true" className="UFOV-game__menu-icon is-advanced" viewBox="0 0 40 40">
            <g fill="none" stroke="#119b72" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.8">
                <circle cx="15.5" cy="24.5" r="8.8" />
                <circle cx="15.5" cy="24.5" r="2.3" />
                <path d="M15.5 9.5v6" />
                <path d="M15.5 33.5v4" />
                <path d="M.5 24.5h6" />
                <path d="M24.3 24.5h4" />
            </g>
            <g fill="none" stroke="#e54d42" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2">
                <circle cx="29" cy="11" r="5.2" />
                <circle cx="29" cy="11" r="1.4" />
                <path d="M29 2.5v3" />
                <path d="M29 16.2v3.3" />
                <path d="M20.5 11h3.3" />
                <path d="M34.2 11h3.3" />
            </g>
        </svg>
    );
}

function SOPTLogoMenuIcon({ advanced = false, src }) {
    const logoSrc = src || MENU_LOGO_SRC;

    return (
        <span className={`UFOV-game__sopt-menu-icon${advanced ? ' is-advanced' : ''}`}>
            <img
                alt=""
                aria-hidden="true"
                className="UFOV-game__sopt-menu-logo is-front"
                decoding="async"
                src={logoSrc}
            />
        </span>
    );
}

function EvidenceBadgeIcon() {
    return (
        <svg aria-hidden="true" className="UFOV-game__mode-badge-icon" viewBox="0 0 640 640">
            <path opacity="1" fill="currentColor" d="M192 433.5L192 569.2C192 587 210.8 598.6 226.7 590.7L320 544L413.3 590.6C429.3 598.6 448 587 448 569.1C448 523.9 448 478.7 447.9 433.7C440.4 447.5 425.7 455.9 410 455.6C381.4 454.9 366.3 459 341.9 473.9C328.5 482.1 311.6 482.1 298.2 473.9C273.8 459 258.7 455 230.1 455.6C214.4 456 199.8 447.5 192.1 433.5z" />
            <path fill="currentColor" d="M341.9 38.1C328.5 29.9 311.6 29.9 298.2 38.1C273.8 53 258.7 57 230.1 56.4C214.4 56 199.8 64.5 192.2 78.3C178.5 103.4 167.4 114.5 142.3 128.2C128.5 135.7 120.1 150.4 120.4 166.1C121.1 194.7 117 209.8 102.1 234.2C93.9 247.6 93.9 264.5 102.1 277.9C117 302.3 121 317.4 120.4 346C120 361.7 128.5 376.3 142.3 383.9C167.4 397.6 178.5 408.7 192.2 433.8C199.7 447.6 214.4 456 230.1 455.7C258.7 455 273.8 459.1 298.2 474C311.6 482.2 328.5 482.2 341.9 474C366.3 459.1 381.4 455.1 410 455.7C425.7 456.1 440.3 447.6 447.9 433.8C461.6 408.7 472.7 397.6 497.8 383.9C511.6 376.4 520 361.7 519.7 346C519 317.4 523.1 302.3 538 277.9C546.2 264.5 546.2 247.6 538 234.2C523.1 209.8 519.1 194.7 519.7 166.1C520.1 150.4 511.6 135.8 497.8 128.2C472.7 114.5 461.6 103.4 447.9 78.3C440.4 64.5 425.7 56.1 410 56.4C381.4 57.1 366.3 53 341.9 38.1zM320 160C373 160 416 203 416 256C416 309 373 352 320 352C267 352 224 309 224 256C224 203 267 160 320 160z" />
        </svg>
    );
}

function CalendarIcon() {
    return (
        <svg aria-hidden="true" className="UFOV-game__small-icon" viewBox="0 0 28 28">
            <rect {...SVG_STROKE} x="4" y="6" width="20" height="18" rx="5" />
            <path {...SVG_STROKE} d="M9 3v6" />
            <path {...SVG_STROKE} d="M19 3v6" />
            <path {...SVG_STROKE} d="M4 11h20" />
            <circle cx="10" cy="16" r="1.4" fill="currentColor" />
            <circle cx="18" cy="16" r="1.4" fill="currentColor" />
            <circle cx="10" cy="20" r="1.4" fill="currentColor" />
        </svg>
    );
}

function HelpIcon() {
    return (
        <svg aria-hidden="true" className="UFOV-game__footer-icon" viewBox="0 0 28 28">
            <circle {...SVG_STROKE} cx="14" cy="14" r="10.5" />
            <path {...SVG_STROKE} d="M10.7 10.5a3.5 3.5 0 0 1 6.2 2.1c0 2.3-2.9 2.6-2.9 4.5" />
            <circle cx="14" cy="19.7" r="1.3" fill="currentColor" />
        </svg>
    );
}

function GearIcon() {
    return (
        <svg aria-hidden="true" className="UFOV-game__footer-icon is-settings" viewBox="0 0 28 28">
            <path
                d="M11.7 3.8h4.6l.7 3c.7.3 1.4.6 2 1.1l2.9-1 2.3 4-2.2 2c.1.7.1 1.5 0 2.2l2.2 2-2.3 4-2.9-1c-.6.5-1.3.9-2 1.1l-.7 3h-4.6l-.7-3c-.7-.3-1.4-.6-2-1.1l-2.9 1-2.3-4 2.2-2c-.1-.7-.1-1.5 0-2.2l-2.2-2 2.3-4 2.9 1c.6-.5 1.3-.9 2-1.1l.7-3Z"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
            />
            <circle cx="14" cy="14" r="3.2" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
    );
}

function BarsIcon() {
    return (
        <svg aria-hidden="true" className="UFOV-game__progress-bars-icon" viewBox="0 0 28 28">
            <path d="M7 20v-5M14 20V8M21 20v-9" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="3.2" />
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg aria-hidden="true" className="UFOV-game__footer-icon" viewBox="0 0 24 24">
            <path d="m5 5 14 14M19 5 5 19" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="2.4" />
        </svg>
    );
}

function InfinityIcon() {
    return (
        <svg aria-hidden="true" className="UFOV-game__billing-svg" viewBox="0 0 54 30">
            <defs>
                <linearGradient id="UFOV-paywall-gradient" x1="0%" x2="100%" y1="50%" y2="50%">
                    <stop offset="0%" stopColor="#35c9c1" />
                    <stop offset="52%" stopColor="#f2c04b" />
                    <stop offset="100%" stopColor="#ff7a66" />
                </linearGradient>
            </defs>
            <path d="M8 15c3.3-5.8 9.7-9.3 15 0 5.4 9.3 11.8 5.8 15 0s9.7-5.8 15 0" fill="none" stroke="url(#UFOV-paywall-gradient)" strokeLinecap="round" strokeWidth="4.4" />
        </svg>
    );
}

function SlidersIcon() {
    return (
        <svg aria-hidden="true" className="UFOV-game__billing-svg" viewBox="0 0 44 44">
            <path d="M8 12h28M8 22h28M8 32h28" fill="none" stroke="url(#UFOV-paywall-gradient-2)" strokeLinecap="round" strokeWidth="3" />
            <circle cx="17" cy="12" r="4.2" fill="#fff" stroke="#35c9c1" strokeWidth="2.6" />
            <circle cx="25" cy="22" r="4.2" fill="#fff" stroke="#f2c04b" strokeWidth="2.6" />
            <circle cx="14" cy="32" r="4.2" fill="#fff" stroke="#ff7a66" strokeWidth="2.6" />
            <defs>
                <linearGradient id="UFOV-paywall-gradient-2" x1="0%" x2="100%" y1="50%" y2="50%">
                    <stop offset="0%" stopColor="#35c9c1" />
                    <stop offset="52%" stopColor="#f2c04b" />
                    <stop offset="100%" stopColor="#ff7a66" />
                </linearGradient>
            </defs>
        </svg>
    );
}

function CloudCheckIcon() {
    return (
        <svg aria-hidden="true" className="UFOV-game__billing-svg" viewBox="0 0 48 36">
            <path d="M16 29h18a8 8 0 0 0 1-16 11 11 0 0 0-20-2 8 8 0 0 0 1 18Z" fill="none" stroke="#0f5d68" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.4" />
            <path d="m20 21 3.2 3.2L29.5 18" fill="none" stroke="#f2c04b" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.8" />
        </svg>
    );
}

function getResultCopy(feedback, copy) {
    if (feedback?.perfect) {
        return {
            title: copy.wellDone,
            subtitle: copy.gotItRight,
        };
    }

    const caughtAnyTarget = feedback?.centerCorrect
        || feedback?.positionCorrect
        || (feedback?.modeId === 'advanced' && feedback?.peripheralCorrect);

    if (caughtAnyTarget) {
        return {
            title: copy.goodRead,
            subtitle: copy.caughtPart,
        };
    }

    return {
        title: copy.signalLost,
        subtitle: copy.keepTrainingEdge,
    };
}

function getResultDifficultyAxis(feedback, progress, copy) {
    if (!feedback?.difficultyChanged || !feedback?.levelDelta) {
        return null;
    }

    return copy.speed;
}

function ResultGlyph({ correct, label, value, isSymbol = false, direction = false }) {
    return (
        <div className={`UFOV-game__result-glyph ${correct ? 'is-good' : 'is-bad'} ${direction ? 'is-direction' : ''}`.trim()}>
            <span className="UFOV-game__result-glyph-label">{label}</span>
            <span className="UFOV-game__result-glyph-badge" aria-hidden="true">
                {correct ? '✓' : '×'}
            </span>
            <strong>
                <span className="UFOV-game__result-glyph-orb">
                    {isSymbol ? <UFOVSymbol symbol={value} usage="result" /> : value}
                </span>
            </strong>
        </div>
    );
}

function ResultLevelChange({ feedback, progress, copy }) {
    if (!feedback?.difficultyChanged || !feedback?.levelDelta) {
        return null;
    }

    const levelWentUp = feedback.levelDelta > 0;
    const axis = getResultDifficultyAxis(feedback, progress, copy);
    const directionSymbol = levelWentUp ? '\u2191' : '\u2193';

    return (
        <div className={`UFOV-game__result-change ${levelWentUp ? 'is-up' : 'is-down'}`}>
            <div className="UFOV-game__result-level-pill">
                <span aria-hidden="true">{directionSymbol}</span>
                <strong>{copy.difficultyChange || 'Difficulty change'}</strong>
            </div>
            {axis ? (
                <div className="UFOV-game__result-axis-pill">
                    {axis} {directionSymbol}
                </div>
            ) : null}
        </div>
    );
}

function ProgressTrack({ value, compact = false, tone = '' }) {
    const className = [
        'UFOV-game__progress-track',
        compact ? 'is-compact' : '',
        tone ? `is-${tone}` : '',
    ].filter(Boolean).join(' ');

    return (
        <span className={className} aria-hidden="true">
            <span className="UFOV-game__progress-track-fill" style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
        </span>
    );
}

const MENU_TOUR_ITEM_IDS = Object.freeze(['classic', 'advanced', 'daily', 'progress']);

function UFOVMenuTourOverlay({ step = -1, onDismiss, onNext, language = 'en' }) {
    const progressCopy = getProgressCopy(language);
    const tourItemId = MENU_TOUR_ITEM_IDS[step];
    const tourCopy = progressCopy.tutorialSteps?.[step];
    const open = Boolean(tourItemId && tourCopy);
    const dialogRef = useDialogFocusTrap(open, onDismiss);
    const isLastStep = step === MENU_TOUR_ITEM_IDS.length - 1;

    if (!open) {
        return null;
    }

    const overlay = (
        <div
            className={`UFOV-game__menu-tour-overlay is-for-${tourItemId}`}
            role="dialog"
            aria-modal="true"
            aria-label={tourCopy[0]}
            ref={dialogRef}
            tabIndex={-1}
        >
            <div className="UFOV-game__menu-tour-bubble">
                <div className="UFOV-game__menu-tour-head">
                    <span>{step + 1} / {MENU_TOUR_ITEM_IDS.length}</span>
                    <button
                        aria-label={progressCopy.tutorialClose}
                        className="UFOV-game__menu-tour-close"
                        type="button"
                        onClick={onDismiss}
                    >
                        <X aria-hidden="true" />
                    </button>
                </div>
                <strong>{tourCopy[0]}</strong>
                <p>{tourCopy[1]}</p>
                <button
                    className="UFOV-game__menu-tour-next"
                    data-dialog-autofocus="true"
                    type="button"
                    onClick={onNext}
                >
                    {isLastStep ? progressCopy.tutorialDone : progressCopy.tutorialNext}
                </button>
            </div>
        </div>
    );

    return typeof document === 'undefined'
        ? overlay
        : createPortal(overlay, document.body);
}

function PaywallFeature({ icon, title, description }) {
    return (
        <div className="UFOV-game__paywall-feature">
            <div className="UFOV-game__paywall-feature-icon">{icon}</div>
            <div className="UFOV-game__paywall-feature-copy">
                <strong>{title}</strong>
                <span>{description}</span>
            </div>
        </div>
    );
}

function PaywallPlanCard({ plan, selected, onSelect }) {
    return (
        <button
            className={`UFOV-game__paywall-plan is-${plan.planKind || plan.id} ${selected ? 'is-selected' : ''}`}
            type="button"
            onClick={() => onSelect(plan.id)}
        >
            <span className="UFOV-game__paywall-radio" aria-hidden="true">
                <span className="UFOV-game__paywall-radio-dot" />
            </span>
            <span className="UFOV-game__paywall-plan-copy">
                <span className="UFOV-game__paywall-plan-name">{plan.label}</span>
                <span className="UFOV-game__paywall-plan-price-row">
                    {plan.comparisonPrice ? (
                        <span className="UFOV-game__paywall-plan-price-old">{plan.comparisonPrice}</span>
                    ) : null}
                    <span className="UFOV-game__paywall-plan-price">{plan.priceLine}</span>
                </span>
                <span className="UFOV-game__paywall-plan-note">{plan.note}</span>
            </span>
            {plan.discountLabel || plan.badge ? (
                <span className="UFOV-game__paywall-plan-badge">{plan.discountLabel || plan.badge}</span>
            ) : null}
        </button>
    );
}

function MenuModeCard({
    active,
    locked,
    highlighted = false,
    className = '',
    controlsId,
    disclosure,
    expanded,
    title,
    subtitle,
    badge,
    footer,
    icon,
    onClick,
}) {
    const cardClassName = [
        'UFOV-game__mode-card',
        active ? 'is-active' : '',
        locked ? 'is-locked' : '',
        highlighted ? 'is-tour-highlight' : '',
        footer ? 'has-footer' : '',
        className,
    ].filter(Boolean).join(' ');

    return (
        <button
            aria-controls={controlsId || undefined}
            aria-expanded={typeof expanded === 'boolean' ? expanded : undefined}
            aria-pressed={active}
            className={cardClassName}
            disabled={locked}
            type="button"
            onClick={onClick}
        >
            <span className="UFOV-game__mode-icon">{icon}</span>
            <span className="UFOV-game__mode-copy">
                <strong>{title}</strong>
                <span>{subtitle}</span>
            </span>
            {badge || disclosure ? (
                <span className="UFOV-game__mode-actions">
                    {badge ? <span className="UFOV-game__mode-badge">{badge}</span> : null}
                    {disclosure ? <span className="UFOV-game__mode-disclosure">{disclosure}</span> : null}
                </span>
            ) : null}
            {footer}
        </button>
    );
}

function LevelRing({ level, label = 'LEVEL', accessibleLabel = label, actionLabel = 'Change level', onClick = null }) {
    const Component = onClick ? 'button' : 'div';

    return (
        <Component
            aria-label={`${accessibleLabel} ${level}${onClick ? `. ${actionLabel}.` : ''}`}
            className={`UFOV-game__level-ring ${onClick ? 'is-clickable' : ''}`.trim()}
            type={onClick ? 'button' : undefined}
            onClick={onClick || undefined}
        >
            <div className="UFOV-game__level-ring-inner">
                <span>{label}</span>
                <strong>{level}</strong>
            </div>
        </Component>
    );
}

export function UFOVMenuScreen({
    ageEstimateIsCanonical = false,
    brainGameLevels = {},
    dailyProgressByItem = {},
    dailyTarget = 20,
    estimatedAge = 85,
    activeMode,
    brainGameMenuItems = [],
    language = 'en',
    modeLevels = {},
    onSelectMode,
    onStart,
    onConfig,
    onFeedback,
    onHelp,
    onSelectBrainGame,
    onProgress,
    onChangeLevel = null,
    selectedBrainGameId = '',
    startDisabled = false,
    startLabel = 'Start run',
    tourStep = -1,
    onTourDismiss = null,
    onTourNext = null,
}) {
    const brand = getUFOVBrandConfig();
    const copy = getUFOVGameCopy(language, brand);
    const progressCopy = getProgressCopy(language);
    const gameConfig = getUFOVConfig().game || {};
    const useImageMenuIcons = gameConfig.menuIconStyle === 'image';
    const menuIconAssets = gameConfig.menuIconAssets || {};
    const logoSrc = brand.logoMarkUrl || MENU_LOGO_SRC;
    const isTourActive = tourStep >= 0;
    const tourTargetId = isTourActive ? MENU_TOUR_ITEM_IDS[tourStep] : '';
    const visibleBrainGameMenuItems = Array.isArray(brainGameMenuItems) ? brainGameMenuItems : [];
    const otherBrainGameMenuItems = visibleBrainGameMenuItems.filter((game) => (game.group || 'other') === 'other');
    const normalizedMenuLanguage = normalizeUFOVLanguage(language);
    const menuAgeLabel = MENU_AGE_LABELS[normalizedMenuLanguage] || MENU_AGE_LABELS.en;
    const levelBasedAgeLabel = MENU_LEVEL_BASED_AGE_LABELS[normalizedMenuLanguage]
        || MENU_LEVEL_BASED_AGE_LABELS.en;
    const safeEstimatedAge = Number.isFinite(Number(estimatedAge)) ? Math.round(Number(estimatedAge)) : 85;
    const [levelEditorTarget, setLevelEditorTarget] = React.useState(null);
    const [levelDraft, setLevelDraft] = React.useState(0);
    const levelEditorOpen = Boolean(levelEditorTarget);
    const [expandedMenuItemId, setExpandedMenuItemId] = React.useState(selectedBrainGameId || '');
    const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);
    const levelEditorSheetRef = React.useRef(null);
    const levelEditorReturnFocusRef = React.useRef(null);
    const menuScrollRef = React.useRef(null);
    const preExpansionScrollTopRef = React.useRef(0);
    const wasTourActiveRef = React.useRef(isTourActive);
    const getTourHighlightClass = (targetId) => (
        isTourActive && tourTargetId === targetId ? ' is-tour-highlight' : ''
    );
    const normalizeLevelDraft = (
        value,
        minimum = levelEditorTarget?.minimum ?? 0,
        maximum = levelEditorTarget?.maximum ?? 100
    ) => {
        const parsedLevel = Number(value);

        return Math.max(
            minimum,
            Math.min(maximum, Number.isFinite(parsedLevel) ? Math.round(parsedLevel) : minimum)
        );
    };
    const openLevelEditor = ({ id, level, maximum = 100, minimum = 0, title }) => {
        if (!onChangeLevel || isTourActive) {
            return;
        }

        levelEditorReturnFocusRef.current = typeof document !== 'undefined' ? document.activeElement : null;
        setLevelDraft(normalizeLevelDraft(level, minimum, maximum));
        setLevelEditorTarget({ id, maximum, minimum, title });
    };
    const closeLevelEditor = () => {
        setLevelEditorTarget(null);

        if (typeof window !== 'undefined') {
            window.requestAnimationFrame(() => levelEditorReturnFocusRef.current?.focus?.());
        }
    };
    const adjustLevelDraft = (delta) => {
        setLevelDraft((currentLevel) => normalizeLevelDraft((Number(currentLevel) || 0) + delta));
    };
    const applyLevelEditor = () => {
        onChangeLevel?.(levelEditorTarget?.id, levelDraft);
        closeLevelEditor();
    };
    const soptLogoSrc = menuIconAssets.soptLogo || brand.logoMarkUrl || MENU_LOGO_SRC;
    const renderSOPTIcon = (fallbackIcon, advanced = false) => (
        useImageMenuIcons ? <SOPTLogoMenuIcon advanced={advanced} src={soptLogoSrc} /> : fallbackIcon
    );
    const renderBrainGameIcon = (game) => <BrainGameCardIcon gameId={game.id} />;
    const handleLevelEditorKeyDown = (event) => {
        const isSheetTarget = event.target === event.currentTarget;

        if (event.key === 'Tab') {
            const controls = [...event.currentTarget.querySelectorAll('button:not(:disabled), [role="spinbutton"][tabindex="0"]')];
            const firstControl = controls[0];
            const lastControl = controls[controls.length - 1];

            if (event.shiftKey && (isSheetTarget || event.target === firstControl)) {
                event.preventDefault();
                lastControl?.focus();
            } else if (!event.shiftKey && (isSheetTarget || event.target === lastControl)) {
                event.preventDefault();
                firstControl?.focus();
            }
        }

        if (event.key === 'Enter' && isSheetTarget) {
            event.preventDefault();
            applyLevelEditor();
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            closeLevelEditor();
        }

        if (isSheetTarget && (event.key === 'ArrowUp' || event.key === 'ArrowRight')) {
            event.preventDefault();
            adjustLevelDraft(1);
        }

        if (isSheetTarget && (event.key === 'ArrowDown' || event.key === 'ArrowLeft')) {
            event.preventDefault();
            adjustLevelDraft(-1);
        }
    };
    const toggleMenuItem = (itemId, selectItem) => {
        if (isTourActive) {
            return;
        }

        const isCollapsing = expandedMenuItemId === itemId;

        if (!expandedMenuItemId) {
            preExpansionScrollTopRef.current = menuScrollRef.current?.scrollTop || 0;
        }

        setExpandedMenuItemId(isCollapsing ? '' : itemId);

        if (isCollapsing && typeof window !== 'undefined') {
            window.requestAnimationFrame(() => {
                menuScrollRef.current?.scrollTo({
                    behavior: 'auto',
                    top: preExpansionScrollTopRef.current,
                });
            });
        }

        selectItem?.();
    };
    const renderMenuPreview = ({
        id,
        itemId,
        title,
        videoSrc,
        posterSrc,
        orientation = 'portrait',
        level = 0,
        levelMaximum = 100,
        levelMinimum = 0,
        canEditLevel = true,
    }) => {
        const numericLevel = Number(level);
        const safeSoptLevel = Number.isFinite(numericLevel)
            ? Math.max(0, Math.min(100, Math.round(numericLevel)))
            : 0;
        const difficultyTier = SOPT_MENU_ITEM_IDS.has(itemId)
            ? getVideoBucketForLevel(safeSoptLevel).label.toLowerCase()
            : getLevelDifficultyTier(level, levelMinimum, levelMaximum);
        const difficultyLabel = copy[MENU_DIFFICULTY_COPY_KEY_BY_TIER[difficultyTier]]
            || `${difficultyTier.charAt(0).toUpperCase()}${difficultyTier.slice(1)}`;
        const itemDailyRuns = Math.max(0, Number(dailyProgressByItem?.[itemId]?.runs) || 0);
        const itemDailyTarget = Math.max(1, Number(dailyTarget) || 20);
        const itemDailyCount = Math.min(itemDailyRuns, itemDailyTarget);
        const itemDailyPercent = Math.round((itemDailyCount / itemDailyTarget) * 100);
        const itemDailyComplete = itemDailyPercent >= 100;
        const startingLevelLabel = copy.startingLevel || copy.level;

        return (
            <div
                aria-label={`${title} ${copy.gameplayExample || 'Gameplay example'}`}
                className={`UFOV-game__menu-preview is-${orientation}`}
                id={id}
                role="region"
            >
                <div className="UFOV-game__menu-preview-stats">
                    <div
                        aria-label={`${startingLevelLabel} ${level}, ${difficultyLabel}`}
                        className="UFOV-game__menu-preview-stat is-level"
                    >
                        <span className="UFOV-game__menu-preview-level-copy">
                            <span className="UFOV-game__menu-preview-level-label">{startingLevelLabel}</span>
                            <small className={`UFOV-game__menu-preview-difficulty is-${difficultyTier}`}>
                                {difficultyLabel}
                            </small>
                        </span>
                        <span className="UFOV-game__menu-preview-level-value">
                            <strong>{level}</strong>
                        </span>
                    </div>
                    {canEditLevel ? (
                        <button
                            className="UFOV-game__menu-preview-change-level"
                            disabled={!onChangeLevel || isTourActive}
                            type="button"
                            onClick={() => openLevelEditor({
                                id: itemId,
                                level,
                                maximum: levelMaximum,
                                minimum: levelMinimum,
                                title,
                            })}
                        >
                            {copy.changeStartingLevel || copy.changeLevel}
                        </button>
                    ) : null}
                    <div
                        className={`UFOV-game__menu-preview-stat is-daily${itemDailyComplete ? ' is-complete' : ''}${getTourHighlightClass('daily')}`}
                    >
                        <span className="UFOV-game__menu-preview-daily-head">
                            <span className="UFOV-game__mode-daily-calendar" aria-hidden="true">
                                <CalendarIcon />
                            </span>
                            <span className="UFOV-game__menu-preview-daily-label">{copy.dailyRun}</span>
                            <strong>{itemDailyCount}/{itemDailyTarget}</strong>
                        </span>
                        <ProgressTrack value={itemDailyPercent} compact tone="daily" />
                    </div>
                </div>
                <div className="UFOV-game__menu-preview-media">
                    <span className="UFOV-game__menu-preview-media-label">
                        {copy.gameplayExample || 'Gameplay example'}
                    </span>
                    <video
                        aria-label={`${title} ${copy.gameplayExample || 'gameplay example'}`}
                        autoPlay={!prefersReducedMotion}
                        className="UFOV-game__menu-preview-video"
                        disablePictureInPicture
                        loop
                        muted
                        playsInline
                        poster={posterSrc}
                        preload="metadata"
                        src={videoSrc}
                    />
                </div>
            </div>
        );
    };
    React.useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return undefined;
        }

        const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
        const updateMotionPreference = () => setPrefersReducedMotion(motionPreference.matches);

        updateMotionPreference();
        if (motionPreference.addEventListener) {
            motionPreference.addEventListener('change', updateMotionPreference);
        } else {
            motionPreference.addListener?.(updateMotionPreference);
        }

        return () => {
            if (motionPreference.removeEventListener) {
                motionPreference.removeEventListener('change', updateMotionPreference);
            } else {
                motionPreference.removeListener?.(updateMotionPreference);
            }
        };
    }, []);
    React.useEffect(() => {
        if (levelEditorOpen) {
            levelEditorSheetRef.current?.focus();
        }
    }, [levelEditorOpen]);
    React.useEffect(() => {
        if (isTourActive) {
            setExpandedMenuItemId(tourTargetId === 'daily' ? 'sopt-classic' : '');
        } else if (wasTourActiveRef.current) {
            setExpandedMenuItemId('');
        }

        wasTourActiveRef.current = isTourActive;
    }, [isTourActive, tourTargetId]);

    return (
        <>
            <ScreenShell
                ariaLabel={copy.attentionRunMenu}
                className="UFOV-game__screen--menu"
                main={(
                    <div className="UFOV-game__menu-main">
                        <div className="UFOV-game__menu-top">
                            <div className="UFOV-game__menu-brand">
                                <img alt="" className="UFOV-game__menu-logo" decoding="async" fetchpriority="high" src={logoSrc} />
                            </div>
                            {onFeedback ? (
                                <button
                                    className="UFOV-game__contact-pill"
                                    type="button"
                                    aria-label={`${copy.contact} ${copy.contactHint}`}
                                    onClick={isTourActive ? undefined : onFeedback}
                                >
                                    <MessageCircle aria-hidden="true" className="UFOV-game__contact-pill-icon" strokeWidth={2.2} />
                                    <span className="UFOV-game__contact-pill-copy">
                                        <strong>{copy.contact}</strong>
                                        <span>{copy.contactHint}</span>
                                    </span>
                                </button>
                            ) : null}
                        </div>

                        <div className="UFOV-game__menu-meter">
                            <LevelRing
                                accessibleLabel={ageEstimateIsCanonical ? menuAgeLabel : levelBasedAgeLabel}
                                level={safeEstimatedAge}
                                label={menuAgeLabel}
                            />
                            {!ageEstimateIsCanonical ? (
                                <span className="UFOV-game__menu-age-source">{levelBasedAgeLabel}</span>
                            ) : null}
                        </div>

                        <div className="UFOV-game__mode-grid">
                            <div
                                className={`UFOV-game__menu-entry${expandedMenuItemId === 'sopt-classic' ? ' is-expanded' : ''}`}
                            >
                                <MenuModeCard
                                    active={!selectedBrainGameId && activeMode === 'classic'}
                                    className="is-classic-mode"
                                    controlsId="UFOV-menu-preview-sopt-classic"
                                    disclosure={<ChevronDown aria-hidden="true" strokeWidth={2.4} />}
                                    expanded={expandedMenuItemId === 'sopt-classic'}
                                    highlighted={tourTargetId === 'classic'}
                                    title={copy.classic}
                                    subtitle={copy.classicSubtitle}
                                    badge={<><EvidenceBadgeIcon /> {copy.evidenceBadge}</>}
                                    icon={renderSOPTIcon(<TargetIcon />)}
                                    onClick={() => toggleMenuItem('sopt-classic', () => onSelectMode('classic'))}
                                />
                                {expandedMenuItemId === 'sopt-classic' ? renderMenuPreview({
                                    id: 'UFOV-menu-preview-sopt-classic',
                                    itemId: 'sopt-classic',
                                    title: copy.classic,
                                    level: modeLevels['sopt-classic'] ?? 0,
                                    canEditLevel: true,
                                    ...SOPT_MENU_PREVIEWS.classic,
                                }) : null}
                            </div>
                            <div
                                className={`UFOV-game__menu-entry${expandedMenuItemId === 'sopt-advanced' ? ' is-expanded' : ''}`}
                            >
                                <MenuModeCard
                                    active={!selectedBrainGameId && activeMode === 'advanced'}
                                    className="is-advanced-mode"
                                    controlsId="UFOV-menu-preview-sopt-advanced"
                                    disclosure={<ChevronDown aria-hidden="true" strokeWidth={2.4} />}
                                    expanded={expandedMenuItemId === 'sopt-advanced'}
                                    highlighted={tourTargetId === 'advanced'}
                                    title={copy.advanced}
                                    subtitle={copy.advancedSubtitle}
                                    icon={renderSOPTIcon(<AdvancedModeIcon />, true)}
                                    onClick={() => toggleMenuItem('sopt-advanced', () => onSelectMode('advanced'))}
                                />
                                {expandedMenuItemId === 'sopt-advanced' ? renderMenuPreview({
                                    id: 'UFOV-menu-preview-sopt-advanced',
                                    itemId: 'sopt-advanced',
                                    title: copy.advanced,
                                    level: modeLevels['sopt-advanced'] ?? 0,
                                    canEditLevel: true,
                                    ...SOPT_MENU_PREVIEWS.advanced,
                                }) : null}
                            </div>
                            {otherBrainGameMenuItems.length > 0 ? (
                                <div className="UFOV-game__brain-games-label">
                                    <span>{copy.otherGames}</span>
                                </div>
                            ) : null}
                            {otherBrainGameMenuItems.map((game, index) => {
                                const isSelected = selectedBrainGameId === game.id;
                                const isExpanded = expandedMenuItemId === game.id;
                                const previewId = `UFOV-menu-preview-${game.id}`;

                                return (
                                    <div
                                        className={`UFOV-game__menu-entry${isExpanded ? ' is-expanded' : ''}`}
                                        key={game.id}
                                    >
                                        <MenuModeCard
                                            active={isSelected}
                                            className={`is-brain-game ${getBrainGameCardToneClass(game.id, index)}`}
                                            controlsId={previewId}
                                            disclosure={<ChevronDown aria-hidden="true" strokeWidth={2.4} />}
                                            expanded={isExpanded}
                                            title={game.title}
                                            subtitle={game.focus}
                                            icon={renderBrainGameIcon(game)}
                                            onClick={() => toggleMenuItem(game.id, () => onSelectBrainGame?.(game.id))}
                                        />
                                        {isExpanded ? renderMenuPreview({
                                            id: previewId,
                                            itemId: game.id,
                                            title: game.title,
                                            videoSrc: game.previewVideoSrc,
                                            posterSrc: game.previewPosterSrc,
                                            orientation: game.previewOrientation,
                                            level: brainGameLevels[game.id] ?? game.defaultLevel ?? 1,
                                            levelMaximum: game.maxLevel ?? 20,
                                            levelMinimum: game.minLevel ?? 1,
                                        }) : null}
                                    </div>
                                );
                            })}
                        </div>

                    </div>
                )}
                mainRef={menuScrollRef}
                footerClassName={`UFOV-game__menu-footer${tourTargetId === 'progress' ? ' is-tour-highlight-container' : ''}`}
                footer={(
                    <>
                        <button className="UFOV-game__primary-button is-menu" type="button" disabled={startDisabled} onClick={onStart}>{startLabel}</button>

                        <div className="UFOV-game__footer-links">
                            <button
                                className={`UFOV-game__footer-link${getTourHighlightClass('progress')}`}
                                type="button"
                                onClick={isTourActive ? undefined : onProgress}
                            >
                                <BarsIcon />
                                <span>{progressCopy.title}</span>
                            </button>
                            <div className="UFOV-game__footer-divider" aria-hidden="true" />
                            <button className="UFOV-game__footer-link" type="button" onClick={onConfig}>
                                <GearIcon />
                                <span>{copy.settings}</span>
                            </button>
                            <div className="UFOV-game__footer-divider" aria-hidden="true" />
                            <button className="UFOV-game__footer-link" type="button" onClick={onHelp}>
                                <HelpIcon />
                                <span>{copy.help}</span>
                            </button>
                        </div>

                        <UFOVMenuTourOverlay
                            language={language}
                            onDismiss={onTourDismiss}
                            onNext={onTourNext}
                            step={tourStep}
                        />
                    </>
                )}
            />

            {levelEditorOpen ? (
                <div className="UFOV-game__level-editor-backdrop" role="presentation" onClick={closeLevelEditor}>
                    <div
                        aria-label={(copy.setStartingLevel || 'Set {game} level').replace('{game}', levelEditorTarget?.title || '').trim()}
                        aria-modal="true"
                        className="UFOV-game__level-editor-sheet"
                        ref={levelEditorSheetRef}
                        role="dialog"
                        tabIndex={-1}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={handleLevelEditorKeyDown}
                    >
                        <div className="UFOV-game__level-editor-handle" aria-hidden="true" />
                        <div className="UFOV-game__level-editor-head">
                            <strong id="UFOV-level-editor-title">{levelEditorTarget?.title || copy.startingLevel || copy.level}</strong>
                            <button className="UFOV-game__level-editor-cancel" type="button" onClick={closeLevelEditor}>{copy.cancel || 'Cancel'}</button>
                        </div>
                        <div
                            className="UFOV-game__level-editor-control"
                            aria-label={(copy.setStartingLevel || 'Set {game} level').replace('{game}', levelEditorTarget?.title || '').trim()}
                        >
                            <button
                                aria-label={copy.decreaseLevel || 'Decrease level'}
                                disabled={levelDraft <= (levelEditorTarget?.minimum ?? 0)}
                                type="button"
                                onClick={() => adjustLevelDraft(-1)}
                            >-</button>
                            <strong
                                aria-label={`${copy.startingLevel || copy.level} ${levelDraft}`}
                                aria-valuemax={levelEditorTarget?.maximum ?? 100}
                                aria-valuemin={levelEditorTarget?.minimum ?? 0}
                                aria-valuenow={levelDraft}
                                role="spinbutton"
                                tabIndex={0}
                                onKeyDown={(event) => {
                                    if (event.key === 'ArrowUp' || event.key === 'ArrowRight') {
                                        event.preventDefault();
                                        adjustLevelDraft(1);
                                    } else if (event.key === 'ArrowDown' || event.key === 'ArrowLeft') {
                                        event.preventDefault();
                                        adjustLevelDraft(-1);
                                    }
                                }}
                            >{levelDraft}</strong>
                            <button
                                aria-label={copy.increaseLevel || 'Increase level'}
                                disabled={levelDraft >= (levelEditorTarget?.maximum ?? 100)}
                                type="button"
                                onClick={() => adjustLevelDraft(1)}
                            >+</button>
                        </div>
                        <button className="UFOV-game__level-editor-set" type="button" onClick={applyLevelEditor}>{copy.setLevel || 'Set'}</button>
                    </div>
                </div>
            ) : null}
        </>
    );
}

export function UFOVConfigScreen({
    themes = [],
    selectedAssetThemeIds = [],
    countdownHapticsEnabled = true,
    colorBlindModeEnabled = false,
    language = 'en',
    initialView = 'options',
    feedbackEnabled = true,
    feedbackSource = 'settings',
    billingBusyAction = '',
    managementHref = '',
    restoreStatusMessage = '',
    termsHref = '/terms',
    privacyHref = '/privacy',
    onSelectedAssetThemesChange,
    onCountdownHapticsChange,
    onColorBlindModeChange,
    onLanguageChange,
    onResetAll,
    onRestorePurchases,
    onSubmitFeedback,
    onClose,
}) {
    const copy = getUFOVGameCopy(language);
    const selectedLanguage = normalizeOnboardingLanguage(language);
    const [configView, setConfigView] = React.useState(feedbackEnabled && initialView === 'feedback' ? 'feedback' : 'options');
    const [draftThemeIds, setDraftThemeIds] = React.useState(selectedAssetThemeIds);
    const [feedbackEmail, setFeedbackEmail] = React.useState('');
    const [feedbackMessage, setFeedbackMessage] = React.useState('');
    const [feedbackStatus, setFeedbackStatus] = React.useState('idle');
    const liveThemeIds = React.useMemo(() => themes.map((theme) => theme.id), [themes]);
    const selectedThemeCount = selectedAssetThemeIds.length || themes.length;

    React.useEffect(() => {
        if (configView === 'options') {
            setDraftThemeIds(selectedAssetThemeIds);
        }
    }, [configView, selectedAssetThemeIds]);

    React.useEffect(() => {
        if (!feedbackEnabled && configView === 'feedback') {
            setConfigView('options');
        }
    }, [configView, feedbackEnabled]);

    const openThemeView = () => {
        setDraftThemeIds(selectedAssetThemeIds.length ? selectedAssetThemeIds : liveThemeIds);
        setConfigView('themes');
    };
    const draftThemeIdSet = React.useMemo(
        () => new Set(draftThemeIds),
        [draftThemeIds]
    );
    const toggleDraftTheme = (themeId) => {
        const nextThemeIds = draftThemeIdSet.has(themeId)
            ? draftThemeIds.filter((selectedThemeId) => selectedThemeId !== themeId)
            : [...draftThemeIds, themeId];

        if (!nextThemeIds.length) {
            return;
        }

        setDraftThemeIds(nextThemeIds);
    };
    const selectAllThemes = () => {
        setDraftThemeIds(liveThemeIds);
    };
    const saveThemes = () => {
        onSelectedAssetThemesChange?.(draftThemeIds.length ? draftThemeIds : liveThemeIds);
        setConfigView('options');
    };
    const isThemeView = configView === 'themes';
    const isFeedbackView = configView === 'feedback';
    const openFeedbackView = () => {
        setFeedbackEmail('');
        setFeedbackMessage('');
        setFeedbackStatus('idle');
        setConfigView('feedback');
    };
    const submitSettingsFeedback = async (event) => {
        event.preventDefault();
        const message = feedbackMessage.trim();

        if (!message || feedbackStatus === 'submitting') {
            return;
        }

        setFeedbackStatus('submitting');

        try {
            await onSubmitFeedback?.(message, feedbackEmail.trim(), feedbackSource);
            setFeedbackEmail('');
            setFeedbackMessage('');
            setFeedbackStatus('sent');
        } catch (error) {
            setFeedbackStatus('error');
        }
    };

    return (
        <ScreenShell
            ariaLabel={copy.settings}
            className="UFOV-game__screen--config"
            header={isThemeView ? (
                <div className="UFOV-game__ranking-topbar">
                    <div className="UFOV-game__ranking-title">{copy.themesTitle}</div>
                </div>
            ) : null}
            main={isThemeView ? (
                <div className="UFOV-game__config-panel is-theme-page">
                    <div className="UFOV-game__config-heading">
                        <h1>{copy.chooseThemesTitle}</h1>
                        <p>{copy.chooseThemesDescription}</p>
                    </div>

                    <div className="UFOV-game__theme-picker is-page">
                        <div className="UFOV-game__theme-picker-head">
                            <strong>{draftThemeIds.length || liveThemeIds.length} {copy.selected}</strong>
                            <button type="button" onClick={selectAllThemes}>{copy.selectAll}</button>
                        </div>
                        <div className="UFOV-game__theme-list is-page">
                            {themes.map((theme) => (
                                <button
                                    className={`UFOV-game__theme-option ${draftThemeIdSet.has(theme.id) ? 'is-selected' : ''}`.trim()}
                                    key={theme.id}
                                    type="button"
                                    onClick={() => toggleDraftTheme(theme.id)}
                                >
                                    <span aria-hidden="true">{draftThemeIdSet.has(theme.id) ? copy.onLabel : ''}</span>
                                    <strong>{theme.label}</strong>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            ) : isFeedbackView ? (
                <form className="UFOV-game__config-panel is-feedback-page" onSubmit={submitSettingsFeedback}>
                    <div className="UFOV-game__config-heading">
                        <h1>{copy.settingsFeedbackPageTitle}</h1>
                        <p>{copy.settingsFeedbackPageDescription}</p>
                    </div>

                    <label className="UFOV-game__settings-feedback-field is-email">
                        <span>{copy.settingsFeedbackEmailLabel}</span>
                        <input
                            autoComplete="email"
                            disabled={feedbackStatus === 'submitting' || feedbackStatus === 'sent'}
                            inputMode="email"
                            maxLength={254}
                            placeholder={copy.settingsFeedbackEmailPlaceholder}
                            type="email"
                            value={feedbackEmail}
                            onChange={(event) => {
                                setFeedbackEmail(event.target.value);
                                if (feedbackStatus === 'error') {
                                    setFeedbackStatus('idle');
                                }
                            }}
                        />
                    </label>

                    <label className="UFOV-game__settings-feedback-field">
                        <span>{copy.settingsFeedbackLabel}</span>
                        <textarea
                            autoFocus
                            disabled={feedbackStatus === 'submitting' || feedbackStatus === 'sent'}
                            maxLength={2000}
                            placeholder={copy.settingsFeedbackPlaceholder}
                            rows={7}
                            value={feedbackMessage}
                            onChange={(event) => {
                                setFeedbackMessage(event.target.value);
                                if (feedbackStatus === 'error') {
                                    setFeedbackStatus('idle');
                                }
                            }}
                        />
                    </label>

                    {feedbackStatus === 'sent' ? (
                        <p className="UFOV-game__settings-feedback-status is-sent" role="status">{copy.settingsFeedbackSent}</p>
                    ) : null}
                    {feedbackStatus === 'error' ? (
                        <p className="UFOV-game__settings-feedback-status is-error" role="alert">{copy.settingsFeedbackError}</p>
                    ) : null}

                    {feedbackStatus !== 'sent' ? (
                        <button
                            className="UFOV-game__primary-button UFOV-game__settings-feedback-submit"
                            disabled={!feedbackMessage.trim() || feedbackStatus === 'submitting'}
                            type="submit"
                        >
                            {feedbackStatus === 'submitting' ? copy.settingsFeedbackSending : copy.settingsFeedbackSubmit}
                        </button>
                    ) : null}
                </form>
            ) : (
                <div className="UFOV-game__config-panel">
                    <div className="UFOV-game__config-heading">
                        <h1>{copy.optionsTitle}</h1>
                        <p>{copy.optionsDescription}</p>
                    </div>

                    <div className="UFOV-game__settings-card is-language">
                        <div>
                            <strong>{copy.languageTitle}</strong>
                            <span>{copy.settingsDescription}</span>
                        </div>
                        <div className="UFOV-game__settings-language-options">
                            {ONBOARDING_LANGUAGE_OPTIONS.map((option) => (
                                <button
                                    className={`UFOV-game__settings-language-option ${selectedLanguage === option.id ? 'is-selected' : ''}`.trim()}
                                    key={option.id}
                                    type="button"
                                    onClick={() => onLanguageChange?.(option.id)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="UFOV-game__settings-card">
                        <div>
                            <strong>{copy.themesTitle}</strong>
                            <span>{selectedThemeCount} {copy.selectedThemes}</span>
                        </div>
                        <button type="button" onClick={openThemeView}>{copy.chooseThemesButton}</button>
                    </div>

                    {SHOW_VIBRATION_CONTROLS ? (
                        <div className="UFOV-game__settings-card is-toggle">
                            <div>
                                <strong>{copy.vibrationTitle}</strong>
                                <span>{copy.vibrationDescription}</span>
                            </div>
                            <label className="UFOV-game__toggle-control">
                                <input
                                    checked={countdownHapticsEnabled}
                                    className="UFOV-game__toggle-input"
                                    type="checkbox"
                                    onChange={(event) => onCountdownHapticsChange?.(event.target.checked)}
                                />
                                <span aria-hidden="true" className="UFOV-game__toggle-track">
                                    <span className="UFOV-game__toggle-thumb" />
                                </span>
                                <span className="UFOV-game__toggle-label">{countdownHapticsEnabled ? copy.onLabel : copy.offLabel}</span>
                            </label>
                        </div>
                    ) : null}

                    <div className="UFOV-game__settings-card is-toggle">
                        <div>
                            <strong>{copy.colorBlindModeTitle}</strong>
                            <span>{copy.colorBlindModeDescription}</span>
                        </div>
                        <label className="UFOV-game__toggle-control">
                            <input
                                checked={colorBlindModeEnabled}
                                className="UFOV-game__toggle-input"
                                type="checkbox"
                                onChange={(event) => onColorBlindModeChange?.(event.target.checked)}
                            />
                            <span aria-hidden="true" className="UFOV-game__toggle-track">
                                <span className="UFOV-game__toggle-thumb" />
                            </span>
                            <span className="UFOV-game__toggle-label">{colorBlindModeEnabled ? copy.onLabel : copy.offLabel}</span>
                        </label>
                    </div>

                    <div className="UFOV-game__settings-card">
                        <div>
                            <strong>{copy.purchasesTitle}</strong>
                            <span>{copy.purchasesDescription}</span>
                        </div>
                        <button
                            type="button"
                            disabled={billingBusyAction === 'restore'}
                            onClick={onRestorePurchases}
                        >
                            {billingBusyAction === 'restore' ? copy.checking : copy.restorePurchase}
                        </button>
                    </div>

                    {restoreStatusMessage ? (
                        <p className="UFOV-game__config-status">{restoreStatusMessage}</p>
                    ) : null}

                    {feedbackEnabled ? (
                        <div className="UFOV-game__settings-card">
                            <div>
                                <strong>{copy.settingsFeedbackTitle}</strong>
                                <span>{copy.settingsFeedbackDescription}</span>
                            </div>
                            <button type="button" onClick={openFeedbackView}>{copy.settingsFeedbackButton}</button>
                        </div>
                    ) : null}

                    <div className="UFOV-game__settings-card">
                        <div>
                            <strong>{copy.resetAppTitle}</strong>
                            <span>{copy.resetAppDescription}</span>
                        </div>
                        <button type="button" onClick={onResetAll}>
                            {copy.resetApp}
                        </button>
                    </div>

                    <div className="UFOV-game__config-links">
                        {managementHref ? (
                            <>
                                <a href={managementHref}>{copy.manageSubscription}</a>
                                <span aria-hidden="true">&bull;</span>
                            </>
                        ) : null}
                        <a href={termsHref}>{copy.terms}</a>
                        <span aria-hidden="true">&bull;</span>
                        <a href={privacyHref}>{copy.privacy}</a>
                    </div>
                </div>
            )}
            footer={isThemeView ? (
                <button className="UFOV-game__primary-button" type="button" onClick={saveThemes}>
                    {copy.save}
                </button>
            ) : isFeedbackView ? (
                <button className="UFOV-game__primary-button" type="button" onClick={() => setConfigView('options')}>
                    {feedbackStatus === 'sent' ? copy.continueLabel : copy.back}
                </button>
            ) : (
                <button className="UFOV-game__primary-button" type="button" onClick={onClose}>
                    {copy.back}
                </button>
            )}
        />
    );
}

const getProgressMetricLabel = (copy, metric) => {
    if (metric === BRAIN_GAME_HIGH_SCORE_METRICS.STREAK) {
        return copy.metricStreak;
    }

    if (metric === BRAIN_GAME_HIGH_SCORE_METRICS.ELAPSED_MS) {
        return copy.metricTime;
    }

    if (metric === BRAIN_GAME_HIGH_SCORE_METRICS.LEVEL) {
        return copy.metricLevel;
    }

    return copy.metricScore;
};

const formatProgressPointValue = (point) => {
    if (!point) {
        return '—';
    }

    if (point.metric === BRAIN_GAME_HIGH_SCORE_METRICS.ELAPSED_MS) {
        return formatBrainGameHighScoreValue(point);
    }

    if (Number.isFinite(Number(point.maxValue)) && Number(point.maxValue) > 0) {
        return `${point.value}/${point.maxValue}`;
    }

    return `${point.value}`;
};

const formatProgressTrend = (copy, trend) => {
    const value = `${Math.abs(Number(trend?.changePercent) || 0)}%`;
    const template = trend?.direction === 'up'
        ? copy.trendUp
        : trend?.direction === 'down'
            ? copy.trendDown
            : copy.trendFlat;

    return `${template || ''}`.replace('{value}', value);
};

function ProgressSparkline({ points = [], overall = false, trend = 'flat' }) {
    const visiblePoints = (Array.isArray(points) ? points : []).slice(-12);

    if (!visiblePoints.length) {
        return <div aria-hidden="true" className="UFOV-game__progress-sparkline is-empty" />;
    }

    if (visiblePoints.length === 1) {
        return (
            <div
                aria-hidden="true"
                className={`UFOV-game__progress-sparkline is-${trend} is-single`}
            />
        );
    }

    const values = visiblePoints.map((point) => {
        if (overall) {
            return Number(point.score) || 0;
        }

        const maxValue = Number(point.maxValue);
        const value = Number(point.value) || 0;
        const comparableValue = Number.isFinite(maxValue) && maxValue > 0 ? value / maxValue : value;

        return isLowerGameScoreBetter(point.metric) ? -comparableValue : comparableValue;
    });
    const minimum = Math.min(...values);
    const maximum = Math.max(...values);
    const range = maximum - minimum;
    const chartWidth = 132;
    const chartHeight = 44;
    const padding = 4;
    const coordinates = values.map((value, index) => {
        const x = values.length === 1
            ? chartWidth / 2
            : padding + ((chartWidth - (padding * 2)) * index) / (values.length - 1);
        const y = range === 0
            ? chartHeight / 2
            : padding + ((maximum - value) / range) * (chartHeight - (padding * 2));

        return { x, y };
    });
    const pointList = coordinates.map(({ x, y }) => `${x},${y}`).join(' ');
    const latest = coordinates[coordinates.length - 1];

    return (
        <svg
            aria-hidden="true"
            className={`UFOV-game__progress-sparkline is-${trend}`}
            preserveAspectRatio="none"
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        >
            <polyline fill="none" points={pointList} />
            <path
                className="UFOV-game__progress-sparkline-point"
                d={`M ${latest.x} ${latest.y} h 0.01`}
            />
        </svg>
    );
}

function ProgressGameIcon({ gameId }) {
    if (gameId === 'sopt-classic') {
        return <span className="UFOV-game__progress-game-icon is-classic"><TargetIcon /></span>;
    }

    if (gameId === 'sopt-advanced') {
        return <span className="UFOV-game__progress-game-icon is-advanced"><AdvancedModeIcon /></span>;
    }

    return (
        <span className={`UFOV-game__progress-game-icon ${getBrainGameCardToneClass(gameId, 0)}`}>
            <BrainGameCardIcon gameId={gameId} />
        </span>
    );
}

export function UFOVProgressScreen({
    brainGameHighScores: rawBrainGameHighScores = {},
    brainGameMenuItems = [],
    gameScoreHistory: rawGameScoreHistory = {},
    modeProgress = {},
    onContinue,
    onMenu,
    language = 'en',
}) {
    const copy = getUFOVGameCopy(language);
    const progressCopy = getProgressCopy(language);
    const highScores = createBrainGameHighScores(rawBrainGameHighScores);
    const classicProgress = modeProgress?.classic || {};
    const advancedProgress = modeProgress?.advanced || {};
    const legacySeeds = {
        ...(Number(classicProgress.level) > 0 || Number(classicProgress.trials) > 0 ? {
            'sopt-classic': {
                metric: BRAIN_GAME_HIGH_SCORE_METRICS.LEVEL,
                value: Number(classicProgress.level) || 0,
                level: Number(classicProgress.level) || 0,
                recordedAt: classicProgress.lastPlayedAt || '1970-01-01T00:00:00.000Z',
            },
        } : {}),
        ...(Number(advancedProgress.level) > 0 || Number(advancedProgress.trials) > 0 ? {
            'sopt-advanced': {
                metric: BRAIN_GAME_HIGH_SCORE_METRICS.LEVEL,
                value: Number(advancedProgress.level) || 0,
                level: Number(advancedProgress.level) || 0,
                recordedAt: advancedProgress.lastPlayedAt || '1970-01-01T00:00:00.000Z',
            },
        } : {}),
        ...Object.fromEntries(Object.entries(highScores).map(([gameId, record]) => [gameId, {
            ...record,
            recordedAt: record.achievedAt || '1970-01-01T00:00:00.000Z',
        }])),
    };
    const histories = seedGameScoreHistory(
        createGameScoreHistory(rawGameScoreHistory),
        legacySeeds
    );
    const overallSeries = buildOverallProgressSeries(histories);
    const latestOverall = overallSeries[overallSeries.length - 1] || null;
    const overallChange = latestOverall ? latestOverall.score - 100 : 0;
    const overallTrend = {
        changePercent: overallChange,
        direction: overallChange > 0 ? 'up' : overallChange < 0 ? 'down' : 'flat',
    };
    const totalResults = Object.values(histories).reduce((total, series) => total + series.length, 0);
    const trackedGames = Object.values(histories).filter((series) => series.length > 0).length;
    const soptItems = [
        { id: 'sopt-classic', title: copy.classic, focus: copy.classicSubtitle },
        { id: 'sopt-advanced', title: copy.advanced, focus: copy.advancedSubtitle },
    ];
    const brainItems = (Array.isArray(brainGameMenuItems) ? brainGameMenuItems : [])
        .map((game, index) => ({ ...game, originalIndex: index }));
    const playedBrainItems = brainItems.filter((game) => (histories[game.id] || []).length > 0);
    const unplayedBrainItems = brainItems.filter((game) => (histories[game.id] || []).length === 0);
    const gameItems = [...soptItems, ...playedBrainItems, ...unplayedBrainItems];

    return (
        <ScreenShell
            ariaLabel={progressCopy.title}
            className="UFOV-game__screen--progress"
            header={(
                <div className="UFOV-game__progress-topbar">
                    <button
                        aria-label={progressCopy.backToGames}
                        className="UFOV-game__progress-back"
                        type="button"
                        onClick={onMenu}
                    >
                        <ArrowLeft aria-hidden="true" />
                    </button>
                    <div>
                        <h1>{progressCopy.title}</h1>
                        <p>{progressCopy.subtitle}</p>
                    </div>
                </div>
            )}
            main={(
                <div className="UFOV-game__progress-stage">
                    <section className="UFOV-game__progress-overall" aria-labelledby="UFOV-progress-overall-title">
                        <div className="UFOV-game__progress-overall-head">
                            <div>
                                <span>{progressCopy.overallTitle}</span>
                                <h2 id="UFOV-progress-overall-title">
                                    {latestOverall ? latestOverall.score : '—'}
                                </h2>
                                <small>{progressCopy.progressIndex}</small>
                            </div>
                            <span className={`UFOV-game__progress-trend is-${overallTrend.direction}`}>
                                {formatProgressTrend(progressCopy, overallTrend)}
                            </span>
                        </div>
                        <ProgressSparkline
                            overall
                            points={overallSeries}
                            trend={overallTrend.direction}
                        />
                        <p>{progressCopy.baselineHint}</p>
                        <div className="UFOV-game__progress-overall-stats">
                            <span><strong>{trackedGames}</strong>{progressCopy.trackedGames}</span>
                            <span><strong>{totalResults}</strong>{progressCopy.totalResults}</span>
                        </div>
                    </section>

                    <section className="UFOV-game__progress-by-game" aria-labelledby="UFOV-progress-by-game-title">
                        <h2 id="UFOV-progress-by-game-title">{progressCopy.byGameTitle}</h2>
                        <div className="UFOV-game__progress-game-list">
                            {gameItems.map((game) => {
                                const series = histories[game.id] || [];
                                const latest = series[series.length - 1] || null;
                                const best = getBestGameScorePoint(series);
                                const trend = getGameScoreTrend(series);
                                const metricLabel = latest
                                    ? getProgressMetricLabel(progressCopy, latest.metric)
                                    : progressCopy.metricScore;

                                return (
                                    <article
                                        className={`UFOV-game__progress-game${series.length ? '' : ' is-empty'}`}
                                        key={game.id}
                                    >
                                        <div className="UFOV-game__progress-game-head">
                                            <ProgressGameIcon gameId={game.id} />
                                            <div className="UFOV-game__progress-game-name">
                                                <strong>{game.title}</strong>
                                                <span>{series.length ? metricLabel : (game.focus || metricLabel)}</span>
                                            </div>
                                            {series.length ? (
                                                <span className={`UFOV-game__progress-trend is-${trend.direction}`}>
                                                    {formatProgressTrend(progressCopy, trend)}
                                                </span>
                                            ) : null}
                                        </div>

                                        {series.length ? (
                                            <>
                                                <ProgressSparkline
                                                    points={series}
                                                    trend={trend.direction}
                                                />
                                                <div className="UFOV-game__progress-game-stats">
                                                    <span>
                                                        <small>{progressCopy.current}</small>
                                                        <strong>{formatProgressPointValue(latest)}</strong>
                                                    </span>
                                                    <span>
                                                        <small>{progressCopy.best}</small>
                                                        <strong>{formatProgressPointValue(best)}</strong>
                                                    </span>
                                                    <span>
                                                        <small>{progressCopy.results}</small>
                                                        <strong>{series.length}</strong>
                                                    </span>
                                                </div>
                                                {Number.isFinite(Number(latest.level)) ? (
                                                    <p className="UFOV-game__progress-level-note">
                                                        {progressCopy.metricLevel} {latest.level}
                                                    </p>
                                                ) : null}
                                            </>
                                        ) : (
                                            <p className="UFOV-game__progress-empty-copy">
                                                <strong>{progressCopy.noScoreYet}</strong>
                                                <span>{progressCopy.playToTrack}</span>
                                            </p>
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                    </section>
                </div>
            )}
            footer={(
                <button className="UFOV-game__primary-button" type="button" onClick={onContinue || onMenu}>
                    {progressCopy.backToGames}
                </button>
            )}
        />
    );
}

function CountdownTimer({ mode = 'animated', countdownValue = null, countdownProgress = null, language = 'en' }) {
    const copy = getUFOVGameCopy(language);

    if (mode === 'static' && Number.isFinite(Number(countdownValue))) {
        const count = Number(countdownValue);
        const progress = Math.max(0, Math.min(1, Number.isFinite(Number(countdownProgress)) ? Number(countdownProgress) : 0));
        const ringOffset = 326.73 * progress;

        return (
            <div className="UFOV-game__countdown is-static" aria-label={`${count} ${copy.secondCountdown || 'second countdown'}`} role="timer">
                <div aria-hidden="true" className="UFOV-game__countdown-step is-static">
                    <svg viewBox="0 0 120 120">
                        <circle className="UFOV-game__countdown-track" cx="60" cy="60" r="52" />
                        <circle
                            className="UFOV-game__countdown-ring is-static"
                            cx="60"
                            cy="60"
                            r="52"
                            style={{ strokeDashoffset: ringOffset }}
                        />
                    </svg>
                    <strong>{count}</strong>
                </div>
            </div>
        );
    }

    return (
        <div className="UFOV-game__countdown" aria-label={`3 ${copy.secondCountdown || 'second countdown'}`} role="timer">
            {[3, 2, 1].map((count, index) => (
                <div
                    aria-hidden="true"
                    className="UFOV-game__countdown-step"
                    key={count}
                    style={{ '--UFOV-countdown-delay': `${index * 1000}ms` }}
                >
                    <svg viewBox="0 0 120 120">
                        <circle className="UFOV-game__countdown-track" cx="60" cy="60" r="52" />
                        <circle className="UFOV-game__countdown-ring" cx="60" cy="60" r="52" />
                    </svg>
                    <strong>{count}</strong>
                </div>
            ))}
        </div>
    );
}

export function UFOVPlayScreen({
    phase,
    trial,
    countdownMode = 'animated',
    countdownValue = null,
    countdownProgress = null,
    language = 'en',
    onPrewarmReady = null,
}) {
    const copy = getUFOVGameCopy(language);
    const isPrerender = phase === 'prerender';
    const boardLayerRef = React.useRef(null);
    const prewarmRootRef = React.useRef(null);
    const title = phase === 'fixation' || isPrerender
        ? copy.getReady
        : phase === 'stimulus'
            ? copy.catchFlash
            : copy.lockItIn;
    const isCountdown = phase === 'fixation';
    const shouldRenderBoard = Boolean(trial);
    const shouldShowBoardSymbols = phase === 'stimulus' || isCountdown || isPrerender;

    React.useEffect(() => {
        if (!isPrerender || typeof onPrewarmReady !== 'function') {
            return undefined;
        }

        let cancelled = false;
        const completePrewarm = () => {
            if (cancelled) {
                return;
            }

            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    if (!cancelled) {
                        onPrewarmReady();
                    }
                });
            });
        };
        const waitForImageDecode = (image) => {
            const waitForLoad = image.complete && image.naturalWidth > 0
                ? Promise.resolve()
                : new Promise((resolve) => {
                    image.addEventListener('load', resolve, { once: true });
                    image.addEventListener('error', resolve, { once: true });
                });

            return waitForLoad.then(() => (
                typeof image.decode === 'function'
                    ? image.decode().catch(() => undefined)
                    : undefined
            ));
        };
        const waitForImages = () => {
            const images = Array.from(prewarmRootRef.current?.querySelectorAll('img') || []);

            if (!images.length) {
                completePrewarm();
                return;
            }

            Promise.all(images.map(waitForImageDecode)).then(completePrewarm);
        };

        waitForImages();

        return () => {
            cancelled = true;
        };
    }, [isPrerender, onPrewarmReady, trial?.id]);

    return (
        <ScreenShell
            ariaLabel={copy.liveTrial}
            className="UFOV-game__screen--play"
            header={<ScreenHeading className="UFOV-game__heading--play-clean" compactBrand title={title} />}
            mainClassName="UFOV-game__screen-main--play"
            main={(
                <div className="UFOV-game__play-board-wrap" ref={prewarmRootRef}>
                    {shouldRenderBoard ? (
                        <div
                            aria-hidden={isCountdown || isPrerender ? 'true' : undefined}
                            className={`UFOV-game__live-board-layer ${isCountdown || isPrerender ? 'is-prewarming' : ''}`.trim()}
                            ref={boardLayerRef}
                        >
                            <UFOVBoardSurface
                                trial={trial}
                                showNoise={shouldShowBoardSymbols}
                                showCenter={shouldShowBoardSymbols}
                                showPeripheral={shouldShowBoardSymbols}
                                masked={phase === 'mask'}
                            />
                        </div>
                    ) : null}
                    {isCountdown ? (
                        <CountdownTimer
                            countdownProgress={countdownProgress}
                            countdownValue={countdownValue}
                            language={language}
                            mode={countdownMode}
                        />
                    ) : null}
                    {isPrerender ? (
                        <div aria-label={copy.loading} className="UFOV-game__board-prerender-status" role="status" />
                    ) : null}
                </div>
            )}
        />
    );
}

export function UFOVCenterQuestionScreen({
    trial,
    step,
    language = 'en',
    selectedCenter,
    onSelectCenter,
    onContinue,
    onMissedIt = null,
    showFooter = true,
}) {
    const copy = getUFOVGameCopy(language);

    return (
        <ScreenShell
            ariaLabel={copy.centerQuestion}
            className="UFOV-game__screen--question"
            header={(
                <div className="UFOV-game__heading">
                    <StepPill>{step}</StepPill>
                </div>
            )}
            main={(
                <div className="UFOV-game__question-stage">
                    <div className="UFOV-game__prompt-block">
                        <h2 className="UFOV-game__question-title">{copy.whichCenter}</h2>
                        <UFOVChoiceGrid options={trial.centerOptions} selected={selectedCenter} onSelect={onSelectCenter} />
                        {onMissedIt ? (
                            <button className="UFOV-game__missed-it-button" type="button" onClick={onMissedIt}>
                                {copy.missedIt}
                            </button>
                        ) : null}
                    </div>
                </div>
            )}
            footer={showFooter ? <button className="UFOV-game__primary-button" type="button" onClick={onContinue} disabled={!selectedCenter}>{copy.next}</button> : null}
        />
    );
}

export function UFOVPositionQuestionScreen({
    trial,
    positions,
    step,
    language = 'en',
    selectedPosition,
    onSelectPosition,
    onContinue,
    onMissedIt = null,
    buttonLabel,
    showFooter = true,
}) {
    const copy = getUFOVGameCopy(language);

    return (
        <ScreenShell
            ariaLabel={copy.positionQuestion}
            className="UFOV-game__screen--question"
            header={(
                <div className="UFOV-game__heading">
                    <StepPill>{step}</StepPill>
                </div>
            )}
            main={(
                <div className="UFOV-game__question-stage">
                    <div className="UFOV-game__prompt-block">
                        <h2 className="UFOV-game__question-title">
                            {copy.whichDirectionPrefix} <UFOVSymbol className="UFOV-game__inline-symbol" symbol={trial?.peripheralSymbol} usage="inline" />{copy.whichDirectionSuffix}
                        </h2>
                        <UFOVDirectionPad positions={positions} selected={selectedPosition} onSelect={onSelectPosition} />
                        {onMissedIt ? (
                            <button className="UFOV-game__missed-it-button" type="button" onClick={onMissedIt}>
                                {copy.missedIt}
                            </button>
                        ) : null}
                    </div>
                </div>
            )}
            footer={showFooter ? <button className="UFOV-game__primary-button" type="button" onClick={onContinue} disabled={!selectedPosition}>{buttonLabel || copy.scoreRun}</button> : null}
        />
    );
}

export function UFOVPeripheralQuestionScreen({
    trial,
    step,
    language = 'en',
    selectedPeripheral,
    onSelectPeripheral,
    onContinue,
    onMissedIt = null,
    buttonLabel,
    showFooter = true,
}) {
    const copy = getUFOVGameCopy(language);

    return (
        <ScreenShell
            ariaLabel={copy.peripheralQuestion}
            className="UFOV-game__screen--question"
            header={(
                <div className="UFOV-game__heading">
                    <StepPill>{step}</StepPill>
                </div>
            )}
            main={(
                <div className="UFOV-game__question-stage">
                    <div className="UFOV-game__prompt-block">
                        <h2 className="UFOV-game__question-title">{copy.whichOddIcon}</h2>
                        <UFOVChoiceGrid options={trial.peripheralOptions} selected={selectedPeripheral} onSelect={onSelectPeripheral} />
                        {onMissedIt ? (
                            <button className="UFOV-game__missed-it-button" type="button" onClick={onMissedIt}>
                                {copy.missedIt}
                            </button>
                        ) : null}
                    </div>
                </div>
            )}
            footer={showFooter ? <button className="UFOV-game__primary-button" type="button" onClick={onContinue} disabled={!selectedPeripheral}>{buttonLabel || copy.scoreRun}</button> : null}
        />
    );
}

export function UFOVMissedItDialog({ language = 'en', onCancel, onRetry, open = false }) {
    const copy = getUFOVGameCopy(language);
    const dialogRef = useDialogFocusTrap(open, onCancel);

    if (!open) {
        return null;
    }

    return (
        <div className="UFOV-game__missed-it-overlay" role="presentation">
            <div
                aria-describedby="UFOV-missed-it-description"
                aria-labelledby="UFOV-missed-it-title"
                aria-modal="true"
                className="UFOV-game__missed-it-dialog"
                ref={dialogRef}
                role="dialog"
                tabIndex="-1"
            >
                <div className="UFOV-game__missed-it-copy">
                    <h2 id="UFOV-missed-it-title">{copy.missedItTitle}</h2>
                    <p id="UFOV-missed-it-description">{copy.missedItDescription}</p>
                </div>
                <div className="UFOV-game__missed-it-actions">
                    <button type="button" onClick={onCancel}>{copy.cancel}</button>
                    <button autoFocus className="is-primary" type="button" onClick={onRetry}>{copy.retry}</button>
                </div>
            </div>
        </div>
    );
}

function PaywallTrustLine() {
    return (
        <div className="UFOV-game__paywall-trust-line">
            <svg aria-hidden="true" className="UFOV-game__paywall-trust-icon" viewBox="0 0 512 512">
                <path d="M269.4 2.9C265.2 1 260.7 0 256 0s-9.2 1-13.4 2.9L54.3 82.8c-22 9.3-38.4 31-38.3 57.2c.5 99.2 41.3 280.7 213.6 363.2c16.7 8 36.1 8 52.8 0C454.7 420.7 495.5 239.2 496 140c.1-26.2-16.3-47.9-38.3-57.2L269.4 2.9zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z" />
            </svg>
            <span>No payment now &bull; Cancel anytime</span>
        </div>
    );
}

const getVisiblePaywallStatusMessage = (message = '') => {
    const normalized = `${message || ''}`.replace(/\s+/g, ' ').trim();

    if (!normalized) {
        return '';
    }

    if (/RevenueCat billing diagnostic|offerings-empty|App Store Connect|StoreKit|api key|configuration|configured|product|offering|package|sdk/i.test(normalized)) {
        return '';
    }

    return normalized.length > 180 ? '' : normalized;
};

export function UFOVPaywallScreen({
    selectedPlan,
    plans,
    isPremium,
    busyAction,
    statusMessage,
    language = 'en',
    variant = 'menu',
    progressLabel = '',
    onSelectPlan,
    onPurchase,
    onRestore,
    onClose,
    onContinue = null,
    continueLabel = '',
    showClose = true,
    title: configuredTitle = '',
    description: configuredDescription = '',
    termsHref = '/terms',
    privacyHref = '/privacy',
}) {
    const copy = getUFOVGameCopy(language);
    const isOnboarding = variant === 'onboarding';
    const activePlan = plans.find((plan) => plan.id === selectedPlan) || plans[0];
    const purchaseBusy = Boolean(busyAction && busyAction === selectedPlan);
    const restoreBusy = busyAction === 'restore';
    const showStoreWaitOverlay = purchaseBusy || restoreBusy;
    const ctaLabel = isPremium
        ? copy.trainingActive
        : purchaseBusy
            ? (statusMessage === copy.waitingStoreConfirmation || statusMessage === copy.waitingStoreConfirmationLong
                ? copy.waitingStoreButton
                : copy.openingCheckout)
            : activePlan?.ctaLabel || copy.startTraining;
    const title = configuredTitle || (isOnboarding ? copy.startTrainingToday : copy.startTraining);
    const description = configuredDescription || (isOnboarding
        ? copy.paywallOnboardingDescription
        : copy.paywallMenuDescription);
    const visibleStatusMessage = getVisiblePaywallStatusMessage(statusMessage);
    const storeWaitMessage = restoreBusy
        ? (visibleStatusMessage || copy.checkingStoreAccount)
        : (statusMessage === copy.waitingStoreConfirmation || statusMessage === copy.waitingStoreConfirmationLong
            ? visibleStatusMessage
            : copy.waitingStoreConfirmation);

    return (
        <ScreenShell
            ariaLabel={copy.startTraining}
            className={`UFOV-game__screen--paywall ${isOnboarding ? 'is-onboarding' : ''}`.trim()}
            header={(
                <>
                    {progressLabel ? <OnboardingProgress language={language} step={progressLabel} /> : null}
                    <header className="UFOV-game__paywall-top">
                        {showClose ? (
                            <button className="UFOV-game__paywall-close" type="button" onClick={onClose} aria-label={copy.closePaywall}>
                                <CloseIcon />
                            </button>
                        ) : (
                            <div className="UFOV-game__paywall-spacer" aria-hidden="true" />
                        )}
                        <div className="UFOV-game__paywall-spacer" aria-hidden="true" />
                        <div className="UFOV-game__paywall-spacer" aria-hidden="true" />
                    </header>
                </>
            )}
            main={(
                <div className="UFOV-game__paywall-main">
                    <div className="UFOV-game__paywall-heading">
                        <h1>{title}</h1>
                        <p>{description}</p>
                    </div>

                    <div className="UFOV-game__paywall-features">
                        <PaywallFeature icon={<InfinityIcon />} title={copy.unlimitedDailyRuns} description={copy.unlimitedDailyRunsDescription} />
                        <PaywallFeature icon={<SlidersIcon />} title={copy.advancedMode} description={copy.advancedModeDescription} />
                        <PaywallFeature icon={<CloudCheckIcon />} title={copy.progressSaved} description={copy.progressSavedDescription} />
                    </div>

                    <div className="UFOV-game__paywall-plan-list">
                        {plans.map((plan) => (
                            <PaywallPlanCard
                                key={plan.id}
                                plan={plan}
                                selected={selectedPlan === plan.id}
                                onSelect={onSelectPlan}
                            />
                        ))}
                    </div>
                </div>
            )}
            footer={(
                <>
                    <button
                        className="UFOV-game__primary-button"
                        type="button"
                        disabled={isPremium || busyAction === selectedPlan || !activePlan?.ready}
                        onClick={() => onPurchase(selectedPlan)}
                    >
                        {ctaLabel}
                    </button>

                    {onContinue ? (
                        <button className="UFOV-game__secondary-button" type="button" onClick={onContinue}>
                            {continueLabel || copy.continueLabel}
                        </button>
                    ) : null}

                    <PaywallTrustLine />

                    <div className="UFOV-game__paywall-links">
                        <button type="button" onClick={onRestore} disabled={busyAction === 'restore'}>
                            {busyAction === 'restore' ? copy.checking : copy.restorePurchases}
                        </button>
                        <span aria-hidden="true">&bull;</span>
                        <a href={termsHref}>{copy.terms}</a>
                        <span aria-hidden="true">&bull;</span>
                        <a href={privacyHref}>{copy.privacy}</a>
                    </div>

                    {visibleStatusMessage ? (
                        <p className={`UFOV-game__paywall-status ${isPremium ? 'is-active' : ''}`}>{visibleStatusMessage}</p>
                    ) : null}

                    {showStoreWaitOverlay ? (
                        <div className="UFOV-game__store-wait-overlay" role="status" aria-live="polite">
                            <div className="UFOV-game__store-wait-card">
                                <span className="UFOV-game__store-wait-spinner" aria-hidden="true" />
                                <strong>{restoreBusy ? copy.checkingStoreAccount : copy.waitingStoreTitle}</strong>
                                <span>{storeWaitMessage}</span>
                            </div>
                        </div>
                    ) : null}
                </>
            )}
        />
    );
}

const getPostPurchaseSteps = () => [
    {
        title: 'Congratulations!',
        description: 'Thank you - you now have full access to all training sessions and premium features.',
        asset: UFOV_ONBOARDING_STATIC_ASSETS.postPurchaseMedal,
        assetClassName: 'is-medal',
        buttonLabel: 'Continue',
    },
    {
        title: 'Future updates included',
        description: 'You will also get access to future updates, new modes, and upcoming features - at no extra cost.',
        asset: UFOV_ONBOARDING_STATIC_ASSETS.postPurchaseUpdates,
        assetClassName: 'is-updates',
        buttonLabel: 'Continue',
    },
    {
        title: 'Start with rest days',
        description: 'At first, leave 1 day between sessions. Example: Monday - Wednesday - Friday.',
        asset: UFOV_ONBOARDING_STATIC_ASSETS.postPurchaseCalendar,
        assetClassName: 'is-calendar',
        lowerCopy: 'This helps you stay fresh and focused.',
        buttonLabel: 'Got it',
    },
];

export function UFOVPostPurchaseScreen({
    language = 'en',
    stepIndex = 0,
    totalSteps = 3,
    onNext,
}) {
    const copy = getUFOVGameCopy(language);
    const steps = getPostPurchaseSteps();
    const safeStepIndex = Math.max(0, Math.min(steps.length - 1, Number(stepIndex) || 0));
    const step = steps[safeStepIndex];
    const progressTotal = Math.max(1, Number(totalSteps) || steps.length);
    const progressLabel = `${safeStepIndex + 1} / ${progressTotal}`;

    return (
        <ScreenShell
            ariaLabel={step.title}
            className="UFOV-game__screen--onboarding UFOV-game__screen--assessment-ready"
            header={(
                <div className="UFOV-game__heading">
                    <OnboardingProgress language={language} step={progressLabel} />
                    <h1 className="UFOV-game__title is-compact">{step.title}</h1>
                    {step.description ? <p className="UFOV-game__subtitle">{step.description}</p> : null}
                </div>
            )}
            main={(
                <div className={`UFOV-game__onboarding-stage ${step.asset ? 'is-assessment-ready' : ''}`.trim()}>
                    {step.asset ? (
                        <div className={`UFOV-game__assessment-art is-post-purchase ${step.assetClassName || ''}`.trim()} aria-hidden="true">
                            <img
                                alt=""
                                className="UFOV-game__assessment-image"
                                decoding="async"
                                draggable="false"
                                loading="eager"
                                src={step.asset}
                            />
                        </div>
                    ) : null}

                    {Array.isArray(step.studies) ? (
                        <div className="UFOV-game__onboarding-list is-post-purchase-studies">
                            {step.studies.map((study, index) => (
                                <div className="UFOV-game__onboarding-row" key={`${study.icon}-${study.text}`}>
                                    <div className={`UFOV-game__onboarding-index is-${study.tone}`}>{index + 1}</div>
                                    <div className={`UFOV-game__onboarding-icon is-${study.tone}`}><OnboardingIcon type={study.icon} /></div>
                                    <strong>{study.text}</strong>
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {step.lowerCopy ? (
                        <div className="UFOV-game__assessment-copy">
                            <p>{step.lowerCopy}</p>
                        </div>
                    ) : null}
                </div>
            )}
            footer={(
                <button className="UFOV-game__primary-button" type="button" onClick={onNext}>
                    {step.buttonLabel || copy.continueLabel}
                </button>
            )}
        />
    );
}

export function UFOVResultScreen({
    feedback,
    progress,
    trial,
    language = 'en',
    onNext,
    onMenu,
    nextLabel = '',
    secondaryLabel = '',
    summarySubtitle = null,
    summaryTitle = null,
}) {
    const copy = getUFOVGameCopy(language);
    const isAdvanced = feedback?.modeId === 'advanced';
    const answerLabel = trial?.peripheralPosition?.label;
    const defaultResultCopy = getResultCopy(feedback, copy);
    const resultCopy = {
        title: summaryTitle ?? defaultResultCopy.title,
        subtitle: summarySubtitle ?? defaultResultCopy.subtitle,
    };
    const resolvedNextLabel = nextLabel || copy.nextRun;
    const resolvedSecondaryLabel = secondaryLabel || copy.changeMode;

    return (
        <ScreenShell
            ariaLabel={copy.runResult}
            className="UFOV-game__screen--result"
            header={(
                <div className="UFOV-game__result-head">
                    <div aria-hidden="true" />
                    <div className="UFOV-game__result-level">{copy.currentDifficulty || copy.level} {progress.level}</div>
                </div>
            )}
            main={(
                <div className="UFOV-game__result-stage">
                    <div className="UFOV-game__result-summary">
                        <h1>{resultCopy.title}</h1>
                        {resultCopy.subtitle ? <p>{resultCopy.subtitle}</p> : null}
                    </div>

                    <div className={`UFOV-game__result-grid ${isAdvanced ? 'is-advanced' : ''}`}>
                        <ResultGlyph correct={feedback?.centerCorrect} isSymbol label={copy.center} value={trial?.centerSymbol} />
                        <ResultGlyph correct={feedback?.positionCorrect} label={copy.direction} value={answerLabel} />
                        {isAdvanced ? (
                            <ResultGlyph correct={feedback?.peripheralCorrect} isSymbol label={copy.outside} value={trial?.peripheralSymbol} />
                        ) : null}
                    </div>

                    <ResultLevelChange feedback={feedback} progress={progress} copy={copy} />
                </div>
            )}
            footer={(
                <>
                    <button className="UFOV-game__primary-button" type="button" onClick={onNext}>{resolvedNextLabel}</button>
                    {onMenu ? (
                        <button className="UFOV-game__text-link" type="button" onClick={onMenu}>{resolvedSecondaryLabel}</button>
                    ) : null}
                </>
            )}
        />
    );
}

export const UFOVGameStyles = `
    html,
    body {
        margin: 0;
        overflow: hidden;
        padding: 0;
    }

    .UFOV-game,
    .UFOV-game *,
    .UFOV-game *::before,
    .UFOV-game *::after {
        box-sizing: border-box;
        -webkit-tap-highlight-color: transparent;
    }

    .UFOV-game,
    .UFOV-game * {
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }

    .UFOV-game button,
    .UFOV-game a,
    .UFOV-game [role="button"] {
        touch-action: manipulation;
    }

    .UFOV-game button:focus:not(:focus-visible),
    .UFOV-game a:focus:not(:focus-visible),
    .UFOV-game [role="button"]:focus:not(:focus-visible) {
        outline: none;
    }

    .UFOV-game button:focus-visible,
    .UFOV-game a:focus-visible,
    .UFOV-game [role="button"]:focus-visible {
        outline: 3px solid var(--UFOV-color-focus-ring);
        outline-offset: 3px;
    }

    .UFOV-game__symbol {
        align-items: center;
        display: inline-flex;
        height: 1em;
        justify-content: center;
        line-height: 1;
        vertical-align: middle;
        width: 1em;
    }

    .UFOV-game__symbol--fallback {
        background: rgba(19, 38, 79, 0.08);
        border-radius: 999px;
        color: var(--UFOV-muted);
        font-size: 0.52em;
        font-weight: 700;
    }

    .UFOV-game__symbol-img {
        display: block;
        height: 100%;
        object-fit: contain;
        width: 100%;
    }

    .UFOV-game__symbol-sprite-frame {
        display: block;
        height: 100%;
        overflow: hidden;
        position: relative;
        width: 100%;
    }

    .UFOV-game__symbol-sprite-sheet {
        display: block;
        max-width: none;
        position: absolute;
    }

    .UFOV-game__inline-symbol {
        font-size: 1.05em;
        margin-inline: 0.1em;
        vertical-align: -0.12em;
    }

    .UFOV-game {
        --UFOV-app-height-fallback: 100vh;
        --UFOV-resolved-app-height: var(--UFOV-app-height, var(--UFOV-app-height-fallback));
        --UFOV-font-family-ui: 'Poppins', system-ui, sans-serif;
        --UFOV-font-weight-regular: 400;
        --UFOV-font-weight-medium: 500;
        --UFOV-font-weight-semibold: 600;
        --UFOV-font-weight-bold: 700;
        --UFOV-color-canvas: #f7f8fc;
        --UFOV-color-surface: rgba(255, 255, 255, 0.82);
        --UFOV-color-surface-strong: #ffffff;
        --UFOV-color-text: #13264f;
        --UFOV-color-heading: #0d1627;
        --UFOV-color-text-muted: #697487;
        --UFOV-color-text-on-soft: #59677d;
        --UFOV-color-border: rgba(19, 38, 79, 0.12);
        --UFOV-color-track: #e6e9f2;
        --UFOV-color-action: #1f6bff;
        --UFOV-color-action-hover: #1758d5;
        --UFOV-color-action-soft: #eef4ff;
        --UFOV-color-action-border: #cfddf8;
        --UFOV-color-success: #16845b;
        --UFOV-color-success-text: #0d704c;
        --UFOV-color-success-soft: #e7f7ef;
        --UFOV-color-success-border: #b9e4ce;
        --UFOV-color-focus-ring: #1f6bff;
        --UFOV-radius-sm: 8px;
        --UFOV-radius-md: 12px;
        --UFOV-radius-lg: 16px;
        --UFOV-radius-xl: 22px;
        --UFOV-radius-sheet: 28px;
        --UFOV-radius-pill: 999px;
        --UFOV-interaction-fast: 160ms;
        --UFOV-interaction-standard: 220ms;
        --UFOV-interaction-ease: cubic-bezier(0.22, 1, 0.36, 1);
        --UFOV-interaction-press-scale: 0.98;
        --UFOV-interaction-disabled-opacity: 0.48;
        --UFOV-bg: var(--UFOV-color-canvas);
        --UFOV-surface: var(--UFOV-color-surface);
        --UFOV-surface-strong: var(--UFOV-color-surface-strong);
        --UFOV-ink: var(--UFOV-color-text);
        --UFOV-title: var(--UFOV-color-heading);
        --UFOV-muted: var(--UFOV-color-text-muted);
        --UFOV-line: var(--UFOV-color-border);
        --UFOV-track: var(--UFOV-color-track);
        --UFOV-mint: #35c9c1;
        --UFOV-gold: #f2c04b;
        --UFOV-coral: #ff7a66;
        --UFOV-soft-mint: #d7f6f2;
        --UFOV-soft-sand: #fdf0c9;
        --UFOV-soft-peach: #ffdcd0;
        background:
            radial-gradient(circle at 50% 22%, rgba(255, 255, 255, 0.96), rgba(247, 248, 252, 0.92) 58%, rgba(237, 241, 249, 0.94) 100%);
        color: var(--UFOV-ink);
        font-family: var(--UFOV-font-family-ui);
        min-height: var(--UFOV-resolved-app-height);
        overscroll-behavior: none;
        -ms-overflow-style: none;
        scrollbar-width: none;
        touch-action: manipulation;
        width: 100%;
    }

    .UFOV-game.is-color-blind-mode {
        --UFOV-mint: #0072b2;
        --UFOV-gold: #e69f00;
        --UFOV-coral: #cc79a7;
        --UFOV-soft-mint: #d8eef8;
        --UFOV-soft-sand: #fff1c4;
        --UFOV-soft-peach: #f3d8ef;
    }

    @supports (height: 100svh) {
        .UFOV-game {
            --UFOV-app-height-fallback: 100svh;
        }
    }

    .UFOV-game *,
    .UFOV-game {
        -ms-overflow-style: none;
        scrollbar-width: none;
        -webkit-text-size-adjust: 100%;
    }

    .UFOV-game::-webkit-scrollbar,
    .UFOV-game *::-webkit-scrollbar {
        display: none;
        height: 0;
        width: 0;
    }

    .UFOV-game__device {
        display: flex;
        justify-content: center;
        min-height: var(--UFOV-resolved-app-height);
        position: relative;
        width: 100%;
    }

    .UFOV-game__screen {
        --UFOV-screen-horizontal-gap: 16px;
        --UFOV-screen-top-gap: 14px;
        --UFOV-screen-bottom-gap: 14px;
        display: flex;
        flex-direction: column;
        gap: 0px;
        height: var(--UFOV-resolved-app-height);
        margin: 0 auto;
        max-width: 430px;
        min-height: var(--UFOV-resolved-app-height);
        overflow: hidden;
        padding:
            calc(env(safe-area-inset-top, 0px) + var(--UFOV-screen-top-gap))
            var(--UFOV-screen-horizontal-gap)
            max(12px, calc(env(safe-area-inset-bottom, 0px) + var(--UFOV-screen-bottom-gap)));
        position: relative;
        width: 100%;
    }

    .UFOV-game__persistent-screen-layer.is-visible {
        display: contents;
    }

    .UFOV-game__persistent-screen-layer.is-prewarming {
        contain: layout paint style;
        inset: 0;
        opacity: 0;
        overflow: hidden;
        pointer-events: none;
        position: absolute;
        visibility: hidden;
        z-index: 0;
    }

    .UFOV-game__screen-top {
        display: grid;
        flex: none;
        gap: clamp(8px, 1.2vh, 12px);
    }

    .UFOV-game__screen-main {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        gap: clamp(12px, 1.8vh, 18px);
        min-height: 0;
    }

    .UFOV-game__screen-footer {
        display: grid;
        flex: none;
        gap: clamp(8px, 1.4vh, 12px);
    }

    .UFOV-game__screen--onboarding .UFOV-game__screen-footer > .UFOV-game__primary-button {
        justify-self: center;
    }

    .UFOV-game__screen--onboarding .UFOV-game__screen-main,
    .UFOV-game__screen--question .UFOV-game__screen-main,
    .UFOV-game__screen--play .UFOV-game__screen-main,
    .UFOV-game__screen--result .UFOV-game__screen-main,
    .UFOV-game__screen--ranking .UFOV-game__screen-main {
        justify-content: center;
    }

    .UFOV-game__screen--menu .UFOV-game__screen-main {
        justify-content: flex-start;
        margin-inline: calc(0px - var(--UFOV-screen-horizontal-gap));
        overflow-anchor: none;
        overflow-x: hidden;
        overflow-y: auto;
        padding-inline: 28px;
        padding-bottom: clamp(20px, 3vh, 28px);
        scroll-padding-block: 16px 32px;
    }

    .UFOV-game__screen--menu {
        padding-bottom: 0;
    }

    .UFOV-game__screen--paywall .UFOV-game__screen-main {
        justify-content: flex-start;
    }

    .UFOV-game__onboarding-progress {
        background: #e6e9f2;
        border-radius: 999px;
        height: 12px;
        margin: 0 auto clamp(4px, 0.8vh, 8px);
        overflow: hidden;
        position: relative;
        width: min(318px, 82vw);
    }

    .UFOV-game__onboarding-progress > span {
        background: #0d1627;
        border-radius: inherit;
        display: block;
        height: 100%;
        min-width: 10px;
        transition: width 380ms cubic-bezier(0.22, 1, 0.36, 1);
    }

    .UFOV-game__step-pill {
        align-items: center;
        background: #eff1f5;
        border-radius: 999px;
        color: var(--UFOV-ink);
        display: inline-flex;
        font-size: 14px;
        font-weight: 700;
        justify-content: center;
        line-height: 1;
        margin: 0 auto;
        min-height: 36px;
        min-width: 74px;
        padding: 0 16px;
    }

    .UFOV-game__step-pill.is-mint {
        background: #dcf6f1;
    }

    .UFOV-game__heading {
        display: grid;
        gap: clamp(8px, 1.1vh, 12px);
        justify-items: center;
        text-align: center;
    }

    .UFOV-game__heading--play-clean {
        padding-top: 28px;
    }

    .UFOV-game__title,
    .UFOV-game__hero-title,
    .UFOV-game__paywall-heading h1 {
        color: var(--UFOV-title);
        font-size: clamp(32px, 10vw, 52px);
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 0.9;
        margin: 0;
        text-wrap: balance;
    }

    .UFOV-game__title.is-compact {
        font-size: 29px;
        font-weight: 700;
        letter-spacing: 0;
        line-height: 0.98;
        margin-top: 14px;
    }

    .UFOV-game__title.is-compact.is-study {
        font-size: clamp(25px, 8.1vw, 40px);
        margin-top: 10px;
    }

    .UFOV-game__title.is-compact.is-research {
        margin-top: 15px;
    }

    .UFOV-game__title.is-compact.is-research span {
        display: block;
    }

    .UFOV-game__subtitle,
    .UFOV-game__paywall-heading p {
        color: var(--UFOV-muted);
        font-size: clamp(13px, 3.7vw, 18px);
        font-weight: 600;
        line-height: 1.34;
        margin: 0;
        max-width: 320px;
        text-wrap: balance;
    }

    .UFOV-game__subtitle.is-intro {
        white-space: pre-line;
    }

    .UFOV-game__onboarding-stage,
    .UFOV-game__onboarding-example,
    .UFOV-game__question-stage,
    .UFOV-game__result-stage,
    .UFOV-game__paywall-main {
        display: grid;
        gap: clamp(12px, 1.8vh, 18px);
        width: 100%;
    }

    .UFOV-game__onboarding-stage,
    .UFOV-game__question-stage,
    .UFOV-game__result-stage {
        align-content: center;
    }

    .UFOV-game__onboarding-stage {
        position: relative;
    }

    .UFOV-game__onboarding-step-content {
        display: grid;
        gap: clamp(12px, 1.8vh, 18px);
        width: 100%;
    }

    .UFOV-game__onboarding-settings-panel {
        display: grid;
        gap: 0;
        margin: 0 auto;
        width: min(100%, 386px);
    }

    .UFOV-game__screen--onboarding-settings .UFOV-game__screen-main {
        justify-content: flex-start;
    }

    .UFOV-game__screen--onboarding-settings .UFOV-game__onboarding-settings-panel {
        margin-top: clamp(34px, 6vh, 54px);
    }

    .UFOV-game__settings-section-title {
        align-items: center;
        color: #0b183a;
        display: flex;
        gap: 15px;
        padding-left: 15px;
    }

    .UFOV-game__settings-section-title strong {
        font-size: 20px;
        font-weight: 700;
        letter-spacing: 0;
        line-height: 1;
    }

    .UFOV-game__reviewer-access-trigger {
        align-items: center;
        background: transparent;
        border: 0;
        border-radius: 50%;
        color: #0b183a;
        cursor: pointer;
        display: inline-flex;
        flex: 0 0 auto;
        justify-content: center;
        margin: -6px;
        padding: 6px;
        transition: background 160ms ease, transform 160ms ease;
    }

    .UFOV-game__reviewer-access-trigger:hover {
        background: rgba(31, 140, 255, 0.09);
    }

    .UFOV-game__reviewer-access-trigger:active {
        transform: scale(0.92);
    }

    .UFOV-game__reviewer-access-trigger:focus-visible {
        outline: 3px solid rgba(31, 140, 255, 0.32);
        outline-offset: 2px;
    }

    .UFOV-game__settings-section-icon {
        color: #0b183a;
        flex: 0 0 auto;
        height: 34px;
        width: 34px;
    }

    .UFOV-game__settings-section-icon.is-vibration {
        height: 44px;
        width: 44px;
    }

    .UFOV-game__reviewer-access-backdrop {
        align-items: center;
        background: rgba(17, 27, 47, 0.34);
        display: flex;
        inset: 0;
        justify-content: center;
        padding: 18px;
        position: fixed;
        z-index: 1200;
    }

    .UFOV-game__reviewer-access-modal {
        background: #ffffff;
        border: 1px solid rgba(19, 38, 79, 0.12);
        border-radius: 8px;
        box-shadow: 0 24px 60px rgba(17, 27, 47, 0.2);
        color: #17233b;
        display: grid;
        gap: 14px;
        padding: 20px;
        width: min(100%, 350px);
    }

    .UFOV-game__reviewer-access-head {
        align-items: center;
        display: flex;
        gap: 12px;
        justify-content: space-between;
    }

    .UFOV-game__reviewer-access-head h2 {
        font-size: 19px;
        font-weight: 600;
        letter-spacing: 0;
        line-height: 1.2;
        margin: 0;
    }

    .UFOV-game__reviewer-access-close {
        align-items: center;
        background: transparent;
        border: 0;
        border-radius: 50%;
        color: #5f6c83;
        cursor: pointer;
        display: inline-flex;
        height: 36px;
        justify-content: center;
        padding: 0;
        width: 36px;
    }

    .UFOV-game__reviewer-access-close:hover {
        background: #f1f3f7;
        color: #17233b;
    }

    .UFOV-game__reviewer-access-close .UFOV-game__footer-icon {
        height: 20px;
        width: 20px;
    }

    .UFOV-game__reviewer-access-modal > input {
        background: #f7f8fa;
        border: 1px solid #d9dee7;
        border-radius: 8px;
        color: #17233b;
        font: inherit;
        font-size: 16px;
        font-weight: 600;
        height: 52px;
        letter-spacing: 0;
        outline: 0;
        padding: 0 14px;
        text-transform: uppercase;
        width: 100%;
    }

    .UFOV-game__reviewer-access-modal > input:focus {
        border-color: #1f8cff;
        box-shadow: 0 0 0 3px rgba(31, 140, 255, 0.14);
    }

    .UFOV-game__reviewer-access-status {
        color: #6b7486;
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        margin: -4px 0;
        min-height: 18px;
    }

    .UFOV-game__reviewer-access-status.is-accepted {
        color: #11865e;
    }

    .UFOV-game__reviewer-access-status.is-invalid,
    .UFOV-game__reviewer-access-status.is-error {
        color: #c53950;
    }

    .UFOV-game__reviewer-access-submit {
        background: #111d33;
        border: 0;
        border-radius: 8px;
        color: #ffffff;
        cursor: pointer;
        font: inherit;
        font-size: 16px;
        font-weight: 600;
        min-height: 50px;
        padding: 12px 16px;
        transition: background 160ms ease, transform 160ms ease;
    }

    .UFOV-game__reviewer-access-submit:hover:not(:disabled) {
        background: #1b2d4e;
    }

    .UFOV-game__reviewer-access-submit:active:not(:disabled) {
        transform: scale(0.97);
    }

    .UFOV-game__reviewer-access-submit:disabled,
    .UFOV-game__reviewer-access-close:disabled {
        cursor: default;
        opacity: 0.48;
    }

    .UFOV-game__haptic-copy {
        display: grid;
        gap: 5px;
        min-width: 0;
    }

    .UFOV-game__haptic-copy strong {
        color: var(--UFOV-title);
        font-size: 20px;
        font-weight: 700;
        letter-spacing: 0;
        line-height: 1.05;
    }

    .UFOV-game__haptic-copy span {
        color: #63718d;
        font-size: 17px;
        font-weight: 500;
        line-height: 1.25;
    }

    .UFOV-game__language-options {
        display: grid;
        gap: 8px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        margin-top: 16px;
        min-height: 0;
    }

    .UFOV-game__language-options.is-expanded {
        max-height: min(36vh, 290px);
        overflow-y: auto;
        overscroll-behavior: contain;
        padding: 3px 5px 3px 3px;
        scrollbar-color: rgba(73, 91, 127, 0.38) transparent;
        scrollbar-width: thin;
    }

    .UFOV-game__language-options.is-expanded::-webkit-scrollbar {
        width: 5px;
    }

    .UFOV-game__language-options.is-expanded::-webkit-scrollbar-thumb {
        background: rgba(73, 91, 127, 0.38);
        border-radius: 99px;
    }

    .UFOV-game__language-option {
        align-items: center;
        background: #fff;
        border: 1px solid rgba(19, 38, 79, 0.12);
        border-radius: 12px;
        box-shadow: 0 7px 18px rgba(17, 27, 47, 0.06);
        color: #0b183a;
        cursor: pointer;
        display: flex;
        font: inherit;
        font-size: 14px;
        font-weight: 600;
        gap: 10px;
        letter-spacing: 0;
        min-height: 52px;
        min-width: 0;
        padding: 9px 11px;
        position: relative;
        transition: background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease, transform 160ms ease;
    }

    .UFOV-game__language-option:hover {
        border-color: rgba(31, 140, 255, 0.45);
        transform: translateY(-1px);
    }

    .UFOV-game__language-option:active {
        transform: scale(0.97);
    }

    .UFOV-game__language-option span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .UFOV-game__language-option.is-selected {
        background: #eaf5ff;
        border-color: #1f8cff;
        box-shadow: 0 7px 18px rgba(31, 140, 255, 0.14);
        color: #0b183a;
    }

    .UFOV-game__language-flag {
        aspect-ratio: 4 / 3;
        border: 1px solid rgba(19, 38, 79, 0.1);
        border-radius: 4px;
        display: block;
        flex: 0 0 auto;
        height: 24px;
        object-fit: cover;
        width: 32px;
    }

    .UFOV-game__language-more-button {
        align-items: center;
        background: transparent;
        border: 0;
        color: #56637e;
        cursor: pointer;
        display: flex;
        font: inherit;
        font-size: 14px;
        font-weight: 600;
        gap: 9px;
        justify-content: center;
        letter-spacing: 0;
        margin-top: 11px;
        min-height: 42px;
        padding: 8px 14px;
        width: 100%;
    }

    .UFOV-game__language-more-button::after {
        border-bottom: 2px solid currentColor;
        border-right: 2px solid currentColor;
        content: "";
        height: 7px;
        margin-top: -4px;
        transform: rotate(45deg);
        transition: transform 180ms ease, margin 180ms ease;
        width: 7px;
    }

    .UFOV-game__language-more-button.is-expanded::after {
        margin-top: 4px;
        transform: rotate(225deg);
    }

    .UFOV-game__language-more-button:hover {
        color: #163d85;
    }

    .UFOV-game__settings-divider {
        background: rgba(19, 38, 79, 0.1);
        height: 1px;
        margin: 30px 14px 19px;
    }

    .UFOV-game__haptic-row {
        align-items: center;
        color: #0b183a;
        display: grid;
        gap: 18px;
        grid-template-columns: auto minmax(0, 1fr) auto;
        padding: 0 14px 0 11px;
    }

    .UFOV-game__screen--onboarding-settings .UFOV-game__toggle-control {
        gap: 0;
    }

    .UFOV-game__screen--onboarding-settings .UFOV-game__toggle-track {
        background: rgba(19, 38, 79, 0.16);
        border: 2px solid rgba(255, 255, 255, 0.88);
        box-shadow: 0 12px 22px rgba(19, 38, 79, 0.16);
        height: 38px;
        padding: 4px;
        width: 68px;
    }

    .UFOV-game__screen--onboarding-settings .UFOV-game__toggle-thumb {
        background: #fff;
        box-shadow: 0 6px 14px rgba(17, 27, 47, 0.2);
        height: 26px;
        width: 26px;
    }

    .UFOV-game__screen--onboarding-settings .UFOV-game__toggle-input:checked + .UFOV-game__toggle-track {
        background: #132a64;
    }

    .UFOV-game__screen--onboarding-settings .UFOV-game__toggle-input:checked + .UFOV-game__toggle-track .UFOV-game__toggle-thumb {
        transform: translateX(30px);
    }

    .UFOV-game__screen--onboarding-settings .UFOV-game__toggle-label {
        height: 1px;
        margin: -1px;
        overflow: hidden;
        position: absolute;
        width: 1px;
    }

    .UFOV-game__onboarding-age-card {
        background: rgba(255, 255, 255, 0.78);
        border: 1px solid rgba(19, 38, 79, 0.08);
        border-radius: 24px;
        box-shadow: 0 14px 28px rgba(17, 27, 47, 0.04);
        display: grid;
        gap: 10px;
        justify-items: center;
        margin: 0 auto;
        padding: 24px 22px;
        text-align: center;
        width: min(100%, 320px);
    }

    .UFOV-game__onboarding-share-result {
        align-content: center;
        aspect-ratio: 1;
        background: #fff;
        border: 8px solid #d9f7e6;
        border-radius: 50%;
        box-shadow: 0 18px 36px rgba(17, 27, 47, 0.1);
        display: grid;
        justify-items: center;
        margin: 0 auto;
        padding: 24px;
        text-align: center;
        width: min(58vw, 220px);
    }

    .UFOV-game__onboarding-share-result span,
    .UFOV-game__onboarding-share-result small {
        color: var(--UFOV-muted);
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0;
    }

    .UFOV-game__onboarding-share-result strong {
        color: var(--UFOV-title);
        font-size: clamp(58px, 19vw, 82px);
        font-weight: 700;
        letter-spacing: 0;
        line-height: 0.94;
    }

    .UFOV-game__onboarding-share-footer {
        justify-items: center;
    }

    .UFOV-game__onboarding-demographics-main {
        justify-content: flex-start !important;
        overflow-x: hidden;
        overflow-y: auto;
        padding: 6px 2px 14px;
        scroll-padding-block: 10px 24px;
    }

    .UFOV-game__demographics-form {
        background: rgba(255, 255, 255, 0.82);
        border: 1px solid rgba(19, 38, 79, 0.1);
        border-radius: var(--UFOV-radius-xl);
        box-shadow: 0 14px 30px rgba(17, 27, 47, 0.06);
        display: grid;
        gap: 18px;
        margin: auto;
        padding: 18px;
        width: min(100%, 390px);
    }

    .UFOV-game__demographics-age-field,
    .UFOV-game__demographics-gender-field {
        border: 0;
        display: grid;
        gap: 9px;
        margin: 0;
        min-width: 0;
        padding: 0;
    }

    .UFOV-game__demographics-age-field > span,
    .UFOV-game__demographics-gender-field > legend {
        color: var(--UFOV-title);
        font-size: 13px;
        font-weight: 700;
        line-height: 1.25;
        padding: 0;
    }

    .UFOV-game__demographics-age-field input {
        appearance: textfield;
        background: #fff;
        border: 1.5px solid rgba(19, 38, 79, 0.16);
        border-radius: var(--UFOV-radius-lg);
        color: var(--UFOV-title);
        font: inherit;
        font-size: 18px;
        font-weight: 600;
        height: 54px;
        min-width: 0;
        outline: none;
        padding: 0 15px;
        user-select: text;
        width: 100%;
    }

    .UFOV-game__demographics-age-field input:focus-visible {
        border-color: var(--UFOV-color-action);
        box-shadow: 0 0 0 3px rgba(31, 107, 255, 0.14);
    }

    .UFOV-game__demographics-gender-options {
        display: grid;
        gap: 8px;
    }

    .UFOV-game__demographics-gender-option {
        cursor: pointer;
        display: block;
        min-width: 0;
        position: relative;
    }

    .UFOV-game__demographics-gender-option input {
        height: 1px;
        opacity: 0;
        pointer-events: none;
        position: absolute;
        width: 1px;
    }

    .UFOV-game__demographics-gender-option span {
        align-items: center;
        background: #fff;
        border: 1.5px solid rgba(19, 38, 79, 0.12);
        border-radius: var(--UFOV-radius-lg);
        color: var(--UFOV-title);
        display: grid;
        font-size: 13px;
        font-weight: 600;
        gap: 10px;
        grid-template-columns: minmax(0, 1fr) auto;
        line-height: 1.3;
        min-height: 44px;
        padding: 9px 13px;
        transition:
            background-color var(--UFOV-interaction-standard) var(--UFOV-interaction-ease),
            border-color var(--UFOV-interaction-standard) var(--UFOV-interaction-ease),
            transform var(--UFOV-interaction-fast) var(--UFOV-interaction-ease);
    }

    .UFOV-game__demographics-gender-option span::after {
        align-items: center;
        background: var(--UFOV-color-action);
        border-radius: 50%;
        color: #fff;
        content: "✓";
        display: inline-flex;
        font-size: 11px;
        height: 20px;
        justify-content: center;
        opacity: 0;
        transform: scale(0.75);
        transition:
            opacity var(--UFOV-interaction-fast) var(--UFOV-interaction-ease),
            transform var(--UFOV-interaction-fast) var(--UFOV-interaction-ease);
        width: 20px;
    }

    .UFOV-game__demographics-gender-option input:checked + span {
        background: var(--UFOV-color-action-soft);
        border-color: var(--UFOV-color-action);
    }

    .UFOV-game__demographics-gender-option input:checked + span::after {
        opacity: 1;
        transform: scale(1);
    }

    .UFOV-game__demographics-gender-option input:focus-visible + span {
        box-shadow: 0 0 0 3px rgba(31, 107, 255, 0.14);
    }

    .UFOV-game__demographics-gender-option:active span {
        transform: scale(var(--UFOV-interaction-press-scale));
    }

    .UFOV-game__whatsapp-button {
        align-items: center;
        background: #128c7e;
        border: 0;
        border-radius: 22px;
        color: #fff;
        cursor: pointer;
        display: inline-flex;
        font: inherit;
        font-size: 16px;
        font-weight: 700;
        height: 58px;
        justify-content: center;
        letter-spacing: 0;
        max-width: 560px;
        padding: 0 24px;
        transition: background-color 180ms ease, transform 180ms ease;
        width: 100%;
    }

    .UFOV-game__whatsapp-button:hover {
        background: #0d796d;
    }

    .UFOV-game__whatsapp-button:active {
        transform: scale(0.98);
    }

    .UFOV-game__onboarding-copy {
        display: grid;
        margin: 0 auto;
        max-width: 290px;
        place-items: center;
        text-align: center;
        width: 100%;
    }

    .UFOV-game__onboarding-copy p {
        color: var(--UFOV-title);
        font-size: 20px;
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 1.28;
        margin: 0;
        text-wrap: balance;
    }

    .UFOV-game__onboarding-stage.is-assessment-ready {
        align-content: center;
        gap: clamp(22px, 4vh, 34px);
        justify-items: center;
    }

    .UFOV-game__assessment-art {
        display: block;
        margin: 0 auto;
        max-width: 250px;
        width: min(100%, 360px);
    }

    .UFOV-game__assessment-art.is-post-purchase {
        max-width: 270px;
    }

    .UFOV-game__assessment-art.is-post-purchase.is-updates {
        max-width: 285px;
    }

    .UFOV-game__assessment-art.is-post-purchase.is-calendar {
        max-width: 300px;
    }

    .UFOV-game__assessment-image {
        display: block;
        height: auto;
        width: 100%;
    }

    .UFOV-game__assessment-copy {
        display: grid;
        gap: 6px;
        margin: 0 auto;
        max-width: 350px;
        text-align: center;
    }

    .UFOV-game__assessment-copy p {
        color: #3d281b;
        font-size: 16px;
        font-weight: 500;
        letter-spacing: -0.01em;
        line-height: 1;
        margin: 0;
        opacity: 0.7;
    }

    .UFOV-game__onboarding-list.is-post-purchase-studies {
        gap: 8px;
        margin: 0 auto;
        max-width: 386px;
    }

    .UFOV-game__onboarding-list.is-post-purchase-studies .UFOV-game__onboarding-row {
        animation: none;
        gap: 10px;
        grid-template-columns: 34px 48px minmax(0, 1fr);
        min-height: 58px;
        opacity: 1;
        padding: 8px 10px;
        transform: none;
    }

    .UFOV-game__onboarding-list.is-post-purchase-studies .UFOV-game__onboarding-index {
        font-size: 14px;
        height: 34px;
        width: 34px;
    }

    .UFOV-game__onboarding-list.is-post-purchase-studies .UFOV-game__onboarding-icon {
        height: 48px;
        width: 48px;
    }

    .UFOV-game__onboarding-list.is-post-purchase-studies .UFOV-game__onboarding-row strong {
        font-size: 13px;
        line-height: 1.16;
    }

    .UFOV-game__onboarding-age-card span {
        color: var(--UFOV-muted);
        font-size: 14px;
        font-weight: 700;
        letter-spacing: -0.01em;
        text-transform: uppercase;
    }

    .UFOV-game__onboarding-age-card strong {
        color: var(--UFOV-title);
        font-size: clamp(36px, 11vw, 52px);
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 0.96;
    }

    .UFOV-game__feedback-list {
        display: grid;
        gap: 10px;
        margin: 0 auto;
        width: min(100%, 340px);
    }

    .UFOV-game__feedback-option {
        background:
            linear-gradient(var(--UFOV-surface-strong), var(--UFOV-surface-strong)) padding-box,
            linear-gradient(135deg, rgba(19, 38, 79, 0.12), rgba(19, 38, 79, 0.12)) border-box;
        border: 2px solid transparent;
        border-radius: 22px;
        color: var(--UFOV-ink);
        cursor: pointer;
        display: grid;
        gap: 4px;
        padding: 16px;
        text-align: left;
    }

    .UFOV-game__feedback-option.is-selected {
        background:
            linear-gradient(rgba(255, 250, 241, 0.98), rgba(255, 250, 241, 0.98)) padding-box,
            linear-gradient(135deg, var(--UFOV-mint), var(--UFOV-gold), var(--UFOV-coral)) border-box;
    }

    .UFOV-game__feedback-option strong {
        color: var(--UFOV-ink);
        font-size: 15px;
        font-weight: 700;
        letter-spacing: -0.01em;
    }

    .UFOV-game__feedback-option span {
        color: var(--UFOV-muted);
        font-size: 12px;
        font-weight: 600;
        line-height: 1.35;
    }

    .UFOV-game__native-review-card {
        background: var(--UFOV-surface-strong);
        border: 2px solid rgba(19, 38, 79, 0.1);
        border-radius: 26px;
        box-shadow: 0 18px 34px rgba(17, 27, 47, 0.08);
        display: grid;
        gap: 10px;
        margin: auto 0;
        padding: 24px 22px;
        text-align: center;
    }

    .UFOV-game__native-review-card strong {
        color: var(--UFOV-title);
        font-size: 24px;
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 1;
    }

    .UFOV-game__native-review-card span {
        color: var(--UFOV-muted);
        font-size: 15px;
        font-weight: 600;
        line-height: 1.35;
    }

    .UFOV-game__review-modal-backdrop {
        align-items: center;
        background: rgba(9, 17, 36, 0.42);
        display: flex;
        inset: 0;
        justify-content: center;
        padding: 18px;
        position: fixed;
        z-index: 10000;
    }

    .UFOV-game__review-modal {
        background: #fffaf1;
        border: 2px solid rgba(19, 38, 79, 0.12);
        border-radius: 26px;
        box-shadow: 0 28px 72px rgba(9, 17, 36, 0.28);
        color: var(--UFOV-ink);
        display: grid;
        gap: 16px;
        max-height: calc(100vh - 36px);
        overflow: auto;
        padding: 22px;
        width: min(100%, 390px);
    }

    .UFOV-game__review-modal-head {
        display: grid;
        gap: 7px;
        text-align: center;
    }

    .UFOV-game__review-modal-head h2 {
        color: var(--UFOV-title);
        font-size: 25px;
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 1.04;
        margin: 0;
    }

    .UFOV-game__review-modal-head p {
        color: var(--UFOV-muted);
        font-size: 14px;
        font-weight: 500;
        line-height: 1.3;
        margin: 0;
    }

    .UFOV-game__review-rating-field {
        border: 0;
        display: grid;
        gap: 8px;
        margin: 0;
        padding: 0;
        text-align: center;
    }

    .UFOV-game__review-rating-field legend {
        color: var(--UFOV-ink);
        font-size: 13px;
        font-weight: 600;
        padding: 0;
        width: 100%;
    }

    .UFOV-game__review-stars {
        display: flex;
        justify-content: center;
        gap: 5px;
    }

    .UFOV-game__review-stars button {
        align-items: center;
        background: transparent;
        border: 0;
        color: rgba(19, 38, 79, 0.18);
        cursor: pointer;
        display: flex;
        font-size: 36px;
        justify-content: center;
        line-height: 1;
        min-height: 46px;
        padding: 3px;
        transition: color 150ms ease, transform 150ms ease;
    }

    .UFOV-game__review-stars button.is-selected {
        color: #f4b526;
    }

    .UFOV-game__review-stars button:hover:not(:disabled),
    .UFOV-game__review-stars button:focus-visible {
        transform: translateY(-2px) scale(1.05);
    }

    .UFOV-game__review-comment-field {
        display: grid;
        gap: 8px;
    }

    .UFOV-game__review-comment-intro {
        color: var(--UFOV-muted);
        font-size: 14px;
        font-weight: 600;
        line-height: 1.3;
        margin: 0;
        text-align: center;
    }

    .UFOV-game__review-comment-field span {
        color: var(--UFOV-ink);
        font-size: 14px;
        font-weight: 700;
    }

    .UFOV-game__review-comment-field textarea {
        background: rgba(255, 255, 255, 0.92);
        border: 2px solid rgba(19, 38, 79, 0.12);
        border-radius: 18px;
        color: var(--UFOV-ink);
        font: inherit;
        font-size: 14px;
        font-weight: 500;
        line-height: 1.35;
        min-height: 104px;
        outline: none;
        padding: 12px 13px;
        resize: vertical;
    }

    .UFOV-game__review-comment-field textarea:focus {
        border-color: rgba(19, 38, 79, 0.34);
    }

    .UFOV-game__review-modal-error {
        color: #a42b2b;
        font-size: 12px;
        font-weight: 700;
        line-height: 1.3;
        margin: -4px 0 0;
        text-align: center;
    }

    .UFOV-game__review-modal-actions,
    .UFOV-game__review-choice-actions {
        display: grid;
        gap: 10px;
    }

    .UFOV-game__review-modal-actions {
        grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    }

    .UFOV-game__review-choice-actions {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .UFOV-game__review-modal-actions button,
    .UFOV-game__review-choice-actions button {
        border: 0;
        border-radius: 20px;
        cursor: pointer;
        font: inherit;
        font-size: 14px;
        font-weight: 700;
        min-height: 52px;
        padding: 0 14px;
    }

    .UFOV-game__review-modal-actions button:disabled,
    .UFOV-game__review-choice-actions button:disabled {
        cursor: wait;
        opacity: 0.72;
    }

    .UFOV-game__review-modal-secondary {
        background: rgba(19, 38, 79, 0.08);
        color: var(--UFOV-ink);
    }

    .UFOV-game__review-modal-primary {
        background: #0b66ff;
        color: #fff;
        box-shadow: 0 14px 24px rgba(11, 102, 255, 0.22);
    }

    .UFOV-game__onboarding-stage {
        animation: UFOV-onboarding-in 220ms ease both;
    }

    @keyframes UFOV-onboarding-in {
        from {
            opacity: 0;
            transform: translateY(8px);
        }

        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .UFOV-game__onboarding-list {
        display: grid;
        gap: clamp(12px, 1.8vh, 18px);
    }

    .UFOV-game__onboarding-row {
        align-items: center;
        animation: UFOV-onboarding-row-in 420ms ease both;
        display: grid;
        gap: 12px;
        grid-template-columns: 44px 72px minmax(0, 1fr);
        opacity: 0;
    }

    .UFOV-game__onboarding-row:nth-child(1) {
        animation-delay: 0ms;
    }

    .UFOV-game__onboarding-row:nth-child(2) {
        animation-delay: 1000ms;
    }

    .UFOV-game__onboarding-row:nth-child(3) {
        animation-delay: 2000ms;
    }

    @keyframes UFOV-onboarding-row-in {
        from {
            opacity: 0;
            transform: translateY(10px);
        }

        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .UFOV-game__onboarding-index,
    .UFOV-game__onboarding-icon,
    .UFOV-game__daily-icon {
        align-items: center;
        border-radius: 20px;
        display: flex;
        justify-content: center;
    }

    .UFOV-game__onboarding-index {
        color: var(--UFOV-ink);
        font-size: 18px;
        font-weight: 700;
        height: 44px;
        width: 44px;
    }

    .UFOV-game__onboarding-icon {
        height: 72px;
        width: 72px;
    }

    .UFOV-game__onboarding-index.is-mint,
    .UFOV-game__onboarding-icon.is-mint {
        background: var(--UFOV-soft-mint);
    }

    .UFOV-game__onboarding-index.is-sand,
    .UFOV-game__onboarding-icon.is-sand {
        background: var(--UFOV-soft-sand);
    }

    .UFOV-game__onboarding-index.is-peach,
    .UFOV-game__onboarding-icon.is-peach {
        background: var(--UFOV-soft-peach);
    }

    .UFOV-game__onboarding-row strong,
    .UFOV-game__prompt-block h2,
    .UFOV-game__daily-head strong {
        color: var(--UFOV-ink);
        font-size: 14px;
        font-weight: 500;
        letter-spacing: -0.01em;
        line-height: 1.24;
    }

    .UFOV-game__study-preview {
        display: grid;
        gap: clamp(16px, 3vh, 24px);
        justify-items: center;
        margin: 0 auto;
        width: 100%;
    }

    .UFOV-game__study-page {
        border-radius: 14px;
        box-shadow: 0 8px 18px rgba(17, 27, 47, 0.035);
        display: block;
        max-width: min(100%, 229px);
        width: 100%;
    }

    .UFOV-game__study-preview.is-research .UFOV-game__study-page {
        max-width: min(100%, 252px);
    }

    .UFOV-game__answer-toast {
        align-items: center;
        animation: UFOV-answer-toast 900ms ease both;
        background: rgba(255, 244, 242, 0.98);
        border: 1px solid rgba(226, 58, 45, 0.18);
        border-radius: 999px;
        box-shadow: 0 12px 22px rgba(226, 58, 45, 0.12);
        color: #b42318;
        display: inline-flex;
        font-size: 13px;
        font-weight: 700;
        gap: 8px;
        justify-self: center;
        min-height: 34px;
        padding: 0 14px 0 10px;
    }

    .UFOV-game__answer-toast span {
        align-items: center;
        background: #e23a2d;
        border-radius: 50%;
        color: #fff;
        display: inline-flex;
        font-size: 12px;
        height: 20px;
        justify-content: center;
        width: 20px;
    }

    @keyframes UFOV-answer-toast {
        0% {
            opacity: 0;
            transform: translateY(6px) scale(0.96);
        }

        18%,
        70% {
            opacity: 1;
            transform: translateY(0) scale(1);
        }

        100% {
            opacity: 0;
            transform: translateY(-4px) scale(0.98);
        }
    }

    .UFOV-game__illustration-svg {
        color: var(--UFOV-ink);
        height: 42px;
        width: 42px;
    }

    .UFOV-game__menu-icon {
        color: #189aa3;
        height: 34px;
        width: 34px;
    }

    .UFOV-game__menu-icon.is-brain-game {
        color: var(--UFOV-brain-icon-color, #4154d9);
    }

    .UFOV-game__menu-icon.is-brain-game.is-tone-1 {
        color: var(--UFOV-brain-icon-color, #18a87b);
    }

    .UFOV-game__menu-icon.is-brain-game.is-tone-2 {
        color: var(--UFOV-brain-icon-color, #cf5f24);
    }

    .UFOV-game__menu-icon.is-brain-game.is-tone-3 {
        color: var(--UFOV-brain-icon-color, #b644a5);
    }

    .UFOV-game__menu-icon.is-brain-game.is-tone-4 {
        color: var(--UFOV-brain-icon-color, #147caa);
    }

    .UFOV-game__brain-card-icon {
        align-items: center;
        color: var(--UFOV-brain-icon-color, #4154d9);
        display: inline-flex;
        height: 36px;
        justify-content: center;
        position: relative;
        width: 36px;
    }

    .UFOV-game__brain-card-icon-glyph {
        height: 30px;
        width: 30px;
    }

    .UFOV-game__brain-card-icon.has-marker .UFOV-game__brain-card-icon-glyph {
        height: 28px;
        transform: translate(-2px, -2px);
        width: 28px;
    }

    .UFOV-game__brain-card-icon-marker {
        align-items: center;
        background: var(--UFOV-brain-icon-color, #4154d9);
        border: 1.5px solid #fff;
        border-radius: var(--UFOV-radius-pill);
        bottom: -3px;
        box-shadow: 0 2px 5px rgba(19, 38, 79, 0.18);
        color: #fff;
        display: inline-flex;
        font-size: 8px;
        font-weight: 700;
        height: 15px;
        justify-content: center;
        letter-spacing: -0.01em;
        line-height: 1;
        min-width: 15px;
        padding: 0 3px;
        position: absolute;
        right: -4px;
        white-space: nowrap;
    }

    .UFOV-game__menu-icon.is-muted {
        color: #8d96a8;
    }

    .UFOV-game__small-icon,
    .UFOV-game__footer-icon {
        color: #6f788a;
        height: 24px;
        flex: 0 0 auto;
        width: 24px;
    }

    .UFOV-game__footer-icon.is-settings {
        color: var(--UFOV-ink);
    }

    .UFOV-game__primary-button,
    .UFOV-game__text-link,
    .UFOV-game__footer-link,
    .UFOV-game__contact-pill,
    .UFOV-game__ranking-pill.is-menu,
    .UFOV-game__paywall-links button {
        border: 0;
        cursor: pointer;
        font: inherit;
    }

    .UFOV-game__primary-button {
        align-items: center;
        background: #111b2f;
        border-radius: 22px;
        color: #fff;
        display: inline-flex;
        font-size: 16px;
        font-weight: 600;
        height: 58px;
        justify-content: center;
        letter-spacing: -0.01em;
        padding: 0 24px;
        width: 100%;
    }

    .UFOV-game__primary-button:disabled {
        cursor: default;
        opacity: 0.5;
    }

    .UFOV-game__secondary-button {
        align-items: center;
        background: rgba(255, 255, 255, 0.8);
        border: 1px solid rgba(19, 38, 79, 0.1);
        border-radius: 20px;
        color: var(--UFOV-ink);
        cursor: pointer;
        display: inline-flex;
        font: inherit;
        font-size: 14px;
        font-weight: 700;
        height: 48px;
        justify-content: center;
        padding: 0 18px;
        width: 100%;
    }

    .UFOV-game__button-spinner {
        animation: UFOV-button-spinner 720ms linear infinite;
        border: 3px solid rgba(255, 255, 255, 0.34);
        border-top-color: #fff;
        border-radius: 50%;
        display: inline-block;
        height: 22px;
        width: 22px;
    }

    @keyframes UFOV-button-spinner {
        to {
            transform: rotate(360deg);
        }
    }

    .UFOV-game__board {
        background:
            linear-gradient(rgba(228, 233, 232, 0.55) 1px, transparent 1px),
            linear-gradient(90deg, rgba(228, 233, 232, 0.55) 1px, transparent 1px),
            #fbfbf5;
        background-size: 10.2% 10.2%, 10.2% 10.2%, auto;
        border: 1.5px solid rgba(19, 38, 79, 0.12);
        border-radius: 18px;
        box-shadow: 0 12px 28px rgba(17, 27, 47, 0.06);
        margin: 0 auto;
        overflow: hidden;
        position: relative;
        width: min(100%, 336px);
        aspect-ratio: 1 / 1;
        filter: grayscale(1) saturate(0);
    }

    .UFOV-game__board.has-board-art {
        background: var(--UFOV-board-image) center / contain no-repeat;
        border: 0;
        border-radius: 0;
        box-shadow: none;
        filter: none;
    }

    .UFOV-game__screen--play .UFOV-game__board {
        aspect-ratio: 1 / 1;
        width: var(--UFOV-play-board-size);
    }

    .UFOV-game__board.is-muted {
        filter: grayscale(1) saturate(0) opacity(0.9);
    }

    .UFOV-game__board.has-board-art.is-muted {
        filter: opacity(0.92);
    }

    .UFOV-game__board-grid,
    .UFOV-game__board-mask {
        inset: 0;
        position: absolute;
    }

    .UFOV-game__board.has-board-art .UFOV-game__board-grid {
        display: none;
    }

    .UFOV-game__board-noise,
    .UFOV-game__board-peripheral,
    .UFOV-game__board-center {
        position: absolute;
    }

    .UFOV-game__board-noise {
        color: #9ca3ab;
        line-height: 1;
        transition: none;
        user-select: none;
    }

    .UFOV-game__board-peripheral {
        color: #5f6066;
        filter: drop-shadow(0 3px 4px rgba(38, 22, 8, 0.42));
        font-size: calc(36px * var(--UFOV-board-scale, 1));
        line-height: 1;
        transition: none;
        z-index: 2;
    }

    .UFOV-game.is-variant-fruit .UFOV-game__board-peripheral {
        font-size: calc(34px * var(--UFOV-board-scale, 1));
    }

    .UFOV-game__board-noise,
    .UFOV-game__board-peripheral {
        --UFOV-symbol-rotation: 0deg;
        --UFOV-symbol-move-x: 0px;
        --UFOV-symbol-move-y: 0px;
        --UFOV-symbol-spin: 0deg;
        --UFOV-symbol-duration: 2400ms;
        --UFOV-symbol-delay: 0ms;
        animation: UFOV-symbol-drift var(--UFOV-symbol-duration) ease-in-out infinite alternate;
        animation-delay: var(--UFOV-symbol-delay);
        transform: translate(-50%, -50%) rotate(var(--UFOV-symbol-rotation));
        will-change: transform;
    }

    @keyframes UFOV-symbol-drift {
        0% {
            transform: translate(-50%, -50%) rotate(var(--UFOV-symbol-rotation));
        }

        55% {
            transform:
                translate(
                    calc(-50% + var(--UFOV-symbol-move-x)),
                    calc(-50% + var(--UFOV-symbol-move-y))
                )
                rotate(calc(var(--UFOV-symbol-rotation) + var(--UFOV-symbol-spin)));
        }

        100% {
            transform:
                translate(
                    calc(-50% - var(--UFOV-symbol-move-y)),
                    calc(-50% + var(--UFOV-symbol-move-x))
                )
                rotate(calc(var(--UFOV-symbol-rotation) - var(--UFOV-symbol-spin)));
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .UFOV-game {
            --UFOV-interaction-fast: 0ms;
            --UFOV-interaction-standard: 0ms;
            --UFOV-interaction-press-scale: 1;
        }

        .UFOV-game__board-noise,
        .UFOV-game__board-peripheral {
            animation: none;
            transform: translate(-50%, -50%) rotate(var(--UFOV-symbol-rotation));
        }

        .UFOV-game__answer-toast,
        .UFOV-game__onboarding-stage,
        .UFOV-game__onboarding-row {
            animation: none;
        }

        .UFOV-game__result-level-pill,
        .UFOV-game__result-axis-pill {
            animation: none;
            opacity: 1;
            transform: none;
        }
    }

    .UFOV-game__board-center {
        align-items: center;
        border: calc(2.5px * var(--UFOV-board-scale, 1)) solid rgba(17, 27, 47, 0.92);
        display: flex;
        font-size: calc(42px * var(--UFOV-board-scale, 1));
        height: calc(59px * var(--UFOV-board-scale, 1));
        justify-content: center;
        left: 50%;
        line-height: 1;
        top: 50%;
        transform: translate(-50%, -50%);
        width: calc(59px * var(--UFOV-board-scale, 1));
    }

    .UFOV-game.is-variant-fruit .UFOV-game__board-center {
        background: rgb(255 251 242 / 26%);
        border-color: rgb(55 28 3 / 72%);
        box-shadow: 0 10px 18px rgba(95, 62, 27, 0.12);
        border-radius: calc(20px * var(--UFOV-board-scale, 1));
    }

    .UFOV-game__board-center > span {
        display: block;
        transition: none;
    }

    .UFOV-game__board-noise.is-hidden,
    .UFOV-game__board-peripheral.is-hidden,
    .UFOV-game__board-center > span.is-hidden {
        opacity: 0;
    }

    .UFOV-game__board-mask {
        background: #05080f;
    }

    .UFOV-game__board.has-board-art .UFOV-game__board-mask {
        background: rgba(54, 34, 18, 0.92);
    }

    .UFOV-game__onboarding-demo-layers {
        display: grid;
        justify-items: center;
        place-items: center;
        width: 100%;
    }

    .UFOV-game__onboarding-demo-layers.is-hidden-only {
        inset: 0;
        pointer-events: none;
        position: absolute;
        z-index: 0;
    }

    .UFOV-game__onboarding-demo-layers.has-visible-layer {
        position: relative;
        z-index: 1;
    }

    .UFOV-game__onboarding-demo-layer {
        grid-area: 1 / 1;
        width: 100%;
    }

    .UFOV-game__onboarding-demo-layer.is-prewarming {
        inset: 0;
        opacity: 0.001;
        pointer-events: none;
        position: absolute;
        z-index: 0;
    }

    .UFOV-game__onboarding-demo-layer.is-visible {
        opacity: 1;
        position: relative;
        z-index: 1;
    }

    .UFOV-game__board-preload {
        align-items: center;
        aspect-ratio: 1 / 1;
        background:
            linear-gradient(135deg, rgba(255, 251, 242, 0.92), rgba(244, 223, 194, 0.86));
        border: 2px solid rgba(91, 58, 32, 0.34);
        border-radius: 18px;
        color: var(--UFOV-muted);
        display: flex;
        font-size: 15px;
        font-weight: 700;
        justify-content: center;
        letter-spacing: -0.01em;
        margin: 0 auto;
        width: min(100%, 336px);
    }

    .UFOV-game__board-preload.is-compact {
        aspect-ratio: auto;
        min-height: 180px;
        width: 100%;
    }

    .UFOV-game__board-preload::after,
    .UFOV-game__board-prerender-status::after {
        animation: UFOV-button-spinner 720ms linear infinite;
        border: 3px solid rgba(74, 47, 22, 0.18);
        border-radius: 50%;
        border-top-color: rgba(74, 47, 22, 0.7);
        content: '';
        display: block;
        height: 22px;
        width: 22px;
    }

    .UFOV-game__prompt-block {
        display: grid;
        gap: 10px;
        margin: 0 auto;
        width: 100%;
    }

    .UFOV-game__prompt-block h2 {
        margin: 0;
    }

    .UFOV-game__question-title {
        color: var(--UFOV-ink);
        font-size: 26px;
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 1.08;
        margin: 0;
        text-align: center;
    }

    .UFOV-game__prompt-block h2.UFOV-game__question-title {
        font-size: 26px;
        letter-spacing: -0.01em;
        line-height: 1.08;
        text-align: center;
    }

    .UFOV-game__onboarding-example.is-board-only {
        --UFOV-onboarding-board-size: min(500px, calc(100vw - 32px), calc(var(--UFOV-resolved-app-height) - 260px));
        justify-items: center;
    }

    .UFOV-game__onboarding-example.is-board-only .UFOV-game__board,
    .UFOV-game__onboarding-example.is-board-only .UFOV-game__board-preload {
        width: var(--UFOV-onboarding-board-size);
    }

    .UFOV-game__onboarding-speed-note {
        color: var(--UFOV-muted);
        font-size: 14px;
        font-weight: 600;
        line-height: 1.35;
        margin: 2px auto 0;
        max-width: 310px;
        text-align: center;
    }

    .UFOV-game__choice-grid {
        display: grid;
        gap: 10px;
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    .UFOV-game__missed-it-button {
        background: transparent;
        border: 0;
        color: #426da9;
        cursor: pointer;
        font: inherit;
        font-size: 13px;
        font-weight: 600;
        justify-self: center;
        line-height: 1.2;
        margin-top: 2px;
        min-height: 30px;
        padding: 5px 10px;
        transition:
            color var(--UFOV-interaction-fast) var(--UFOV-interaction-ease),
            transform var(--UFOV-interaction-fast) var(--UFOV-interaction-ease);
    }

    .UFOV-game__missed-it-button:hover,
    .UFOV-game__missed-it-button:focus-visible {
        color: #1f57a4;
        text-decoration: underline;
        text-underline-offset: 3px;
    }

    .UFOV-game__missed-it-button:active {
        transform: scale(var(--UFOV-interaction-press-scale));
    }

    .UFOV-game__missed-it-overlay {
        align-items: flex-end;
        background: rgba(13, 22, 39, 0.38);
        display: flex;
        inset: 0;
        justify-content: center;
        padding: 14px;
        position: absolute;
        z-index: 320;
    }

    .UFOV-game__missed-it-dialog {
        background: var(--UFOV-surface-strong);
        border: 1px solid rgba(19, 38, 79, 0.1);
        border-radius: var(--UFOV-radius-sheet);
        box-shadow: 0 24px 60px rgba(11, 23, 46, 0.24);
        display: grid;
        gap: 20px;
        max-width: 402px;
        padding: 22px;
        width: 100%;
    }

    .UFOV-game__missed-it-copy {
        display: grid;
        gap: 8px;
        text-align: left;
    }

    .UFOV-game__missed-it-copy h2 {
        color: var(--UFOV-title);
        font-size: 22px;
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 1.1;
        margin: 0;
    }

    .UFOV-game__missed-it-copy p {
        color: var(--UFOV-muted);
        font-size: 14px;
        font-weight: 600;
        line-height: 1.45;
        margin: 0;
    }

    .UFOV-game__missed-it-actions {
        display: grid;
        gap: 10px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .UFOV-game__missed-it-actions button {
        background: #eef1f6;
        border: 1px solid rgba(19, 38, 79, 0.08);
        border-radius: var(--UFOV-radius-md);
        color: var(--UFOV-title);
        cursor: pointer;
        font: inherit;
        font-size: 15px;
        font-weight: 700;
        min-height: 50px;
        transition:
            background var(--UFOV-interaction-standard) var(--UFOV-interaction-ease),
            transform var(--UFOV-interaction-fast) var(--UFOV-interaction-ease);
    }

    .UFOV-game__missed-it-actions button.is-primary {
        background: #111b2f;
        border-color: #111b2f;
        color: #ffffff;
    }

    .UFOV-game__missed-it-actions button:hover {
        background: #e3e8f0;
    }

    .UFOV-game__missed-it-actions button.is-primary:hover {
        background: #1b2b49;
    }

    .UFOV-game__missed-it-actions button:active {
        transform: scale(var(--UFOV-interaction-press-scale));
    }

    .UFOV-game__choice-tile,
    .UFOV-game__direction-tile {
        align-items: center;
        background:
            linear-gradient(var(--UFOV-surface-strong), var(--UFOV-surface-strong)) padding-box,
            linear-gradient(135deg, rgba(19, 38, 79, 0.12), rgba(19, 38, 79, 0.12)) border-box;
        border: 2px solid transparent;
        border-radius: 16px;
        color: var(--UFOV-ink);
        cursor: pointer;
        display: inline-flex;
        justify-content: center;
        min-height: 74px;
        padding: 0;
    }

    .UFOV-game__choice-tile.is-selected,
    .UFOV-game__direction-tile.is-selected,
    .UFOV-game__mode-card.is-active {
        background:
            linear-gradient(rgba(255, 250, 241, 0.98), rgba(255, 250, 241, 0.98)) padding-box,
            linear-gradient(135deg, var(--UFOV-mint), var(--UFOV-gold), var(--UFOV-coral)) border-box;
    }

    .UFOV-game__choice-tile > .UFOV-game__symbol {
        line-height: 1;
    }

    .UFOV-game__choice-tile > .UFOV-game__symbol--image {
        height: 46px;
        width: 46px;
    }

    .UFOV-game__direction-pad {
        display: grid;
        gap: 10px;
        grid-template-areas:
            "nw n ne"
            "w c e"
            "sw s se";
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .UFOV-game__direction-tile {
        font-size: 16px;
        font-weight: 700;
        min-height: 68px;
    }

    .UFOV-game__direction-center {
        align-self: center;
        background: #111b2f;
        border-radius: 50%;
        height: 18px;
        justify-self: center;
        width: 18px;
    }

    .UFOV-game__menu-top {
        align-items: center;
        display: grid;
        gap: 8px;
        grid-template-columns: 40px minmax(0, 1fr) auto;
        min-height: 44px;
        width: 100%;
    }

    .UFOV-game__menu-brand {
        align-items: center;
        display: inline-flex;
        gap: 8px;
        grid-column: 1;
        justify-self: start;
        min-width: 0;
    }

    .UFOV-game__menu-brand-label {
        color: var(--UFOV-ink);
        font-size: 17px;
        font-weight: var(--UFOV-font-weight-bold);
        letter-spacing: 0.06em;
        line-height: 1;
    }

    .UFOV-game__menu-logo {
        display: block;
        flex: none;
        height: 40px;
        object-fit: contain;
        width: 40px;
    }

    .UFOV-game__contact-pill {
        align-items: center;
        align-self: center;
        background: var(--UFOV-color-surface-strong);
        border: 1px solid var(--UFOV-color-border);
        border-radius: var(--UFOV-radius-pill);
        color: var(--UFOV-ink);
        display: inline-flex;
        gap: 7px;
        grid-column: 3;
        justify-content: center;
        justify-self: end;
        min-height: 40px;
        padding: 5px 11px;
        transition:
            border-color var(--UFOV-interaction-fast) var(--UFOV-interaction-ease),
            opacity var(--UFOV-interaction-fast) var(--UFOV-interaction-ease),
            transform var(--UFOV-interaction-fast) var(--UFOV-interaction-ease);
    }

    .UFOV-game__contact-pill:hover {
        border-color: var(--UFOV-color-action);
        transform: scale(var(--UFOV-interaction-press-scale));
    }

    .UFOV-game__contact-pill:active {
        opacity: 0.72;
        transform: scale(var(--UFOV-interaction-press-scale));
    }

    .UFOV-game__contact-pill-icon {
        flex: 0 0 auto;
        height: 16px;
        width: 16px;
    }

    .UFOV-game__contact-pill-copy {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-width: 0;
    }

    .UFOV-game__contact-pill-copy strong {
        color: var(--UFOV-ink);
        font-size: 11px;
        font-weight: 600;
        line-height: 12px;
    }

    .UFOV-game__contact-pill-copy > span {
        color: var(--UFOV-muted);
        font-size: 8px;
        font-weight: 500;
        line-height: 10px;
    }

    .UFOV-game__hero-title {
        font-size: clamp(42px, 13vw, 74px);
        text-wrap: nowrap;
    }

    .UFOV-game__menu-main,
    .UFOV-game__menu-meter {
        display: grid;
        gap: clamp(12px, 1.8vh, 16px);
    }

    .UFOV-game__menu-age-source {
        color: var(--UFOV-color-text-muted);
        font-size: 11px;
        font-weight: 600;
        line-height: 1.25;
        margin: -8px auto 0;
        max-width: 240px;
        text-align: center;
    }

    .UFOV-game__mode-grid {
        display: grid;
        gap: 12px;
        grid-template-columns: minmax(0, 1fr);
    }

    .UFOV-game__screen--menu .UFOV-game__menu-main {
        align-content: start;
        width: 100%;
    }

    .UFOV-game__mode-card {
        --UFOV-mode-card-inline-padding: 14px;
        align-items: center;
        background: var(--UFOV-color-surface-strong);
        border: 1px solid var(--UFOV-color-border);
        border-radius: var(--UFOV-radius-xl);
        box-shadow: 0 8px 22px rgba(17, 27, 47, 0.06);
        color: var(--UFOV-color-text);
        cursor: pointer;
        display: grid;
        gap: 8px;
        grid-template-columns: auto minmax(0, 1fr) auto;
        min-height: 88px;
        padding: 14px var(--UFOV-mode-card-inline-padding) 12px;
        position: relative;
        text-align: left;
        transition:
            background-color var(--UFOV-interaction-standard) var(--UFOV-interaction-ease),
            border-color var(--UFOV-interaction-standard) var(--UFOV-interaction-ease),
            box-shadow var(--UFOV-interaction-standard) var(--UFOV-interaction-ease),
            transform var(--UFOV-interaction-fast) var(--UFOV-interaction-ease);
        width: 100%;
    }

    .UFOV-game__mode-card:hover:not(:disabled) {
        border-color: rgba(31, 107, 255, 0.38);
        box-shadow: 0 10px 26px rgba(17, 27, 47, 0.1);
    }

    .UFOV-game__mode-card:active:not(:disabled) {
        transform: scale(var(--UFOV-interaction-press-scale));
    }

    .UFOV-game__mode-card:focus-visible {
        outline: 2px solid var(--UFOV-color-action);
        outline-offset: -4px;
    }

    .UFOV-game__mode-card.is-active:focus-visible {
        outline: none;
    }

    .UFOV-game__mode-card:disabled {
        cursor: default;
        opacity: var(--UFOV-interaction-disabled-opacity);
    }

    .UFOV-game__mode-card.has-footer {
        overflow: hidden;
        padding-bottom: 0;
    }

    .UFOV-game__mode-card.is-active {
        background: var(--UFOV-color-surface-strong);
        border-color: var(--UFOV-color-action);
        box-shadow: 0 8px 22px rgba(17, 27, 47, 0.08);
    }

    .UFOV-game__mode-card.is-active::after {
        border: 2px solid var(--UFOV-color-action);
        border-radius: inherit;
        content: "";
        inset: 0;
        pointer-events: none;
        position: absolute;
    }

    .UFOV-game__mode-card.is-brain-game {
        --UFOV-brain-icon-bg: rgba(65, 84, 217, 0.12);
        --UFOV-brain-icon-color: #4154d9;
        background: var(--UFOV-color-surface-strong);
        min-height: 82px;
    }

    .UFOV-game__mode-card.is-brain-game.is-tone-1 {
        --UFOV-brain-icon-bg: rgba(24, 168, 123, 0.12);
        --UFOV-brain-icon-color: #18a87b;
    }

    .UFOV-game__mode-card.is-brain-game.is-tone-2 {
        --UFOV-brain-icon-bg: rgba(207, 95, 36, 0.12);
        --UFOV-brain-icon-color: #cf5f24;
    }

    .UFOV-game__mode-card.is-brain-game.is-tone-3 {
        --UFOV-brain-icon-bg: rgb(255 0 217 / 12%);
        --UFOV-brain-icon-color: #b644a5;
    }

    .UFOV-game__mode-card.is-brain-game.is-tone-4 {
        --UFOV-brain-icon-bg: rgba(20, 124, 170, 0.12);
        --UFOV-brain-icon-color: #147caa;
    }

    .UFOV-game__mode-card.is-brain-game.is-active {
        background: var(--UFOV-color-surface-strong);
        border-color: var(--UFOV-color-action);
    }

    .UFOV-game__menu-entry {
        display: grid;
        min-width: 0;
        scroll-margin-block: 12px 28px;
    }

    .UFOV-game__menu-entry.is-expanded {
        grid-column: 1 / -1;
    }

    .UFOV-game__menu-entry.is-expanded .UFOV-game__mode-card {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        box-shadow: none;
        z-index: 2;
    }

    .UFOV-game__mode-actions {
        align-items: center;
        display: inline-flex;
        gap: 3px;
        justify-self: end;
        min-width: 0;
    }

    .UFOV-game__mode-disclosure {
        align-items: center;
        color: var(--UFOV-color-text-muted);
        display: inline-flex;
        flex: none;
        height: 26px;
        justify-content: center;
        transition: transform var(--UFOV-interaction-standard) var(--UFOV-interaction-ease);
        width: 26px;
    }

    .UFOV-game__mode-disclosure svg {
        height: 17px;
        width: 17px;
    }

    .UFOV-game__menu-entry.is-expanded .UFOV-game__mode-disclosure {
        color: var(--UFOV-color-action);
        transform: rotate(180deg);
    }

    .UFOV-game__menu-preview {
        animation: UFOV-menu-preview-in var(--UFOV-interaction-standard) var(--UFOV-interaction-ease) both;
        align-items: stretch;
        background: var(--UFOV-color-surface-strong);
        border: 1px solid var(--UFOV-color-border);
        border-radius: 0 0 var(--UFOV-radius-xl) var(--UFOV-radius-xl);
        border-top: 0;
        box-shadow: 0 12px 26px rgba(17, 27, 47, 0.07);
        display: grid;
        gap: 10px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        min-height: 0;
        overflow: hidden;
        padding: 10px;
        transform-origin: top;
    }

    .UFOV-game__menu-preview-stats {
        align-self: start;
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 0;
    }

    .UFOV-game__menu-preview-stat {
        background: #f5f7fb;
        border: 1px solid var(--UFOV-color-border);
        border-radius: var(--UFOV-radius-md);
        color: var(--UFOV-color-heading);
        min-width: 0;
        padding: 9px 10px;
    }

    .UFOV-game__menu-preview-change-level {
        align-items: center;
        background: var(--UFOV-color-heading);
        border: 0;
        border-radius: var(--UFOV-radius-md);
        color: #fff;
        cursor: pointer;
        display: inline-flex;
        font: inherit;
        font-size: 11px;
        font-weight: 600;
        justify-content: center;
        min-height: 36px;
        padding: 7px 10px;
        transition:
            opacity var(--UFOV-interaction-standard) var(--UFOV-interaction-ease),
            transform var(--UFOV-interaction-fast) var(--UFOV-interaction-ease);
        width: 100%;
    }

    .UFOV-game__menu-preview-change-level:hover:not(:disabled) {
        opacity: 0.9;
    }

    .UFOV-game__menu-preview-change-level:active:not(:disabled) {
        transform: scale(var(--UFOV-interaction-press-scale));
    }

    .UFOV-game__menu-preview-change-level:disabled {
        cursor: default;
        opacity: var(--UFOV-interaction-disabled-opacity);
    }

    .UFOV-game__menu-preview-stat.is-level {
        align-items: center;
        background: transparent;
        border: 0;
        border-bottom: 1px solid var(--UFOV-color-border);
        border-radius: 0;
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        min-height: 52px;
        padding: 4px 2px 8px;
    }

    .UFOV-game__menu-preview-level-label,
    .UFOV-game__menu-preview-daily-label {
        color: var(--UFOV-color-text-muted);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.04em;
        line-height: 1.1;
        text-transform: uppercase;
    }

    .UFOV-game__menu-preview-level-copy {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        gap: 5px;
        min-width: 0;
    }

    .UFOV-game__menu-preview-level-value {
        align-items: flex-end;
        display: flex;
        flex-direction: column;
        gap: 3px;
        min-width: 0;
    }

    .UFOV-game__menu-preview-level-value > strong {
        color: var(--UFOV-color-heading);
        font-size: clamp(27px, 8vw, 36px);
        font-variant-numeric: tabular-nums;
        font-weight: 700;
        letter-spacing: -0.03em;
        line-height: 1;
    }

    .UFOV-game__menu-preview-difficulty {
        background: var(--UFOV-color-action-soft);
        border: 1px solid var(--UFOV-color-action-border);
        border-radius: var(--UFOV-radius-pill);
        color: var(--UFOV-color-action);
        font-size: 9px;
        font-weight: 600;
        letter-spacing: 0.01em;
        line-height: 1;
        padding: 3px 6px;
        white-space: nowrap;
    }

    .UFOV-game__menu-preview-stat.is-daily {
        padding: 8px 9px;
    }

    .UFOV-game__menu-preview-daily-head {
        align-items: center;
        display: grid;
        gap: 6px;
        grid-template-columns: 24px minmax(0, 1fr) auto;
        min-width: 0;
    }

    .UFOV-game__menu-preview-daily-head .UFOV-game__mode-daily-calendar {
        height: 24px;
        width: 24px;
    }

    .UFOV-game__menu-preview-daily-head .UFOV-game__small-icon {
        height: 15px;
        width: 15px;
    }

    .UFOV-game__menu-preview-daily-head > strong {
        color: var(--UFOV-color-heading);
        font-size: 12px;
        font-variant-numeric: tabular-nums;
        font-weight: 700;
        line-height: 1;
        white-space: nowrap;
    }

    .UFOV-game__menu-preview-stat.is-daily .UFOV-game__progress-track {
        height: 4px;
        margin-top: 7px;
    }

    .UFOV-game__menu-preview-stat.is-daily.is-complete {
        background: var(--UFOV-color-success-soft);
        border-color: var(--UFOV-color-success-border);
    }

    .UFOV-game__menu-preview-stat.is-daily.is-complete .UFOV-game__mode-daily-calendar,
    .UFOV-game__menu-preview-stat.is-daily.is-complete .UFOV-game__menu-preview-daily-head > strong,
    .UFOV-game__menu-preview-stat.is-daily.is-complete .UFOV-game__menu-preview-daily-label {
        color: var(--UFOV-color-success-text);
    }

    .UFOV-game__menu-preview-stat.is-daily.is-complete .UFOV-game__progress-track-fill {
        background: var(--UFOV-color-success);
    }

    .UFOV-game__menu-preview-media {
        align-items: center;
        background: var(--UFOV-color-surface-strong);
        border-radius: var(--UFOV-radius-lg);
        display: flex;
        justify-content: center;
        min-width: 0;
        overflow: hidden;
        position: relative;
    }

    .UFOV-game__menu-preview-media-label {
        background: var(--UFOV-color-heading);
        border: 0;
        border-radius: var(--UFOV-radius-pill);
        color: #fff;
        font-size: 8px;
        font-weight: 600;
        left: 50%;
        line-height: 1;
        padding: 4px 6px;
        position: absolute;
        top: 7px;
        transform: translateX(-50%);
        white-space: nowrap;
        z-index: 1;
    }

    .UFOV-game__menu-preview-video {
        background: var(--UFOV-color-surface-strong);
        display: block;
        height: clamp(232px, 32vh, 268px);
        max-width: 100%;
        object-fit: contain;
        pointer-events: none;
        width: 100%;
    }

    .UFOV-game__menu-preview.is-landscape {
        grid-template-columns: minmax(0, 1fr);
    }

    .UFOV-game__menu-preview.is-landscape .UFOV-game__menu-preview-stats {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .UFOV-game__menu-preview.is-landscape .UFOV-game__menu-preview-stat.is-daily {
        grid-column: 1 / -1;
    }

    .UFOV-game__menu-preview.is-landscape .UFOV-game__menu-preview-video {
        aspect-ratio: 16 / 9;
        height: auto;
        object-fit: contain;
    }

    @keyframes UFOV-menu-preview-in {
        from {
            opacity: 0;
            transform: translateY(-8px) scaleY(0.96);
        }
        to {
            opacity: 1;
            transform: translateY(0) scaleY(1);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .UFOV-game__menu-preview {
            animation: none;
        }

        .UFOV-game__mode-disclosure {
            transition: none;
        }
    }

    .UFOV-game__brain-games-label {
        align-items: center;
        color: #8b94a5;
        display: flex;
        gap: 10px;
        grid-column: 1 / -1;
        justify-content: center;
        min-height: 22px;
        padding: 2px 4px 0;
        text-align: center;
    }

    .UFOV-game__brain-games-label::before,
    .UFOV-game__brain-games-label::after {
        background: #d9dee8;
        border-radius: 999px;
        content: "";
        flex: 1 1 auto;
        height: 1px;
        min-width: 0;
    }

    .UFOV-game__brain-games-label span {
        color: #8b94a5;
        flex: none;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0;
        text-transform: none;
    }

    .UFOV-game__brain-games-label strong {
        background: rgba(255, 122, 102, 0.16);
        border: 1px solid rgba(255, 122, 102, 0.24);
        border-radius: 999px;
        color: #b54939;
        flex: none;
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 0.04em;
        line-height: 1;
        padding: 5px 7px;
        text-transform: uppercase;
        white-space: nowrap;
    }

    .UFOV-game__mode-card.is-locked {
        color: #8b94a5;
    }

    .UFOV-game__mode-icon {
        align-items: center;
        display: inline-flex;
        justify-content: center;
    }

    .UFOV-game__mode-card.is-classic-mode .UFOV-game__mode-icon,
    .UFOV-game__mode-card.is-advanced-mode .UFOV-game__mode-icon {
        background: rgba(53, 201, 193, 0.14);
        border-radius: 999px;
        flex: none;
        height: 52px;
        width: 52px;
    }

    .UFOV-game__mode-card.is-brain-game .UFOV-game__mode-icon {
        background: var(--UFOV-brain-icon-bg);
        border-radius: 999px;
        flex: none;
        height: 52px;
        width: 52px;
    }

    .UFOV-game__menu-image-icon {
        display: block;
        height: 36px;
        object-fit: contain;
        width: 36px;
    }

    .UFOV-game__mode-card.is-classic-mode .UFOV-game__menu-image-icon,
    .UFOV-game__mode-card.is-advanced-mode .UFOV-game__menu-image-icon {
        height: 34px;
        width: 34px;
    }

    .UFOV-game__sopt-menu-icon {
        display: block;
        height: 36px;
        position: relative;
        width: 36px;
    }

    .UFOV-game__sopt-menu-logo {
        display: block;
        height: 100%;
        inset: 0;
        object-fit: contain;
        position: absolute;
        width: 100%;
    }

    .UFOV-game__sopt-menu-logo.is-front {
        transform: scale(0.98);
    }

    .UFOV-game__sopt-menu-icon.is-advanced .UFOV-game__sopt-menu-logo.is-front {
        filter: hue-rotate(155deg) saturate(1.28) brightness(1.02);
        transform: scale(0.98);
    }

    .UFOV-game__mode-copy {
        display: grid;
        gap: 4px;
        min-width: 0;
    }

    .UFOV-game__mode-copy strong {
        font-size: 14px;
        font-weight: var(--UFOV-font-weight-bold);
        letter-spacing: -0.01em;
    }

    .UFOV-game__mode-copy span,
    .UFOV-game__mode-badge,
    .UFOV-game__daily-card p,
    .UFOV-game__footer-link span,
    .UFOV-game__result-level {
        color: var(--UFOV-muted);
        font-size: 12px;
        font-weight: 600;
        line-height: 1.3;
    }

    .UFOV-game__mode-badge {
        align-items: center;
        align-self: center;
        background: rgba(53, 201, 193, 0.16);
        border-radius: var(--UFOV-radius-pill);
        color: #147d76;
        display: inline-flex;
        font-size: 10px;
        gap: 5px;
        justify-content: center;
        line-height: 1.05;
        justify-self: end;
        padding: 6px 9px;
        text-align: center;
        white-space: nowrap;
        width: auto;
    }

    .UFOV-game__mode-badge-icon {
        display: block;
        flex: none;
        height: 15px;
        width: 15px;
    }

    .UFOV-game__mode-daily-footer {
        align-items: center;
        background: var(--UFOV-color-action-soft);
        border-top: 1px solid var(--UFOV-color-action-border);
        display: grid;
        gap: 10px;
        grid-column: 1 / -1;
        grid-template-columns: 34px minmax(0, 1fr) auto;
        margin: 4px calc(0px - var(--UFOV-mode-card-inline-padding)) 0;
        min-height: 54px;
        padding: 9px var(--UFOV-mode-card-inline-padding);
        transition:
            background-color var(--UFOV-interaction-standard) var(--UFOV-interaction-ease),
            border-color var(--UFOV-interaction-standard) var(--UFOV-interaction-ease);
    }

    .UFOV-game__mode-daily-calendar {
        align-items: center;
        background: var(--UFOV-color-surface-strong);
        border: 1px solid var(--UFOV-color-border);
        border-radius: var(--UFOV-radius-md);
        color: var(--UFOV-color-text-on-soft);
        display: inline-flex;
        height: 34px;
        justify-content: center;
        transition:
            background-color var(--UFOV-interaction-standard) var(--UFOV-interaction-ease),
            color var(--UFOV-interaction-standard) var(--UFOV-interaction-ease);
        width: 34px;
    }

    .UFOV-game__mode-daily-calendar .UFOV-game__small-icon {
        height: 20px;
        width: 20px;
    }

    .UFOV-game__mode-daily-copy {
        display: grid;
        gap: 5px;
        min-width: 0;
    }

    .UFOV-game__mode-daily-copy strong {
        color: var(--UFOV-color-text-on-soft);
        font-size: 10px;
        font-weight: var(--UFOV-font-weight-semibold);
        letter-spacing: 0.04em;
        line-height: 1;
    }

    .UFOV-game__mode-daily-copy .UFOV-game__progress-track {
        height: 5px;
    }

    .UFOV-game__mode-daily-count {
        color: var(--UFOV-color-heading);
        font-size: 13px;
        font-variant-numeric: tabular-nums;
        font-weight: var(--UFOV-font-weight-bold);
        white-space: nowrap;
    }

    .UFOV-game__mode-daily-footer.is-complete {
        background: var(--UFOV-color-success-soft);
        border-top-color: var(--UFOV-color-success-border);
    }

    .UFOV-game__mode-daily-footer.is-complete .UFOV-game__mode-daily-calendar {
        color: var(--UFOV-color-success-text);
    }

    .UFOV-game__mode-daily-footer.is-complete .UFOV-game__mode-daily-copy strong,
    .UFOV-game__mode-daily-footer.is-complete .UFOV-game__mode-daily-count {
        color: var(--UFOV-color-success-text);
    }

    .UFOV-game__mode-daily-footer.is-complete .UFOV-game__progress-track-fill {
        background: var(--UFOV-color-success);
    }

    .UFOV-game__level-ring {
        align-items: center;
        aspect-ratio: 1;
        background: linear-gradient(140deg, var(--UFOV-mint), var(--UFOV-gold), var(--UFOV-coral));
            background: linear-gradient(140deg, #ff009e, #9d40ff, #25d7c5, var(--UFOV-gold), var(--UFOV-coral));
        border: 0;
        border-radius: 50%;
        color: inherit;
        display: flex;
        font: inherit;
        justify-content: center;
        margin: 0 auto;
        padding: 12px;
        width: clamp(154px, 43vw, 200px);
    }

    .UFOV-game__level-ring.is-clickable {
        cursor: pointer;
    }

    .UFOV-game__level-ring.is-clickable:focus-visible {
        outline: 3px solid rgba(53, 201, 193, 0.52);
        outline-offset: 4px;
    }

    .UFOV-game__level-ring-inner {
        align-items: center;
        background: rgba(250, 251, 255, 0.96);
        border-radius: 50%;
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: center;
        position: relative;
        width: 100%;
    }

    .UFOV-game__level-ring-inner span {
        color: #6d7585;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: -0.01em;
    }

    .UFOV-game__level-ring-inner strong {
        color: var(--UFOV-title);
        font-size: clamp(58px, 17vw, 86px);
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 0.9;
        padding-right: 0.035em;
    }

    .UFOV-game__level-editor-backdrop {
        align-items: flex-end;
        background: rgba(5, 10, 22, 0.34);
        display: flex;
        height: 100vh;
        height: 100dvh;
        inset: 0;
        justify-content: center;
        padding: 0 14px calc(14px + env(safe-area-inset-bottom, 0px));
        position: fixed;
        width: 100vw;
        z-index: 70;
    }

    .UFOV-game__level-editor-sheet {
        animation: UFOV-level-sheet-in 180ms cubic-bezier(0.22, 1, 0.36, 1) both;
        background: #fff;
        border: 1px solid rgba(19, 38, 79, 0.1);
        border-radius: 28px;
        box-shadow: 0 -14px 42px rgba(4, 9, 20, 0.22);
        color: var(--UFOV-ink);
        display: grid;
        gap: 18px;
        max-width: 430px;
        outline: none;
        padding: 10px 16px 18px;
        width: 100%;
    }

    .UFOV-game__level-editor-handle {
        background: rgba(19, 38, 79, 0.18);
        border-radius: 999px;
        height: 5px;
        justify-self: center;
        width: 54px;
    }

    .UFOV-game__level-editor-head {
        align-items: center;
        display: grid;
        grid-template-columns: 1fr auto;
    }

    .UFOV-game__level-editor-head strong {
        color: var(--UFOV-muted);
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }

    .UFOV-game__level-editor-cancel {
        background: transparent;
        border: 0;
        color: var(--UFOV-muted);
        cursor: pointer;
        font: inherit;
        font-size: 13px;
        font-weight: 700;
        min-height: 34px;
        padding: 0 8px;
    }

    .UFOV-game__level-editor-control {
        align-items: center;
        display: grid;
        gap: 12px;
        grid-template-columns: 64px minmax(0, 1fr) 64px;
    }

    .UFOV-game__level-editor-control button {
        align-items: center;
        background: whitesmoke;
        border: 0;
        border-radius: 22px;
        box-shadow: 0 14px 24px rgba(13, 27, 51, 0.16);
        color: #0d1627;
        cursor: pointer;
        display: inline-flex;
        font: inherit;
        font-size: 34px;
        font-weight: 700;
        height: 64px;
        justify-content: center;
        line-height: 1;
        padding: 0 0 4px;
        width: 64px;
    }

    .UFOV-game__level-editor-control strong {
        align-items: center;
        background: #eef6ff;
        border: 1px solid rgba(19, 38, 79, 0.09);
        border-radius: 24px;
        color: var(--UFOV-title);
        display: flex;
        font-size: 58px;
        font-weight: 700;
        justify-content: center;
        line-height: 1;
        min-height: 92px;
    }

    .UFOV-game__level-editor-set {
        background: #0d1b33;
        border: 0;
        border-radius: 22px;
        color: #fff;
        cursor: pointer;
        font: inherit;
        font-size: 16px;
        font-weight: 700;
        height: 56px;
        width: 100%;
    }

    @keyframes UFOV-level-sheet-in {
        from {
            opacity: 0;
            transform: translateY(26px);
        }

        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .UFOV-game__progress-track {
        background: var(--UFOV-track);
        border-radius: 999px;
        height: 12px;
        overflow: hidden;
        width: 100%;
    }

    .UFOV-game__progress-track.is-compact {
        height: 9px;
    }

    .UFOV-game__progress-track-fill {
        background: linear-gradient(90deg, var(--UFOV-mint), var(--UFOV-gold), var(--UFOV-coral));
        border-radius: inherit;
        height: 100%;
    }

    .UFOV-game__progress-track.is-daily .UFOV-game__progress-track-fill {
        background: var(--UFOV-color-action);
    }

    .UFOV-game__menu-footer {
        background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 255, 0.99));
        border: 1px solid rgba(19, 38, 79, 0.08);
        border-bottom: 0;
        border-radius: 26px 26px 0 0;
        box-shadow: 0 -18px 36px rgba(19, 38, 79, 0.12);
        gap: 9px;
        margin-left: calc(-1 * var(--UFOV-screen-horizontal-gap));
        margin-right: calc(-1 * var(--UFOV-screen-horizontal-gap));
        padding:
            12px
            var(--UFOV-screen-horizontal-gap)
            max(4px, calc(env(safe-area-inset-bottom, 0px) + 4px));
        position: relative;
        z-index: 4;
    }

    .UFOV-game__menu-tour-overlay {
        align-items: end;
        background: rgba(4, 9, 20, 0.72);
        bottom: 0;
        color: #13264f;
        display: flex;
        height: 100vh;
        height: 100dvh;
        inset: 0;
        justify-content: center;
        left: 0;
        margin: 0;
        max-width: none;
        min-height: 100vh;
        min-height: 100dvh;
        padding: 18px;
        pointer-events: auto;
        position: fixed;
        right: 0;
        top: 0;
        width: 100vw;
        z-index: 30;
    }

    .UFOV-game__menu-tour-bubble {
        background: #fffaf2;
        border: 2px solid rgba(57, 35, 19, 0.18);
        border-radius: 26px;
        box-shadow: 0 26px 50px rgba(3, 7, 18, 0.26);
        color: #13264f;
        display: grid;
        gap: 10px;
        padding: 18px;
        position: relative;
        width: min(100%, 360px);
        z-index: 31;
    }

    .UFOV-game__menu-tour-head {
        align-items: center;
        display: flex;
        justify-content: space-between;
        min-height: 40px;
    }

    .UFOV-game__menu-tour-bubble span {
        color: #697487;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
    }

    .UFOV-game__menu-tour-close {
        align-items: center;
        background: rgba(13, 27, 51, 0.07);
        border: 0;
        border-radius: 50%;
        color: #13264f;
        display: inline-flex;
        height: 40px;
        justify-content: center;
        padding: 0;
        transition: background-color 160ms ease, transform 160ms ease;
        width: 40px;
    }

    .UFOV-game__menu-tour-close:hover,
    .UFOV-game__menu-tour-close:focus-visible {
        background: rgba(13, 27, 51, 0.13);
    }

    .UFOV-game__menu-tour-close:active {
        transform: scale(0.95);
    }

    .UFOV-game__menu-tour-close svg {
        height: 20px;
        width: 20px;
    }

    .UFOV-game__menu-tour-bubble strong {
        color: #0d1627;
        font-size: 26px;
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 0.95;
    }

    .UFOV-game__menu-tour-bubble p {
        color: #5d4634;
        font-size: 15px;
        font-weight: 600;
        line-height: 1.3;
        margin: 0;
    }

    .UFOV-game__menu-tour-next {
        background: #0d1b33;
        border: 0;
        border-radius: 999px;
        color: #fff;
        font-size: 15px;
        font-weight: 700;
        height: 48px;
        margin-top: 4px;
    }

    .UFOV-game__mode-card.is-tour-highlight,
    .UFOV-game__daily-card.is-tour-highlight,
    .UFOV-game__mode-daily-footer.is-tour-highlight,
    .UFOV-game__menu-preview-stat.is-tour-highlight,
    .UFOV-game__ranking-pill.is-tour-highlight,
    .UFOV-game__footer-link.is-tour-highlight {
        box-shadow:
            0 0 0 5px rgba(255, 255, 255, 0.96),
            0 24px 48px rgba(3, 7, 18, 0.3);
        position: relative;
        z-index: 40;
    }

    .UFOV-game__menu-footer.is-tour-highlight-container {
        pointer-events: none;
        z-index: 40;
    }

    .UFOV-game__menu-tour-overlay.is-for-progress {
        align-items: center;
        padding-bottom: 150px;
    }

    .UFOV-game__daily-card {
        background: rgba(255, 252, 245, 0.76);
        border: 1px solid rgba(19, 38, 79, 0.1);
        border-radius: 24px;
        box-shadow: 0 16px 32px rgba(17, 27, 47, 0.05);
        display: grid;
        gap: 12px;
        padding: 14px 16px 16px;
    }

    .UFOV-game__daily-head {
        align-items: center;
        display: grid;
        gap: 10px;
        grid-template-columns: auto minmax(0, 1fr) auto;
    }

    .UFOV-game__daily-head span {
        color: var(--UFOV-title);
        font-size: 24px;
        font-weight: 700;
        justify-self: end;
        letter-spacing: -0.01em;
    }

    .UFOV-game__daily-icon {
        align-items: center;
        background: #eef0f4;
        border-radius: 16px;
        display: flex;
        height: 52px;
        justify-content: center;
        width: 52px;
    }

    .UFOV-game__daily-copy {
        display: grid;
        gap: 3px;
        min-width: 0;
    }

    .UFOV-game__daily-card p {
        margin: 0;
        text-align: left;
    }

    .UFOV-game__footer-links {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        justify-content: center;
        justify-self: center;
        margin: 0 auto;
        max-width: 100%;
        width: fit-content;
    }

    .UFOV-game__footer-link {
        align-items: center;
        background: transparent;
        border: 0;
        color: var(--UFOV-muted);
        display: inline-flex;
        gap: 6px;
        min-height: 34px;
        padding: 4px 6px;
    }

    .UFOV-game__footer-divider {
        background: rgba(19, 38, 79, 0.12);
        height: 18px;
        flex: 0 0 auto;
        width: 1px;
    }

    .UFOV-game__config-panel {
        align-content: start;
        display: grid;
        gap: 14px;
        height: 100%;
        max-height: 100%;
        overflow-y: auto;
        padding-bottom: 4px;
        width: 100%;
    }

    .UFOV-game__config-panel.is-theme-page {
        grid-template-rows: auto minmax(0, 1fr);
        overflow: hidden;
    }

    .UFOV-game__config-heading {
        display: grid;
        gap: 8px;
        text-align: center;
    }

    .UFOV-game__config-heading h1 {
        color: var(--UFOV-title);
        font-size: clamp(34px, 10vw, 48px);
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 0.95;
        margin: 0;
    }

    .UFOV-game__config-heading p {
        color: var(--UFOV-muted);
        font-size: 14px;
        font-weight: 500;
        line-height: 1.35;
        margin: 0 auto;
        max-width: 310px;
    }

    .UFOV-game__config-panel.is-feedback-page {
        align-content: start;
        gap: 20px;
    }

    .UFOV-game__settings-feedback-field {
        display: grid;
        gap: 8px;
    }

    .UFOV-game__settings-feedback-field > span {
        color: var(--UFOV-title);
        font-size: 14px;
        font-weight: 600;
    }

    .UFOV-game__settings-feedback-field input,
    .UFOV-game__settings-feedback-field textarea {
        background: #fffaf1;
        border: 2px solid rgba(19, 38, 79, 0.12);
        border-radius: 20px;
        color: var(--UFOV-ink);
        font: inherit;
        font-size: 15px;
        line-height: 1.45;
        min-height: 180px;
        padding: 16px;
        resize: vertical;
        width: 100%;
    }

    .UFOV-game__settings-feedback-field input {
        min-height: 52px;
    }

    .UFOV-game__settings-feedback-field input:focus,
    .UFOV-game__settings-feedback-field textarea:focus {
        border-color: var(--UFOV-ink);
        outline: 3px solid rgba(31, 140, 255, 0.18);
        outline-offset: 2px;
    }

    .UFOV-game__settings-feedback-status {
        border-radius: 16px;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.4;
        margin: 0;
        padding: 14px;
        text-align: center;
    }

    .UFOV-game__settings-feedback-status.is-sent {
        background: rgba(70, 174, 128, 0.14);
        color: #17663f;
    }

    .UFOV-game__settings-feedback-status.is-error {
        background: rgba(207, 76, 76, 0.12);
        color: #9d2d2d;
    }

    .UFOV-game__settings-feedback-submit {
        margin-top: 0;
        width: 100%;
    }

    .UFOV-game__settings-card {
        align-items: center;
        background: rgba(255, 250, 241, 0.9);
        border: 2px solid rgba(19, 38, 79, 0.1);
        border-radius: 22px;
        color: var(--UFOV-ink);
        display: flex;
        gap: 14px;
        justify-content: space-between;
        padding: 14px;
    }

    .UFOV-game__settings-card > div {
        display: grid;
        gap: 3px;
        min-width: 0;
    }

    .UFOV-game__settings-card.is-language {
        align-items: stretch;
        flex-direction: column;
    }

    .UFOV-game__settings-language-options {
        display: grid;
        gap: 6px;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        width: 100%;
    }

    .UFOV-game__settings-card .UFOV-game__settings-language-option {
        background: rgba(255, 255, 255, 0.72);
        border: 2px solid rgba(19, 38, 79, 0.1);
        color: var(--UFOV-ink);
        font-size: 11px;
        min-height: 36px;
        padding: 0 5px;
    }

    .UFOV-game__settings-card .UFOV-game__settings-language-option.is-selected {
        background: var(--UFOV-ink);
        border-color: var(--UFOV-ink);
        color: #fffaf1;
    }

    .UFOV-game__settings-card strong {
        color: var(--UFOV-title);
        font-size: 16px;
        font-weight: 700;
        letter-spacing: -0.01em;
    }

    .UFOV-game__settings-card span {
        color: var(--UFOV-muted);
        font-size: 12px;
        font-weight: 600;
        line-height: 1.25;
    }

    .UFOV-game__settings-card.is-toggle {
        align-items: center;
    }

    .UFOV-game__settings-card button,
    .UFOV-game__theme-picker-head button {
        background: var(--UFOV-ink);
        border: 0;
        border-radius: 999px;
        color: #fffaf1;
        cursor: pointer;
        flex: 0 0 auto;
        font: inherit;
        font-size: 12px;
        font-weight: 700;
        padding: 10px 13px;
    }

    .UFOV-game__settings-card button:disabled {
        cursor: wait;
        opacity: 0.5;
    }

    .UFOV-game__toggle-control {
        align-items: center;
        cursor: pointer;
        display: inline-flex;
        flex: 0 0 auto;
        gap: 8px;
    }

    .UFOV-game__toggle-input {
        height: 1px;
        opacity: 0;
        pointer-events: none;
        position: absolute;
        width: 1px;
    }

    .UFOV-game__toggle-track {
        align-items: center;
        background: rgba(19, 38, 79, 0.18);
        border-radius: 999px;
        display: inline-flex;
        height: 30px;
        padding: 3px;
        transition: background 160ms ease;
        width: 54px;
    }

    .UFOV-game__toggle-thumb {
        background: #fffaf1;
        border-radius: 50%;
        box-shadow: 0 4px 10px rgba(17, 27, 47, 0.18);
        display: block;
        height: 24px;
        transform: translateX(0);
        transition: transform 160ms ease;
        width: 24px;
    }

    .UFOV-game__toggle-input:checked + .UFOV-game__toggle-track {
        background: var(--UFOV-ink);
    }

    .UFOV-game__toggle-input:checked + .UFOV-game__toggle-track .UFOV-game__toggle-thumb {
        transform: translateX(24px);
    }

    .UFOV-game__toggle-input:focus-visible + .UFOV-game__toggle-track {
        outline: 3px solid rgba(53, 201, 193, 0.48);
        outline-offset: 3px;
    }

    .UFOV-game__settings-card .UFOV-game__toggle-label {
        color: var(--UFOV-ink);
        font-size: 12px;
        font-weight: 700;
        min-width: 22px;
    }

    .UFOV-game__theme-picker {
        background: rgba(255, 255, 255, 0.72);
        border: 1px solid rgba(19, 38, 79, 0.1);
        border-radius: 20px;
        display: grid;
        gap: 10px;
        min-height: 0;
        padding: 12px;
    }

    .UFOV-game__theme-picker.is-page {
        grid-template-rows: auto minmax(0, 1fr);
        overflow: hidden;
    }

    .UFOV-game__theme-picker-head {
        align-items: center;
        display: flex;
        justify-content: space-between;
    }

    .UFOV-game__theme-picker-head strong {
        color: var(--UFOV-title);
        font-size: 15px;
        font-weight: 700;
        letter-spacing: -0.01em;
    }

    .UFOV-game__theme-picker-head button {
        background: rgba(19, 38, 79, 0.08);
        color: var(--UFOV-ink);
        padding: 8px 12px;
    }

    .UFOV-game__theme-list {
        display: grid;
        gap: 8px;
        max-height: 210px;
        overflow-y: auto;
        padding-right: 2px;
    }

    .UFOV-game__theme-list.is-page {
        align-content: start;
        max-height: none;
        min-height: 0;
    }

    .UFOV-game__theme-option {
        align-items: center;
        background: rgba(255, 250, 241, 0.78);
        border: 2px solid rgba(19, 38, 79, 0.1);
        border-radius: 16px;
        color: var(--UFOV-ink);
        cursor: pointer;
        display: grid;
        font: inherit;
        gap: 10px;
        grid-template-columns: 38px 1fr;
        padding: 10px;
        text-align: left;
    }

    .UFOV-game__theme-option.is-selected {
        border-color: rgba(11, 88, 93, 0.5);
        box-shadow: inset 0 0 0 1px rgba(11, 88, 93, 0.24);
    }

    .UFOV-game__theme-option span {
        align-items: center;
        background: rgba(19, 38, 79, 0.08);
        border-radius: 999px;
        color: var(--UFOV-ink);
        display: inline-flex;
        font-size: 10px;
        font-weight: 700;
        height: 24px;
        justify-content: center;
        letter-spacing: 0.02em;
        text-transform: uppercase;
    }

    .UFOV-game__theme-option.is-selected span {
        background: var(--UFOV-ink);
        color: #fffaf1;
    }

    .UFOV-game__theme-option strong {
        color: var(--UFOV-title);
        font-size: 14px;
        font-weight: 700;
        letter-spacing: -0.01em;
    }

    .UFOV-game__config-status {
        color: var(--UFOV-muted);
        font-size: 13px;
        font-weight: 700;
        line-height: 1.25;
        margin: 0;
        text-align: center;
    }

    .UFOV-game__config-links {
        align-items: center;
        color: var(--UFOV-muted);
        display: flex;
        flex-wrap: wrap;
        font-size: 12px;
        font-weight: 700;
        gap: 8px;
        justify-content: center;
    }

    .UFOV-game__config-links a {
        color: var(--UFOV-ink);
        text-decoration: none;
    }

    .UFOV-game__screen--progress .UFOV-game__screen-main {
        align-items: stretch;
        overflow-x: visible;
        overflow-y: auto;
        padding-bottom: 16px;
        scrollbar-width: none;
    }

    .UFOV-game__screen--progress .UFOV-game__screen-main::-webkit-scrollbar {
        display: none;
    }

    .UFOV-game__progress-topbar {
        align-items: center;
        display: grid;
        gap: 12px;
        grid-template-columns: 44px minmax(0, 1fr) 44px;
        text-align: center;
        width: 100%;
    }

    .UFOV-game__progress-topbar::after {
        content: '';
        height: 44px;
        width: 44px;
    }

    .UFOV-game__progress-topbar > div {
        min-width: 0;
    }

    .UFOV-game__progress-topbar h1 {
        color: var(--UFOV-title);
        font-size: 29px;
        font-weight: 700;
        letter-spacing: -0.025em;
        line-height: 1;
        margin: 0;
    }

    .UFOV-game__progress-topbar p {
        color: var(--UFOV-muted);
        font-size: 13px;
        font-weight: 600;
        line-height: 1.25;
        margin: 5px 0 0;
    }

    .UFOV-game__progress-back {
        align-items: center;
        background: rgba(255, 255, 255, 0.92);
        border: 1px solid rgba(19, 38, 79, 0.1);
        border-radius: 50%;
        color: var(--UFOV-title);
        display: inline-flex;
        height: 44px;
        justify-content: center;
        padding: 0;
        transition: background-color 160ms ease, transform 160ms ease;
        width: 44px;
    }

    .UFOV-game__progress-back:hover,
    .UFOV-game__progress-back:focus-visible {
        background: #fff;
    }

    .UFOV-game__progress-back:active {
        transform: scale(0.95);
    }

    .UFOV-game__progress-back svg {
        height: 21px;
        width: 21px;
    }

    [dir="rtl"] .UFOV-game__progress-back svg {
        transform: scaleX(-1);
    }

    .UFOV-game__progress-stage {
        display: grid;
        gap: 16px;
        margin: 0 auto;
        max-width: 440px;
        width: 100%;
    }

    .UFOV-game__progress-overall {
        background: #fff;
        border: 1px solid rgba(19, 38, 79, 0.09);
        border-radius: 24px;
        box-shadow: 0 14px 30px rgba(19, 38, 79, 0.07);
        display: grid;
        gap: 12px;
        padding: 18px;
        text-align: left;
    }

    .UFOV-game__progress-overall-head {
        align-items: start;
        display: flex;
        gap: 12px;
        justify-content: space-between;
    }

    .UFOV-game__progress-overall-head > div {
        display: grid;
        gap: 1px;
    }

    .UFOV-game__progress-overall-head > div > span,
    .UFOV-game__progress-overall-head small {
        color: var(--UFOV-muted);
        font-size: 12px;
        font-weight: 700;
        line-height: 1.2;
    }

    .UFOV-game__progress-overall-head h2 {
        color: var(--UFOV-title);
        font-size: 44px;
        font-weight: 700;
        letter-spacing: -0.035em;
        line-height: 0.95;
        margin: 5px 0 2px;
    }

    .UFOV-game__progress-trend {
        background: rgba(19, 38, 79, 0.07);
        border-radius: var(--UFOV-radius-pill);
        color: var(--UFOV-muted);
        display: inline-flex;
        flex: 0 0 auto;
        font-size: 11px;
        font-weight: 700;
        line-height: 1;
        min-height: 28px;
        padding: 8px 10px;
        white-space: nowrap;
    }

    .UFOV-game__progress-trend.is-up {
        background: rgba(27, 172, 126, 0.12);
        color: #12745a;
    }

    .UFOV-game__progress-trend.is-down {
        background: rgba(219, 98, 74, 0.12);
        color: #a23f31;
    }

    .UFOV-game__progress-overall > p {
        color: var(--UFOV-muted);
        font-size: 12px;
        font-weight: 600;
        line-height: 1.35;
        margin: -4px 0 0;
    }

    .UFOV-game__progress-sparkline {
        color: #266ef1;
        display: block;
        height: 58px;
        overflow: visible;
        width: 100%;
    }

    .UFOV-game__progress-sparkline polyline {
        stroke: currentColor;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 3;
        vector-effect: non-scaling-stroke;
    }

    .UFOV-game__progress-sparkline-point {
        fill: none;
        stroke: currentColor;
        stroke-linecap: round;
        stroke-width: 7;
        vector-effect: non-scaling-stroke;
    }

    .UFOV-game__progress-sparkline.is-single {
        align-items: center;
        display: flex;
        justify-content: center;
    }

    .UFOV-game__progress-sparkline.is-single::after {
        background: currentColor;
        border-radius: 50%;
        content: '';
        height: 8px;
        width: 8px;
    }

    .UFOV-game__progress-sparkline.is-down {
        color: #d96752;
    }

    .UFOV-game__progress-sparkline.is-flat {
        color: #6f7d94;
    }

    .UFOV-game__progress-sparkline.is-empty {
        background: rgba(19, 38, 79, 0.05);
        border-radius: 12px;
        height: 42px;
    }

    .UFOV-game__progress-overall-stats {
        border-top: 1px solid rgba(19, 38, 79, 0.08);
        display: grid;
        gap: 12px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        padding-top: 12px;
    }

    .UFOV-game__progress-overall-stats span {
        color: var(--UFOV-muted);
        display: grid;
        font-size: 11px;
        font-weight: 600;
        gap: 2px;
        line-height: 1.2;
    }

    .UFOV-game__progress-overall-stats strong {
        color: var(--UFOV-title);
        font-size: 21px;
        font-weight: 700;
    }

    .UFOV-game__progress-by-game {
        display: grid;
        gap: 10px;
    }

    .UFOV-game__progress-by-game > h2 {
        color: var(--UFOV-title);
        font-size: 19px;
        font-weight: 700;
        line-height: 1.2;
        margin: 2px 2px 0;
        text-align: left;
    }

    .UFOV-game__progress-game-list {
        display: grid;
        gap: 10px;
    }

    .UFOV-game__progress-game {
        background: rgba(255, 255, 255, 0.94);
        border: 1px solid rgba(19, 38, 79, 0.09);
        border-radius: 20px;
        box-shadow: 0 8px 20px rgba(19, 38, 79, 0.045);
        display: grid;
        gap: 10px;
        padding: 13px 14px;
        text-align: left;
    }

    .UFOV-game__progress-game.is-empty {
        gap: 7px;
    }

    .UFOV-game__progress-game-head {
        align-items: center;
        display: grid;
        gap: 10px;
        grid-template-columns: 46px minmax(0, 1fr) auto;
    }

    .UFOV-game__progress-game-icon {
        --UFOV-brain-icon-color: #4056cf;
        align-items: center;
        background: rgba(69, 85, 205, 0.1);
        border-radius: 15px;
        color: var(--UFOV-brain-icon-color);
        display: inline-flex;
        height: 46px;
        justify-content: center;
        width: 46px;
    }

    .UFOV-game__progress-game-icon.is-classic,
    .UFOV-game__progress-game-icon.is-tone-1 {
        --UFOV-brain-icon-color: #159e79;
        background: rgba(21, 158, 121, 0.11);
    }

    .UFOV-game__progress-game-icon.is-advanced,
    .UFOV-game__progress-game-icon.is-tone-2 {
        --UFOV-brain-icon-color: #cc612b;
        background: rgba(204, 97, 43, 0.1);
    }

    .UFOV-game__progress-game-icon.is-tone-3 {
        --UFOV-brain-icon-color: #ad459f;
        background: rgba(173, 69, 159, 0.1);
    }

    .UFOV-game__progress-game-icon.is-tone-4 {
        --UFOV-brain-icon-color: #147ba8;
        background: rgba(20, 123, 168, 0.1);
    }

    .UFOV-game__progress-game-icon .UFOV-game__menu-icon {
        height: 31px;
        width: 31px;
    }

    .UFOV-game__progress-game-name {
        display: grid;
        gap: 3px;
        min-width: 0;
    }

    .UFOV-game__progress-game-name strong {
        color: var(--UFOV-title);
        font-size: 15px;
        font-weight: 700;
        line-height: 1.15;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .UFOV-game__progress-game-name span {
        color: var(--UFOV-muted);
        font-size: 11px;
        font-weight: 600;
        line-height: 1.2;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .UFOV-game__progress-game .UFOV-game__progress-sparkline {
        height: 42px;
    }

    .UFOV-game__progress-game-stats {
        display: grid;
        gap: 8px;
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .UFOV-game__progress-game-stats > span {
        background: rgba(19, 38, 79, 0.045);
        border-radius: 12px;
        display: grid;
        gap: 3px;
        min-width: 0;
        padding: 8px 9px;
    }

    .UFOV-game__progress-game-stats small {
        color: var(--UFOV-muted);
        font-size: 10px;
        font-weight: 600;
        line-height: 1;
    }

    .UFOV-game__progress-game-stats strong {
        color: var(--UFOV-title);
        font-size: 17px;
        font-weight: 700;
        line-height: 1.05;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .UFOV-game__progress-level-note {
        color: var(--UFOV-muted);
        font-size: 10px;
        font-weight: 700;
        line-height: 1.2;
        margin: -3px 0 0;
        text-align: end;
    }

    .UFOV-game__progress-empty-copy {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 8px;
        margin: 0;
        margin-inline-start: 56px;
    }

    .UFOV-game__progress-empty-copy strong,
    .UFOV-game__progress-empty-copy span {
        font-size: 11px;
        line-height: 1.25;
    }

    .UFOV-game__progress-empty-copy strong {
        color: var(--UFOV-title);
        font-weight: 700;
    }

    .UFOV-game__progress-empty-copy span {
        color: var(--UFOV-muted);
        font-weight: 600;
    }

    .UFOV-game__progress-bars-icon {
        color: #6f788a;
        height: 24px;
        width: 24px;
    }

    .UFOV-game__ranking-topbar {
        align-items: center;
        display: flex;
        justify-content: flex-start;
        min-height: 40px;
    }

    .UFOV-game__ranking-title {
        color: var(--UFOV-ink);
        font-size: 17px;
        font-weight: 700;
        letter-spacing: 0.06em;
    }

    .UFOV-game__ranking-pill {
        align-items: center;
        background: #ffffff;
        border-radius: 999px;
        color: var(--UFOV-ink);
        display: inline-flex;
        font-size: 14px;
        font-weight: 700;
        gap: 8px;
        height: 40px;
        justify-content: center;
        padding: 0 15px;
    }

    .UFOV-game__ranking-pill.is-menu {
        grid-column: 3;
        justify-self: end;
        padding: 0;
        position: static;
        width: 40px;
    }

    .UFOV-game__ranking-pill-icon {
        color: currentColor;
        height: 20px;
        width: 20px;
    }

    .UFOV-game__ranking-stage {
        display: grid;
        gap: 14px;
        text-align: center;
        width: 100%;
    }

    .UFOV-game__screen--ranking .UFOV-game__screen-main {
        justify-content: flex-start;
        overflow-y: auto;
        padding: 4px 2px 12px;
    }

    .UFOV-game__ranking-heading {
        display: grid;
        gap: 6px;
        justify-items: center;
    }

    .UFOV-game__ranking-heading h1 {
        color: var(--UFOV-title);
        font-size: clamp(31px, 9.2vw, 44px);
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 0.98;
        margin: 0;
    }

    .UFOV-game__ranking-heading p,
    .UFOV-game__ranking-card p,
    .UFOV-game__ranking-note {
        color: var(--UFOV-muted);
        font-weight: 600;
        margin: 0;
    }

    .UFOV-game__ranking-heading p {
        font-size: 15px;
    }

    .UFOV-game__ranking-card {
        background: #fffaf1;
        border: 1px solid rgba(19, 38, 79, 0.09);
        border-radius: 24px;
        box-shadow: 0 12px 28px rgba(17, 27, 47, 0.05);
        display: grid;
        gap: 9px;
        justify-items: center;
        min-height: 178px;
        padding: 18px 18px 20px;
    }

    .UFOV-game__ranking-trophy {
        align-items: center;
        background: rgba(255, 224, 204, 0.38);
        border: 2px solid rgba(255, 196, 171, 0.5);
        border-radius: 50%;
        color: #d99f33;
        display: inline-flex;
        height: 82px;
        justify-content: center;
        position: relative;
        width: 82px;
    }

    .UFOV-game__ranking-trophy-icon {
        filter: drop-shadow(0 8px 10px rgba(151, 100, 34, 0.18));
        height: 52px;
        width: 52px;
    }

    .UFOV-game__ranking-spark {
        color: #ffc8b8;
        font-size: 22px;
        position: absolute;
    }

    .UFOV-game__ranking-spark.is-left {
        left: -35px;
        top: 32px;
    }

    .UFOV-game__ranking-spark.is-right {
        right: -35px;
        top: 18px;
    }

    .UFOV-game__ranking-card h2 {
        color: var(--UFOV-title);
        font-size: clamp(31px, 9vw, 44px);
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 0.98;
        margin: 0;
    }

    .UFOV-game__ranking-card p {
        font-size: 15px;
    }

    .UFOV-game__ranking-stats {
        align-items: center;
        background: rgba(255, 255, 255, 0.88);
        border: 1px solid rgba(19, 38, 79, 0.08);
        border-radius: 22px;
        display: grid;
        grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
        min-height: 72px;
        padding: 12px 14px;
    }

    .UFOV-game__ranking-stat {
        align-items: center;
        display: grid;
        gap: 11px;
        grid-template-columns: 38px minmax(0, 1fr);
        text-align: left;
    }

    .UFOV-game__ranking-stat-icon {
        align-items: center;
        background: rgba(255, 220, 208, 0.68);
        border-radius: 50%;
        color: var(--UFOV-title);
        display: inline-flex;
        flex: 0 0 38px;
        height: 38px;
        justify-content: center;
        line-height: 0;
        width: 38px;
    }

    .UFOV-game__ranking-stat-svg {
        display: block;
        height: 18px;
        margin: 0;
        transform: none;
        width: 18px;
    }

    .UFOV-game__ranking-stat-svg.is-bars {
        transform: translateY(0);
    }

    .UFOV-game__ranking-stat-svg.is-flame {
        transform: translateY(0);
    }

    .UFOV-game__ranking-stat > div > span {
        color: var(--UFOV-muted);
        display: block;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.2;
    }

    .UFOV-game__ranking-stat strong {
        color: var(--UFOV-title);
        display: block;
        font-size: 22px;
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 1.1;
    }

    .UFOV-game__ranking-divider {
        background: rgba(19, 38, 79, 0.1);
        height: 42px;
        width: 1px;
    }

    .UFOV-game__ranking-age {
        background: rgba(255, 255, 255, 0.7);
        border: 1px solid rgba(19, 38, 79, 0.08);
        border-radius: 20px;
        display: grid;
        gap: 0;
        padding: 14px 16px;
    }

    .UFOV-game__ranking-age-head {
        align-items: center;
        display: flex;
        justify-content: space-between;
        text-align: left;
        width: 100%;
    }

    .UFOV-game__ranking-age-head span {
        color: var(--UFOV-muted);
        font-size: 13px;
        font-weight: 700;
    }

    .UFOV-game__ranking-age-head strong {
        color: var(--UFOV-title);
        font-size: 20px;
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 1;
        white-space: nowrap;
    }

    .UFOV-game__ranking-game-bests {
        background: rgba(255, 255, 255, 0.88);
        border: 1px solid rgba(19, 38, 79, 0.08);
        border-radius: 20px;
        display: grid;
        gap: 10px;
        padding: 14px;
        text-align: left;
    }

    .UFOV-game__ranking-game-bests-head {
        align-items: center;
        display: flex;
        justify-content: space-between;
    }

    .UFOV-game__ranking-game-bests-head h2 {
        color: var(--UFOV-title);
        font-size: 17px;
        font-weight: 700;
        line-height: 1.2;
        margin: 0;
    }

    .UFOV-game__ranking-game-bests-head > span {
        align-items: center;
        background: var(--UFOV-soft-mint);
        border-radius: var(--UFOV-radius-pill);
        color: #147769;
        display: inline-flex;
        font-size: 12px;
        font-weight: 700;
        height: 26px;
        justify-content: center;
        min-width: 26px;
        padding: 0 8px;
    }

    .UFOV-game__ranking-game-bests > p {
        color: var(--UFOV-muted);
        font-size: 13px;
        font-weight: 600;
        line-height: 1.45;
        margin: 0;
    }

    .UFOV-game__ranking-game-best-list {
        display: grid;
        gap: 0;
    }

    .UFOV-game__ranking-game-best {
        align-items: center;
        border-top: 1px solid rgba(19, 38, 79, 0.07);
        display: flex;
        justify-content: space-between;
        min-height: 58px;
        padding: 9px 2px;
    }

    .UFOV-game__ranking-game-best > div {
        display: grid;
        gap: 2px;
        min-width: 0;
    }

    .UFOV-game__ranking-game-best strong {
        color: var(--UFOV-title);
        font-size: 14px;
        font-weight: 700;
        line-height: 1.2;
    }

    .UFOV-game__ranking-game-best span {
        color: var(--UFOV-muted);
        font-size: 12px;
        font-weight: 600;
        line-height: 1.2;
    }

    .UFOV-game__ranking-game-best b {
        color: var(--UFOV-title);
        font-size: 22px;
        font-weight: 700;
        line-height: 1;
        margin-left: 12px;
    }

    .UFOV-game__ranking-note {
        align-items: center;
        display: inline-flex;
        font-size: 13px;
        gap: 8px;
        justify-content: center;
    }

    .UFOV-game__ranking-note span {
        color: #f2c04b;
        font-size: 21px;
        line-height: 1;
    }

    .UFOV-game__screen-main--play {
        align-items: center;
    }

    .UFOV-game__screen--play {
        --UFOV-play-board-size: min(500px, calc(100vw - 24px), calc(var(--UFOV-resolved-app-height) - 120px));
        --UFOV-countdown-size: min(var(--UFOV-play-board-size), 390px);
        padding-left: 12px;
        padding-right: 12px;
    }

    .UFOV-game__play-board-wrap {
        display: grid;
        justify-items: center;
        min-height: var(--UFOV-play-board-size);
        place-items: center;
        position: relative;
        width: var(--UFOV-play-board-size);
    }

    .UFOV-game__live-board-layer {
        display: grid;
        justify-items: center;
        place-items: center;
        position: relative;
        width: 100%;
    }

    .UFOV-game__live-board-layer.is-prewarming {
        inset: 0;
        opacity: 0.001;
        pointer-events: none;
        position: absolute;
        z-index: 0;
    }

    .UFOV-game__board-prerender-status {
        align-items: center;
        aspect-ratio: 1 / 1;
        color: var(--UFOV-muted);
        display: flex;
        font-size: 15px;
        font-weight: 700;
        justify-content: center;
        width: var(--UFOV-play-board-size);
        z-index: 1;
    }

    .UFOV-game__countdown {
        aspect-ratio: 1 / 1;
        color: #05080f;
        display: grid;
        margin: 0 auto;
        place-items: center;
        position: relative;
        width: var(--UFOV-countdown-size);
        z-index: 1;
    }

    .UFOV-game__countdown-step {
        animation: UFOV-countdown-step 1000ms step-end forwards;
        animation-delay: var(--UFOV-countdown-delay);
        display: grid;
        inset: 0;
        opacity: 1;
        place-items: center;
        position: absolute;
        visibility: hidden;
    }

    .UFOV-game__countdown.is-static .UFOV-game__countdown-step.is-static {
        animation: none;
        inset: 0;
        opacity: 1;
        visibility: visible;
    }

    .UFOV-game__countdown svg {
        height: 100%;
        inset: 0;
        position: absolute;
        width: 100%;
    }

    .UFOV-game__countdown-track,
    .UFOV-game__countdown-ring {
        fill: none;
        stroke-width: 8;
    }

    .UFOV-game__countdown-track {
        stroke: rgba(5, 8, 15, 0.08);
    }

    .UFOV-game__countdown-ring {
        animation: UFOV-countdown-ring 1000ms linear forwards;
        animation-delay: var(--UFOV-countdown-delay);
        stroke: currentColor;
        stroke-dasharray: 326.73;
        stroke-dashoffset: 0;
        stroke-linecap: round;
        transform: rotate(-90deg);
        transform-origin: center;
    }

    .UFOV-game__countdown-ring.is-static {
        animation: none;
        stroke-dashoffset: 0;
    }

    .UFOV-game__countdown strong {
        color: currentColor;
        font-size: clamp(88px, 28vw, 152px);
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 1;
        padding-right: 0.08em;
    }

    @keyframes UFOV-countdown-ring {
        to {
            stroke-dashoffset: 326.73;
        }
    }

    @keyframes UFOV-countdown-step {
        from {
            visibility: visible;
        }

        to {
            visibility: hidden;
        }
    }

    .UFOV-game__result-head {
        align-items: center;
        display: flex;
        justify-content: space-between;
        min-height: 22px;
    }

    .UFOV-game__result-level {
        background: #eef0f4;
        border-radius: 999px;
        color: var(--UFOV-ink);
        padding: 9px 12px;
    }

    .UFOV-game__result-stage {
        gap: clamp(22px, 3.2vh, 30px);
        justify-items: stretch;
    }

    .UFOV-game__result-summary {
        display: grid;
        gap: 10px;
        justify-items: center;
        margin-bottom: 10px;
        text-align: center;
    }

    .UFOV-game__result-summary h1 {
        color: var(--UFOV-title);
        font-size: clamp(34px, 9.4vw, 46px);
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 0.96;
        margin: 0;
    }

    .UFOV-game__result-summary p {
        color: var(--UFOV-muted);
        font-size: 17px;
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 1.2;
        margin: 0;
    }

    .UFOV-game__result-grid {
        display: grid;
        gap: 13px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .UFOV-game__result-grid.is-advanced {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .UFOV-game__result-glyph {
        align-items: center;
        background: rgba(255, 255, 255, 0.64);
        border: 1.5px solid rgba(19, 38, 79, 0.08);
        border-radius: 22px;
        display: grid;
        gap: 10px;
        justify-items: center;
        min-height: 148px;
        overflow: hidden;
        padding: 20px 12px 18px;
        position: relative;
    }

    .UFOV-game__result-glyph.is-good {
        border-color: rgba(53, 201, 193, 0.28);
    }

    .UFOV-game__result-glyph.is-bad {
        border-color: rgba(255, 122, 102, 0.28);
    }

    .UFOV-game__result-glyph-label {
        color: var(--UFOV-muted);
        font-size: 14px;
        font-weight: 700;
        letter-spacing: -0.01em;
        text-transform: uppercase;
    }

    .UFOV-game__result-glyph-badge {
        align-items: center;
        border-radius: 50%;
        color: #fff;
        display: inline-flex;
        font-size: 24px;
        font-weight: 700;
        height: 34px;
        justify-content: center;
        line-height: 1;
        position: absolute;
        right: 18px;
        top: 17px;
        width: 34px;
    }

    .UFOV-game__result-glyph.is-good .UFOV-game__result-glyph-badge {
        background: #74d1c9;
    }

    .UFOV-game__result-glyph.is-bad .UFOV-game__result-glyph-badge {
        background: #f07767;
    }

    .UFOV-game__result-glyph strong {
        align-items: center;
        color: var(--UFOV-title);
        display: flex;
        font-size: 46px;
        font-weight: 700;
        letter-spacing: -0.01em;
        line-height: 1;
    }

    .UFOV-game__result-glyph.is-direction .UFOV-game__result-glyph-orb {
        font-size: 0.88em;
    }

    .UFOV-game__result-glyph-orb {
        align-items: center;
        border-radius: 50%;
        display: inline-flex;
        height: 76px;
        justify-content: center;
        width: 76px;
    }

    .UFOV-game__result-glyph.is-good .UFOV-game__result-glyph-orb {
        background: rgba(215, 246, 242, 0.72);
    }

    .UFOV-game__result-glyph.is-bad .UFOV-game__result-glyph-orb {
        background: rgba(255, 220, 208, 0.76);
    }

    .UFOV-game.is-color-blind-mode .UFOV-game__result-glyph.is-good {
        border-color: rgba(0, 114, 178, 0.34);
    }

    .UFOV-game.is-color-blind-mode .UFOV-game__result-glyph.is-bad {
        border-color: rgba(230, 159, 0, 0.38);
    }

    .UFOV-game.is-color-blind-mode .UFOV-game__result-glyph.is-good .UFOV-game__result-glyph-badge {
        background: #0072b2;
    }

    .UFOV-game.is-color-blind-mode .UFOV-game__result-glyph.is-bad .UFOV-game__result-glyph-badge {
        background: #e69f00;
        color: #1f2937;
    }

    .UFOV-game.is-color-blind-mode .UFOV-game__result-glyph.is-good .UFOV-game__result-glyph-orb {
        background: rgba(216, 238, 248, 0.86);
    }

    .UFOV-game.is-color-blind-mode .UFOV-game__result-glyph.is-bad .UFOV-game__result-glyph-orb {
        background: rgba(255, 241, 196, 0.9);
    }

    .UFOV-game__result-change {
        display: grid;
        gap: 10px;
        justify-items: center;
    }

    .UFOV-game__result-level-pill {
        animation: UFOV-result-pill-pop 420ms cubic-bezier(0.2, 0.9, 0.2, 1) forwards;
        animation-delay: 0.7s;
        align-items: center;
        background:
            linear-gradient(rgba(255, 255, 255, 0.78), rgba(255, 255, 255, 0.78)) padding-box,
            linear-gradient(135deg, rgba(53, 201, 193, 0.45), rgba(242, 192, 75, 0.58), rgba(255, 122, 102, 0.55)) border-box;
        border: 3px solid transparent;
        border-radius: 999px;
        box-shadow: 0 10px 22px rgba(17, 27, 47, 0.05);
        color: var(--UFOV-muted);
        display: inline-flex;
        font-size: 20px;
        font-weight: 700;
        gap: 10px;
        justify-content: center;
        min-height: 50px;
        min-width: 198px;
        opacity: 0;
        padding: 0 22px;
        transform: translateY(10px) scale(0.92);
    }

    .UFOV-game__result-level-pill span {
        color: #65c8c0;
        font-size: 28px;
        line-height: 1;
    }

    .UFOV-game__result-level-pill strong {
        font-weight: 700;
    }

    .UFOV-game__result-change.is-down .UFOV-game__result-level-pill span {
        color: #8b94a5;
    }

    .UFOV-game__result-change.is-down .UFOV-game__result-level-pill {
        background:
            linear-gradient(rgba(255, 255, 255, 0.82), rgba(255, 255, 255, 0.82)) padding-box,
            linear-gradient(135deg, rgba(148, 163, 184, 0.38), rgba(100, 116, 139, 0.42)) border-box;
        color: #6f788a;
    }

    .UFOV-game__result-change.is-down .UFOV-game__result-axis-pill {
        background: rgba(255, 255, 255, 0.84);
        border-color: rgba(100, 116, 139, 0.14);
        color: #6f788a;
    }

    .UFOV-game__result-axis-pill {
        animation: UFOV-result-pill-pop 420ms cubic-bezier(0.2, 0.9, 0.2, 1) forwards;
        animation-delay: 1.4s;
        align-items: center;
        background: rgba(255, 255, 255, 0.78);
        border: 1px solid rgba(19, 38, 79, 0.08);
        border-radius: 999px;
        box-shadow: 0 8px 18px rgba(17, 27, 47, 0.06);
        color: var(--UFOV-muted);
        display: inline-flex;
        font-size: 18px;
        font-weight: 700;
        justify-content: center;
        min-height: 36px;
        min-width: 112px;
        opacity: 0;
        padding: 0 18px;
        transform: translateY(10px) scale(0.92);
    }

    @keyframes UFOV-result-pill-pop {
        from {
            opacity: 0;
            transform: translateY(10px) scale(0.92);
        }

        to {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }

    .UFOV-game__text-link {
        background: transparent;
        color: var(--UFOV-muted);
        font-size: 14px;
        font-weight: 700;
        padding: 0;
        text-align: center;
        text-decoration: underline;
    }

    .UFOV-game__paywall-top {
        align-items: center;
        display: grid;
        gap: 8px;
        grid-template-columns: 36px 1fr 36px;
        width: 100%;
    }

    .UFOV-game__paywall-close {
        align-items: center;
        background: transparent;
        border: 0;
        color: var(--UFOV-title);
        cursor: pointer;
        display: inline-flex;
        height: 36px;
        justify-content: center;
        padding: 0;
        width: 36px;
    }

    .UFOV-game__paywall-spacer {
        height: 36px;
        width: 36px;
    }

    .UFOV-game__paywall-heading {
        display: grid;
        gap: 6px;
        justify-items: center;
        text-align: center;
    }

    .UFOV-game__paywall-features {
        background: rgba(255, 255, 255, 0.8);
        border: 1px solid rgba(19, 38, 79, 0.08);
        border-radius: 24px;
        box-shadow: 0 14px 26px rgba(17, 27, 47, 0.04);
        display: grid;
        gap: 10px;
        padding: 14px;
    }

    .UFOV-game__paywall-feature {
        align-items: center;
        display: grid;
        gap: 10px;
        grid-template-columns: 42px minmax(0, 1fr);
    }

    .UFOV-game__paywall-feature-icon {
        align-items: center;
        color: #0f5d68;
        display: inline-flex;
        height: 42px;
        justify-content: center;
        width: 42px;
    }

    .UFOV-game__billing-svg {
        height: 30px;
        width: 34px;
    }

    .UFOV-game__paywall-feature-copy {
        display: grid;
        gap: 2px;
    }

    .UFOV-game__paywall-feature-copy strong {
        font-size: 14px;
        font-weight: 700;
        letter-spacing: -0.01em;
    }

    .UFOV-game__paywall-feature-copy span {
        color: var(--UFOV-muted);
        font-size: 12px;
        font-weight: 600;
        line-height: 1.24;
    }

    .UFOV-game__paywall-plan-list {
        display: grid;
        gap: 10px;
    }

    .UFOV-game__paywall-plan {
        align-items: center;
        background:
            linear-gradient(var(--UFOV-surface-strong), var(--UFOV-surface-strong)) padding-box,
            linear-gradient(135deg, rgba(19, 38, 79, 0.12), rgba(19, 38, 79, 0.12)) border-box;
        border: 2px solid transparent;
        border-radius: 22px;
        display: grid;
        gap: 10px;
        grid-template-columns: auto minmax(0, 1fr) auto;
        padding: 12px 14px;
        text-align: left;
    }

    .UFOV-game__paywall-plan.is-selected {
        background:
            linear-gradient(rgba(255, 250, 241, 0.98), rgba(255, 250, 241, 0.98)) padding-box,
            linear-gradient(135deg, var(--UFOV-mint), var(--UFOV-gold), var(--UFOV-coral)) border-box;
    }

    .UFOV-game__paywall-radio {
        align-items: center;
        border: 2px solid rgba(19, 38, 79, 0.16);
        border-radius: 50%;
        display: inline-flex;
        height: 28px;
        justify-content: center;
        width: 28px;
    }

    .UFOV-game__paywall-plan.is-selected .UFOV-game__paywall-radio {
        border-color: var(--UFOV-mint);
    }

    .UFOV-game__paywall-radio-dot {
        background: #111b2f;
        border-radius: 50%;
        height: 16px;
        opacity: 0;
        width: 16px;
    }

    .UFOV-game__paywall-plan.is-selected .UFOV-game__paywall-radio-dot {
        opacity: 1;
    }

    .UFOV-game__paywall-plan-copy {
        display: grid;
        gap: 3px;
    }

    .UFOV-game__paywall-plan-name {
        font-size: 15px;
        font-weight: 700;
        letter-spacing: -0.01em;
    }

    .UFOV-game__paywall-plan-price {
        font-size: 17px;
        font-weight: 500;
        letter-spacing: -0.01em;
    }

    .UFOV-game__paywall-plan.is-weekly .UFOV-game__paywall-plan-price {
        font-size: 23px;
        font-weight: 500;
        line-height: 1.05;
    }

    .UFOV-game__paywall-plan-price-row {
        align-items: baseline;
        display: flex;
        flex-wrap: wrap;
        gap: 7px;
    }

    .UFOV-game__paywall-plan-price-old {
        color: var(--UFOV-muted);
        font-size: 14px;
        font-weight: 700;
        text-decoration: line-through;
        text-decoration-thickness: 2px;
    }

    .UFOV-game__paywall-plan-note {
        color: var(--UFOV-muted);
        display: none;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.24;
    }

    .UFOV-game__paywall-plan-badge {
        align-items: center;
        background: #000;
        border-radius: 999px;
        color: #fff;
        display: inline-flex;
        font-size: 12px;
        font-weight: 700;
        justify-content: center;
        min-height: 34px;
        padding: 0 12px;
    }

    .UFOV-game__paywall-links {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        justify-content: center;
    }

    .UFOV-game__paywall-trust-line {
        align-items: center;
        color: #000;
        display: inline-flex;
        font-size: 13px;
        font-weight: 700;
        gap: 3px;
        justify-content: center;
        line-height: 1.2;
        text-align: center;
    }

    .UFOV-game__paywall-trust-icon {
        fill: currentColor;
        height: 14px;
        transform: translate(-3px, -1px);
        width: 14px;
    }

    .UFOV-game__paywall-links button,
    .UFOV-game__paywall-links a,
    .UFOV-game__paywall-links span {
        color: var(--UFOV-muted);
        font-size: 12px;
        font-weight: 600;
    }

    .UFOV-game__paywall-links button,
    .UFOV-game__paywall-links a {
        background: transparent;
        padding: 0;
        text-decoration: underline;
    }

    .UFOV-game__paywall-status {
        color: var(--UFOV-muted);
        font-size: 11px;
        font-weight: 600;
        line-height: 1.3;
        margin: 0;
        max-width: 100%;
        overflow-wrap: anywhere;
        white-space: pre-wrap;
        word-break: break-word;
        text-align: center;
    }

    .UFOV-game__paywall-status.is-active {
        color: #0f5d68;
    }

    .UFOV-game__store-wait-overlay {
        align-items: center;
        background: rgba(255, 255, 255, 0.74);
        display: flex;
        inset: 0;
        justify-content: center;
        padding: 18px;
        position: fixed;
        z-index: 10000;
    }

    .UFOV-game__store-wait-card {
        align-items: center;
        background: #fff;
        border: 1px solid rgba(17, 27, 47, 0.1);
        border-radius: 18px;
        box-shadow: 0 22px 48px rgba(17, 27, 47, 0.18);
        color: #111b2f;
        display: grid;
        gap: 10px;
        justify-items: center;
        max-width: 420px;
        padding: 22px 20px;
        text-align: center;
        width: min(100%, 420px);
    }

    .UFOV-game__store-wait-card strong {
        font-size: 20px;
        font-weight: 700;
        line-height: 1.18;
    }

    .UFOV-game__store-wait-card span:last-child {
        color: var(--UFOV-muted);
        font-size: 14px;
        font-weight: 600;
        line-height: 1.42;
    }

    .UFOV-game__store-wait-spinner {
        animation: UFOV-store-wait-spin 0.8s linear infinite;
        border: 3px solid rgba(17, 27, 47, 0.12);
        border-top-color: #111b2f;
        border-radius: 999px;
        display: inline-block;
        height: 34px;
        width: 34px;
    }

    @keyframes UFOV-store-wait-spin {
        to {
            transform: rotate(360deg);
        }
    }

    @media (max-width: 390px) {
        .UFOV-game__screen {
            --UFOV-screen-horizontal-gap: 14px;
        }

        .UFOV-game__hero-title {
            font-size: clamp(38px, 12vw, 64px);
        }

        .UFOV-game__onboarding-row {
            grid-template-columns: 42px 64px minmax(0, 1fr);
        }

        .UFOV-game__onboarding-icon {
            height: 64px;
            width: 64px;
        }

        .UFOV-game__choice-tile > .UFOV-game__symbol--image {
            height: 42px;
            width: 42px;
        }
    }

    @media (max-height: 860px) {
        .UFOV-game__screen {
            --UFOV-screen-top-gap: 12px;
            --UFOV-screen-bottom-gap: 12px;
            gap: 0px;
        }

        .UFOV-game__hero-title {
            font-size: clamp(38px, 12vw, 60px);
        }

        .UFOV-game__title,
        .UFOV-game__paywall-heading h1,
        .UFOV-game__result-summary h1 {
            font-size: clamp(28px, 8.8vw, 44px);
        }

        .UFOV-game__subtitle,
        .UFOV-game__paywall-heading p,
        .UFOV-game__result-summary p {
            font-size: 13px;
        }

        .UFOV-game__onboarding-list {
            gap: 10px;
        }

        .UFOV-game__mode-card {
            --UFOV-mode-card-inline-padding: 12px;
            min-height: 84px;
            padding: 12px;
        }

        .UFOV-game__mode-card.has-footer {
            padding-bottom: 0;
        }

        .UFOV-game__level-ring {
            width: clamp(144px, 40vw, 181px);
        }

        .UFOV-game__level-ring-inner strong {
            font-size: clamp(51px, 14vw, 75px);
        }

        .UFOV-game__daily-card {
            gap: 10px;
            padding: 12px 14px 14px;
        }

        .UFOV-game__daily-icon {
            height: 48px;
            width: 48px;
        }

        .UFOV-game__board {
            width: min(100%, 400px);
        }

        .UFOV-game__screen--play .UFOV-game__board {
            width: var(--UFOV-play-board-size);
        }

        .UFOV-game__choice-tile,
        .UFOV-game__direction-tile {
            min-height: 62px;
        }

        .UFOV-game__primary-button {
            height: 56px;
        }
    }

    @media (min-height: 781px) and (max-height: 860px) {
        .UFOV-game__screen--menu .UFOV-game__mode-grid {
            gap: 9px;
        }
    }

    @media (max-height: 780px) {
        .UFOV-game__screen {
            --UFOV-screen-top-gap: 10px;
            --UFOV-screen-bottom-gap: 12px;
            gap: 0px;
        }

        .UFOV-game__hero-title {
            font-size: clamp(34px, 11vw, 54px);
        }

        .UFOV-game__title,
        .UFOV-game__paywall-heading h1,
        .UFOV-game__result-summary h1 {
            font-size: clamp(24px, 7.6vw, 38px);
        }

        .UFOV-game__onboarding-list,
        .UFOV-game__onboarding-stage,
        .UFOV-game__onboarding-example,
        .UFOV-game__question-stage,
        .UFOV-game__paywall-main,
        .UFOV-game__result-stage {
            gap: 10px;
        }

        .UFOV-game__onboarding-row {
            grid-template-columns: 38px 58px minmax(0, 1fr);
            gap: 10px;
        }

        .UFOV-game__onboarding-index {
            height: 38px;
            width: 38px;
        }

        .UFOV-game__onboarding-icon {
            height: 58px;
            width: 58px;
        }

        .UFOV-game__onboarding-row strong,
        .UFOV-game__prompt-block h2,
        .UFOV-game__daily-head strong {
            font-size: 13px;
        }

        .UFOV-game__mode-grid {
            gap: 10px;
        }

        .UFOV-game__mode-card {
            --UFOV-mode-card-inline-padding: 11px;
            min-height: 78px;
            padding: 11px;
        }

        .UFOV-game__mode-card.has-footer {
            padding-bottom: 0;
        }

        .UFOV-game__mode-copy strong {
            font-size: 13px;
        }

        .UFOV-game__mode-copy span,
        .UFOV-game__mode-badge,
        .UFOV-game__daily-card p,
        .UFOV-game__footer-link span,
        .UFOV-game__result-level {
            font-size: 11px;
        }

        .UFOV-game__level-ring {
            width: clamp(133px, 38vw, 166px);
            padding: 10px;
        }

        .UFOV-game__level-ring-inner strong {
            font-size: clamp(46px, 13vw, 67px);
        }

        .UFOV-game__progress-track {
            height: 11px;
        }

        .UFOV-game__progress-track.is-compact {
            height: 9px;
        }

        .UFOV-game__daily-card {
            border-radius: 22px;
            gap: 8px;
            padding: 12px;
        }

        .UFOV-game__daily-head span {
            font-size: 22px;
        }

        .UFOV-game__daily-icon {
            height: 44px;
            width: 44px;
        }

        .UFOV-game__board {
            width: min(100%, 400px);
        }

        .UFOV-game__screen--play .UFOV-game__board {
            width: var(--UFOV-play-board-size);
        }

        .UFOV-game__choice-grid,
        .UFOV-game__direction-pad,
        .UFOV-game__paywall-plan-list {
            gap: 8px;
        }

        .UFOV-game__choice-tile,
        .UFOV-game__direction-tile {
            border-radius: 14px;
            min-height: 56px;
        }

        .UFOV-game__choice-tile > .UFOV-game__symbol--image {
            height: 38px;
            width: 38px;
        }

        .UFOV-game__direction-tile {
            font-size: 15px;
        }

        .UFOV-game__result-grid {
            gap: 10px;
        }

        .UFOV-game__result-summary h1 {
            font-size: clamp(34px, 9.4vw, 40px);
        }

        .UFOV-game__result-glyph {
            min-height: 126px;
            padding: 16px 8px 14px;
        }

        .UFOV-game__result-glyph strong {
            font-size: 38px;
        }

        .UFOV-game__result-glyph-orb {
            height: 64px;
            width: 64px;
        }

        .UFOV-game__result-glyph-badge {
            font-size: 20px;
            height: 28px;
            right: 14px;
            top: 14px;
            width: 28px;
        }

        .UFOV-game__result-level-pill {
            border-width: 3px;
            font-size: 17px;
            min-height: 44px;
            min-width: 178px;
        }

        .UFOV-game__result-level-pill span {
            font-size: 23px;
        }

        .UFOV-game__result-axis-pill {
            font-size: 15px;
            min-height: 32px;
            min-width: 96px;
            padding: 0 16px;
        }

        .UFOV-game__paywall-features,
        .UFOV-game__paywall-plan {
            padding: 12px;
        }

        .UFOV-game__paywall-feature {
            gap: 8px;
            grid-template-columns: 38px minmax(0, 1fr);
        }

        .UFOV-game__paywall-feature-icon {
            height: 38px;
            width: 38px;
        }

        .UFOV-game__billing-svg {
            height: 27px;
            width: 32px;
        }

        .UFOV-game__primary-button {
            border-radius: 20px;
            font-size: 15px;
            height: 52px;
        }
    }

    @media (min-width: 700px) {
        .UFOV-game__screen {
            --UFOV-screen-horizontal-gap: clamp(36px, 6vw, 72px);
            --UFOV-screen-top-gap: 28px;
            --UFOV-screen-bottom-gap: 28px;
            gap: 0px;
            max-width: min(760px, calc(100vw - 96px));
        }

        .UFOV-game__screen--play {
            --UFOV-play-board-size: min(500px, calc(100vw - 48px), calc(var(--UFOV-resolved-app-height) - 120px));
            --UFOV-countdown-size: min(var(--UFOV-play-board-size), 390px);
            max-width: min(524px, calc(100vw - 24px));
        }

        .UFOV-game__screen-top,
        .UFOV-game__heading {
            justify-items: center;
            text-align: center;
        }

        .UFOV-game__screen--menu .UFOV-game__screen-main,
        .UFOV-game__screen--config .UFOV-game__screen-main,
        .UFOV-game__screen--onboarding .UFOV-game__screen-main,
        .UFOV-game__screen--question .UFOV-game__screen-main,
        .UFOV-game__screen--progress .UFOV-game__screen-main,
        .UFOV-game__screen--ranking .UFOV-game__screen-main,
        .UFOV-game__screen--result .UFOV-game__screen-main {
            align-items: center;
        }

        .UFOV-game__title,
        .UFOV-game__paywall-heading h1,
        .UFOV-game__result-summary h1 {
            font-size: clamp(44px, 5vw, 68px);
        }

        .UFOV-game__title.is-compact {
            font-size: clamp(42px, 4.2vw, 54px);
            line-height: 0.94;
            margin-top: 6px;
            text-align: center;
        }

        .UFOV-game__title.is-compact.is-study {
            font-size: clamp(40px, 4vw, 54px);
        }

        .UFOV-game__subtitle,
        .UFOV-game__paywall-heading p,
        .UFOV-game__result-summary p {
            font-size: clamp(18px, 1.65vw, 22px);
            line-height: 1.28;
            max-width: 560px;
            text-align: center;
        }

        .UFOV-game__onboarding-stage,
        .UFOV-game__onboarding-example,
        .UFOV-game__question-stage,
        .UFOV-game__result-stage,
        .UFOV-game__paywall-main,
        .UFOV-game__onboarding-step-content,
        .UFOV-game__onboarding-settings-panel,
        .UFOV-game__menu-top,
        .UFOV-game__menu-main,
        .UFOV-game__menu-meter,
        .UFOV-game__menu-footer,
        .UFOV-game__config-panel {
            margin-left: auto;
            margin-right: auto;
            max-width: 620px;
            width: 100%;
        }

        .UFOV-game__screen--paywall {
            max-width: min(680px, calc(100vw - 96px));
        }

        .UFOV-game__paywall-main {
            max-width: 600px;
        }

        .UFOV-game__mode-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .UFOV-game__mode-card {
            --UFOV-mode-card-inline-padding: 18px;
            min-height: 112px;
            padding: 18px;
        }

        .UFOV-game__mode-card.has-footer {
            padding-bottom: 0;
        }

        .UFOV-game__daily-card,
        .UFOV-game__primary-button,
        .UFOV-game__footer-nav {
            max-width: 560px;
            width: 100%;
        }

        .UFOV-game__paywall-status {
            font-size: 13px;
            max-width: 520px;
        }
    }

    @media (min-width: 900px) and (orientation: landscape) {
        .UFOV-game__screen {
            max-width: min(860px, calc(100vw - 128px));
        }

        .UFOV-game__screen--play {
            max-width: min(524px, calc(100vw - 24px));
        }

        .UFOV-game__screen--paywall {
            max-width: min(760px, calc(100vw - 128px));
        }
    }

    .UFOV-game__screen--menu .UFOV-game__menu-meter .UFOV-game__level-ring {
        transform: scale(0.9);
        transform-origin: center;
    }

    .UFOV-game__screen--onboarding .UFOV-game__title,
    .UFOV-game__screen--onboarding .UFOV-game__subtitle {
        hyphens: none;
        overflow-wrap: normal;
        word-break: normal;
    }

    .UFOV-game__screen--onboarding.is-copy-compact .UFOV-game__title {
        font-size: clamp(29px, 8.6vw, 46px);
    }

    .UFOV-game__screen--onboarding.is-copy-compact .UFOV-game__title.is-compact {
        font-size: clamp(27px, 7.8vw, 43px);
    }

    .UFOV-game__screen--onboarding.is-copy-tight .UFOV-game__title,
    .UFOV-game__screen--onboarding.is-copy-tight .UFOV-game__title.is-compact {
        font-size: clamp(24px, 7vw, 38px);
    }

    .UFOV-game__screen--onboarding.is-copy-tight .UFOV-game__subtitle {
        font-size: clamp(12px, 3.3vw, 16px);
    }

`;
